import { useEffect, useState, useRef, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Archive, Loader2, ExternalLink, Pencil, Check, X, Phone, MessageSquare, Mail, Target, History, ChevronDown, ArrowRight, ArrowRightLeft, Trash2, MoreHorizontal, User, UserPlus, Users, Building2, Megaphone, Link2, CheckSquare, StickyNote, AlertTriangle, MessagesSquare, Inbox, Rocket, Radar, MapPin, DollarSign, TrendingUp, Home, Ruler, ShieldCheck, Hammer, HardHat, CheckCircle2, Download, Presentation, Copy, Eye, Plus, Split, Gauge, Sparkles, ScanSearch, ClipboardCheck, RotateCcw, Compass, Percent, FileSignature, FileText, Flame } from "lucide-react";import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MultiSelect } from "../components/MultiSelect";
import { SearchableSelect } from "../components/SearchableSelect";
import api, { apiError } from "../lib/api";
import { updateItem, archiveItem, deleteItem, createItem } from "../hooks/useEntity";
import { getRecordConfig, getSections, LEAD_STATUSES, OPP_STAGES, STATUS_PATHS, LEAD_FIELDS, PROPERTY_SEARCH_FIELDS, SECTIONS, ACQUISITION_TERMINAL, isLeadOnMarket } from "../config/recordConfigs";
import { formatDate, formatDateTime, formatCurrency, formatPhone, formatPhoneInput, isPhoneKey, formatRecordId } from "../lib/utils-format";
import { TasksCard, NotesCard } from "../components/RecordTasksNotes";
import { CommunicationCard } from "../components/CommunicationCard";
import { EngagementActions } from "../components/LeadEngagement";
import { ConceptSitePlanCard } from "../components/ConceptSitePlan";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { ActivityLog } from "../components/ActivityLog";
import { IntakeCard } from "../components/IntakeCard";
import { RecordDocuments } from "../components/RecordDocuments";
import { AnalysisResults } from "../components/AnalysisResults";
import { DueDiligenceResults } from "../components/DueDiligenceResults";
import { GateResults } from "../components/GateResults";
import { AgentDirectoryPicker } from "../components/AgentDirectoryPicker";

// Resources that reuse the Property Analysis (leads) record-detail layout.
const LEAD_LIKE = ["leads", "projects", "property_search", "preconstruction", "construction", "closeout", "directory", "financials", "documents", "schedule", "acquisition", "acquisition_pipeline", "planning", "inbound_leads"];
// Display-only relabel: the "Preliminary Review" pipeline stage shows as "Analyze"
// (stored value stays "Preliminary Review" so existing records aren't orphaned).
const stageLabel = (s) => (s === "Preliminary Review" ? "Analyze" : s);
const COMM_RESOURCES = ["property_search", "leads", "inbound_leads", "projects", "acquisition", "acquisition_pipeline", "planning", "opportunities", "preconstruction", "construction", "closeout"];

// Resources that expose the Zoning (Zoneomics) lookup button.
const ZONING_RESOURCES = ["property_search"];

// Resources that carry the Property Budget Analysis / quote columns.
const QUOTE_RESOURCES = ["leads", "acquisition_pipeline", "projects", "preconstruction", "construction", "closeout"];
// Resources that carry an GDV and its auto-calculated allocation.
const ARV_RESOURCES = ["leads", "acquisition_pipeline", "preconstruction", "construction", "closeout"];
// Records that render the full Property Analysis component set (underwriting,
// subdivision, pad-ready, comps, etc.). Acquisition deals carry these over on convert.
const ANALYSIS_LIKE = ["leads", "acquisition_pipeline"];
// Hard-cap underwriting model. Everything above profit is a MAX; profit is a MINIMUM.
// Guardrail: all costs (Land + Hard + Soft + Contingency + Financing/Carry + Selling)
// can never exceed 90% of GDV, protecting the 10% minimum net developer profit.
const UW_CATEGORIES = [
  { key: "max_land_purchase_price", label: "Land Purchase", pct: 0.15, type: "MAX" },
  { key: "construction_budget", label: "Hard Construction", pct: 0.50, type: "MAX", rollup: true },
  { key: "soft_costs", label: "Soft Costs", pct: 0.05, type: "MAX" },
  { key: "construction_contingency", label: "Contingency", pct: 0.05, type: "MAX" },
  { key: "financing_carry", label: "Financing + Carrying Costs", pct: 0.08, type: "MAX" },
  { key: "selling_costs", label: "Selling Costs", pct: 0.07, type: "MAX" },
  { key: "est_net_profit", label: "Net Developer Profit", pct: 0.10, type: "MIN", profit: true },
];
const UW_ALL_IN_CAP_PCT = 0.90;
// Builder / GC Fee sits INSIDE the 50% construction cap and defaults to 12% of the hard
// construction cost (Available Hard Construction Cap), editable per record.
const DEFAULT_BUILDER_GC_FEE = 50000;
const BUILDER_GC_FEE_PCT = 0.12;
// fee = 12% of the hard construction cost, kept inside the 50% cap → fee = cap * 0.12/1.12.
const defaultBuilderFee = (arv) => {
  const cap = Math.round((Number(arv) || 0) * 0.5);
  return Math.round((cap * BUILDER_GC_FEE_PCT) / (1 + BUILDER_GC_FEE_PCT));
};
const UW_COST_KEYS = ["max_land_purchase_price", "soft_costs", "construction_contingency", "financing_carry", "selling_costs"];
// Physical hard-construction line items (Architecture/Permits → Soft Costs and
// Miscellaneous → Contingency have been moved out). Raw shares are normalized in
// code so they always roll up to exactly the 50% Hard Construction budget.
const CONSTRUCTION_BUDGET_ALLOCATION = {
  budget_water_sewer_setup: 0.014,
  budget_land_prep_excavation: 0.027,
  budget_foundation: 0.131,
  budget_framing_materials: 0.149,
  budget_framing_labor: 0.045,
  budget_windows: 0.022,
  budget_hvac: 0.059,
  budget_plumbing: 0.058,
  budget_electrical: 0.047,
  budget_roofing: 0.022,
  budget_garage_doors: 0.013,
  budget_exterior_cladding_gutters: 0.099,
  budget_insulation: 0.023,
  budget_drywall: 0.032,
  budget_flooring_tile: 0.050,
  budget_interior_painting: 0.021,
  budget_doors_stairs_trim: 0.073,
  budget_light_fixtures: 0.013,
  budget_cabinets_countertops: 0.051,
  budget_appliance: 0.018,
  budget_driveway: 0.013,
  budget_landscaping: 0.017,
};
// Budget line item -> its matching quote field (names are not 1:1 by string).
const QUOTE_KEY_MAP = {
  budget_water_sewer_setup: "quote_water_sewer",
  budget_land_prep_excavation: "quote_land_excavation",
  budget_foundation: "quote_foundation",
  budget_framing_materials: "quote_framing_materials",
  budget_framing_labor: "quote_framing_labor",
  budget_windows: "quote_window",
  budget_hvac: "quote_hvac",
  budget_plumbing: "quote_plumbing",
  budget_electrical: "quote_electrical",
  budget_roofing: "quote_roofing",
  budget_garage_doors: "quote_garage_door",
  budget_exterior_cladding_gutters: "quote_exterior_cladding_gutters",
  budget_insulation: "quote_insulation",
  budget_drywall: "quote_drywall",
  budget_flooring_tile: "quote_flooring_tile",
  budget_interior_painting: "quote_interior_painting",
  budget_doors_stairs_trim: "quote_doors_stairs_trim",
  budget_light_fixtures: "quote_light_fixtures",
  budget_cabinets_countertops: "quote_cabinets_countertops",
  budget_appliance: "quote_appliance",
  budget_driveway: "quote_driveway",
  budget_landscaping: "quote_landscaping",
};
const CB_RAW_TOTAL = Object.values(CONSTRUCTION_BUDGET_ALLOCATION).reduce((a, b) => a + b, 0);

// Days on market = today − Date Added (recomputes live as days pass).
function computeDaysOnMarket(dateAdded) {
  if (!dateAdded) return null;
  const m = typeof dateAdded === "string" && dateAdded.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const start = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(dateAdded);
  if (isNaN(start)) return null;
  const today = new Date();
  const days = Math.floor((today.setHours(0, 0, 0, 0) - start.setHours(0, 0, 0, 0)) / 86400000);
  return days >= 0 ? days : 0;
}

// Given an GDV, returns the full auto-populated payload: each underwriting category
// defaults to its cap, and hard-construction line items are normalized to the 50% cap.
function computeArvUnderwriting(arv, builderFee = defaultBuilderFee(arv)) {
  const n = Number(arv);
  const out = {};
  if (arv == null || arv === "" || isNaN(n)) {
    UW_CATEGORIES.forEach((c) => { out[c.key] = null; });
    Object.keys(CONSTRUCTION_BUDGET_ALLOCATION).forEach((k) => { out[k] = null; });
    return out;
  }
  const allInCap = Math.round(n * UW_ALL_IN_CAP_PCT);
  const hard = Math.round(n * 0.5);              // Total Construction Cap (unchanged 50% of GDV)
  const fee = Math.min(Math.max(Number(builderFee) || 0, 0), hard);   // Builder / GC Fee (inside the cap)
  const availableHard = hard - fee;              // Available Hard Construction Cap
  out.construction_budget = hard;                // Total Construction Cost (hard construction + Builder/GC fee)
  out.builder_gc_fee = fee;
  out.est_net_profit = n - allInCap;
  // Allocate the remaining all-in budget across the non-hard cost categories with
  // largest-remainder rounding so every category default sums EXACTLY to the 90%
  // all-in cap (no ±$1 drift that would otherwise trip a false "Over Cap").
  const others = UW_CATEGORIES.filter((c) => !c.profit && !c.rollup);
  const otherPctTotal = others.reduce((s, c) => s + c.pct, 0);
  const remaining = allInCap - hard;
  const cats = others.map((c) => {
    const exact = remaining * (c.pct / otherPctTotal);
    const base = Math.floor(exact);
    return { key: c.key, base, rem: exact - base };
  });
  let cleft = remaining - cats.reduce((s, p) => s + p.base, 0);
  cats.sort((a, b) => b.rem - a.rem);
  cats.forEach((p, i) => { out[p.key] = p.base + (i < cleft ? 1 : 0); });
  // Hard-construction line items allocate from the AVAILABLE hard construction cap
  // (total cap minus the Builder / GC Fee), so hard lines + fee never exceed the 50% cap.
  Object.entries(CONSTRUCTION_BUDGET_ALLOCATION).forEach(([k, raw]) => {
    out[k] = Math.round(availableHard * raw);
  });
  return out;
}

// Projected all-in cost using quotes where available, else the budgeted cap.
function computeUnderwritingStatus(record) {
  const arv = Number(record.arv_market_value) || 0;
  const lineHard = Object.keys(CONSTRUCTION_BUDGET_ALLOCATION).reduce((sum, bk) => {
    const q = Number(record[QUOTE_KEY_MAP[bk]]);
    const b = Number(record[bk]) || 0;
    return sum + (q > 0 ? q : b);
  }, 0);
  // Total construction cost = editable Hard Construction preliminary + Builder/GC Fee,
  // stored in construction_budget (total). Falls back to the quote/budget line-item sum
  // + fee when not explicitly set. Never exceeds the 50% construction cap.
  const fee = arv > 0 ? (Number(record.builder_gc_fee) || defaultBuilderFee(arv)) : 0;
  const constructionCap = Math.round(arv * 0.5);
  const storedTotal = Number(record.construction_budget) || 0;
  const totalConstruction = arv > 0
    ? Math.min(storedTotal > 0 ? storedTotal : lineHard + fee, constructionCap)
    : (storedTotal || lineHard);
  const landCap = Math.round(arv * 0.15);
  const listed = Number(record.list_price) || Number(record.asking_price) || 0;
  const storedLand = Number(record.max_land_purchase_price) || 0;
  // If the list price is below our 15%-of-GDV land budget, we acquire at list —
  // dropping the projected land cost and lifting profit accordingly.
  const landAdjusted = arv > 0 && listed > 0 && listed < landCap;
  const effectiveLand = landAdjusted ? listed : storedLand;
  const cost = {
    max_land_purchase_price: effectiveLand,
    construction_budget: totalConstruction,
    soft_costs: Number(record.soft_costs) || 0,
    construction_contingency: Number(record.construction_contingency) || 0,
    financing_carry: Number(record.financing_carry) || 0,
    selling_costs: Number(record.selling_costs) || 0,
  };
  const allIn = Object.values(cost).reduce((a, b) => a + b, 0);
  const allInCap = Math.round(arv * UW_ALL_IN_CAP_PCT);
  const projectedProfit = arv - allIn;
  const minProfit = arv - allInCap;
  return { arv, cost, allIn, allInCap, projectedProfit, minProfit, pass: arv > 0 && allIn <= allInCap, landAdjusted, landCap, listed, landSavings: landAdjusted ? (landCap - listed) : 0 };
}

// Available Hard Construction budget = 50% construction cap minus the Builder / GC Fee.
// Used as the denominator for the construction line-item % breakdown.
function availableHardConstruction(record) {
  const cap = Math.round((Number(record.arv_market_value) || 0) * 0.5);
  const fee = cap > 0 ? (Number(record.builder_gc_fee) || defaultBuilderFee(Number(record.arv_market_value) || 0)) : 0;
  return Math.max(cap - fee, 0);
}

// Build the Acquisition snapshot dict from a Project (no navigation/side effects).
function buildAcquisitionSnapshot(proj) {
  // Carry every Property Analysis component field (Property Details, Lot Info,
  // Zoning, Comparables, Budget Analysis, Utilities, etc.) onto the Acquisition
  // so the acquisition team sees the full property picture, not just the numbers.
  const carryAnalysis = (snap) => {
    LEAD_FIELDS.forEach((f) => {
      if (["first_name", "last_name", "lead_status", "status"].includes(f.key)) return;
      const v = proj[f.key];
      if (v !== undefined && v !== null && v !== "" && snap[f.key] === undefined) snap[f.key] = v;
    });
    if (proj.lead_intake && !snap.lead_intake) snap.lead_intake = proj.lead_intake;
    return snap;
  };
  // Primary Strategy is the source of truth when the user has selected one.
  const primaryEcon = primaryStrategyEconomics(proj);
  if (primaryEcon) {
    const snap = {
      project_id: proj.id,
      acquisition_name: proj.project_name || proj.first_name || "Acquisition",
      lead_status: "Approved", status: "Approved",
      target_purchase_price: primaryEcon.maxLandBasis != null ? Math.max(0, primaryEcon.maxLandBasis) : null,
      approved_arv: primaryEcon.revenue ? Math.round(primaryEcon.revenue) : null,
      approved_max_land_price: primaryEcon.maxLandBasis != null ? Math.max(0, primaryEcon.maxLandBasis) : null,
      approved_all_in_cost: primaryEcon.allIn ? Math.round(primaryEcon.allIn) : null,
      approved_min_profit_pct: primaryEcon.margin != null ? Math.round(primaryEcon.margin) : 10,
      approved_min_profit_amt: primaryEcon.profit ? Math.round(primaryEcon.profit) : null,
      approval_date: new Date().toISOString().slice(0, 10),
      approved_by: proj.owner_name || null,
      primary_strategy: primaryEcon.key,
    };
    if (proj.owner_id) snap.owner_id = proj.owner_id;
    return carryAnalysis(snap);
  }
  // Multi-lot: approve the combined (blended) subdivision economics.
  const sub = proj.subdivision;
  if (sub && sub.enabled && (sub.lots || []).length > 0) {
    const r = computeSubdivision(sub);
    const offer = Math.round(r.totalArv * 0.15);
    const snap = {
      project_id: proj.id,
      acquisition_name: proj.project_name || proj.first_name || "Acquisition",
      lead_status: "Approved", status: "Approved",
      target_purchase_price: offer || null,
      approved_arv: Math.round(r.totalArv) || null,
      approved_max_land_price: offer || null,
      approved_hard_construction: null,
      approved_soft_costs: null,
      approved_contingency: null,
      approved_financing_carry: null,
      approved_selling_costs: null,
      approved_min_profit_pct: 10,
      approved_min_profit_amt: r.totalArv ? Math.round(r.totalArv * 0.1) : null,
      approved_all_in_cost: Math.round(r.allIn) || null,
      approved_cost_per_sqft: null,
      approved_living_sqft: null,
      approval_date: new Date().toISOString().slice(0, 10),
      approved_by: proj.owner_name || null,
    };
    if (proj.owner_id) snap.owner_id = proj.owner_id;
    return carryAnalysis(snap);
  }
  const arv = Number(proj.arv_market_value) || 0;
  const maxLand = Number(proj.max_land_purchase_price) || 0;
  const allIn = Math.round(
    maxLand + (Number(proj.construction_budget) || 0) + (Number(proj.soft_costs) || 0)
    + (Number(proj.construction_contingency) || 0) + (Number(proj.financing_carry) || 0)
    + (Number(proj.selling_costs) || 0)
  );
  const living = Number(proj.living_sqft) || 0;
  const snap = {
    project_id: proj.id,
    acquisition_name: proj.project_name || proj.first_name || "Acquisition",
    lead_status: "Approved", status: "Approved",
    target_purchase_price: maxLand || null,
    approved_arv: arv || null,
    approved_max_land_price: maxLand || null,
    approved_hard_construction: Number(proj.construction_budget) || null,
    approved_builder_gc_fee: Number(proj.builder_gc_fee) || defaultBuilderFee(arv),
    approved_soft_costs: Number(proj.soft_costs) || null,
    approved_contingency: Number(proj.construction_contingency) || null,
    approved_financing_carry: Number(proj.financing_carry) || null,
    approved_selling_costs: Number(proj.selling_costs) || null,
    approved_min_profit_pct: 10,
    approved_min_profit_amt: arv ? Math.round(arv * 0.1) : null,
    approved_all_in_cost: allIn || null,
    approved_cost_per_sqft: living > 0 && allIn ? Math.round(allIn / living) : null,
    approved_living_sqft: living || null,
    approval_date: new Date().toISOString().slice(0, 10),
    approved_by: proj.owner_name || null,
  };
  if (proj.owner_id) snap.owner_id = proj.owner_id;
  return carryAnalysis(snap);
}

// Build the Planning snapshot dict from an Acquisition + Project.
function buildPlanningSnapshot(acq, proj) {
  const addr = [proj.street_name || proj.address, proj.city, proj.state, proj.postal_code].filter(Boolean).join(", ");
  const lotSqft = Number(proj.lot_size_sqft) || null;
  const lotAcres = Number(proj.lot_size_acres) || (lotSqft ? Math.round((lotSqft / 43560) * 100) / 100 : null);
  const contractPrice = Number(acq.actual_purchase_price) || Number(acq.target_purchase_price) || null;
  const snap = {
    project_id: acq.project_id || proj.id || null,
    acquisition_id: acq.id || null,
    planning_name: acq.acquisition_name || proj.project_name || addr || "Planning",
    lead_status: "Planning Started", status: "Planning Started",
    planning_property_address: addr || null,
    planning_apn: proj.apn || null,
    planning_lot_size_sqft: lotSqft,
    planning_lot_acres: lotAcres,
    planning_acquisition_status: acq.lead_status || "Approved",
    planning_contract_price: contractPrice,
    due_diligence_deadline: acq.due_diligence_deadline || null,
    scheduled_closing_date: acq.closing_date || acq.estimated_close_date || null,
    planning_start_date: new Date().toISOString().slice(0, 10),
    planning_owner: acq.approved_by || proj.owner_name || null,
    sv_apn: proj.apn || null,
    sv_gross_lot_sqft: lotSqft,
    sv_lot_acres: lotAcres,
    be_gross_lot_area: lotSqft,
  };
  const owner = acq.owner_id || proj.owner_id;
  if (owner) snap.owner_id = owner;
  const intake = acq.lead_intake || proj.lead_intake;
  if (intake) snap.lead_intake = intake;
  return snap;
}

const HIDE = ["_id", "id", "created_by", "updated_by", "owner_id", "archived_at", "password_hash", "funnel_id", "funnel_stage", "product_id", "lead_id", "campaign_id", "header_image", "steps", "config", "stage", "account_id", "primary_contact_id", "customer_name", "external_refs", "arqplus_status", "arqplus_plan", "arqplus_link_status", "arqplus_linked_at", "arqplus_signed_up_at", "arqplus_converted_at", "arqplus_email", "arqplus_plan_name", "arqplus_subscription_status", "first_name", "last_name", "email", "mobile_phone", "business_phone", "job_title", "preferred_channel", "company_name", "website", "services_offered", "company_size", "country", "address", "lead_status", "temperature", "lead_score", "estimated_value", "product_name", "lead_source", "original_source", "most_recent_source", "campaign_name", "mail_address_status", "mail_address_deliverable", "mail_address_verified_at"];
const BADGE_KEYS = ["status", "lead_status", "stage", "temperature", "priority", "trial_status", "subscription_status", "connection_status", "conversation_status", "sentiment", "cancellation_risk", "conversion_likelihood", "usage_level", "audience_type", "content_type", "task_type", "campaign_type", "demo_status", "product_plan", "pricing_plan"];
const CURRENCY_KEYS = ["estimated_value", "mrr", "acv", "budget", "actual_spend", "revenue", "monthly_revenue_goal", "annual_revenue_goal", "monthly_price"];
const isMoneyKey = (k) => CURRENCY_KEYS.includes(k) || /^(budget_|quote_)/.test(k) || (/^approved_/.test(k) && !["approved_by", "approved_min_profit_pct", "approved_living_sqft"].includes(k)) || ["arv_market_value", "construction_budget", "max_land_purchase_price", "soft_costs", "construction_contingency", "financing_carry", "selling_costs", "est_net_profit", "list_price", "asking_price", "original_price", "price_reduction", "last_sale_price", "estimated_offer_price", "zillow_estimate", "redfin_estimate", "realtor_estimate", "estimated_market_value", "estimated_new_construction_arv", "avg_nearby_sale_price", "comp_1", "comp_2", "comp_3", "land_comp_1", "land_comp_2", "land_comp_3", "land_estimated_land_value", "land_estimated_new_construction_arv", "land_estimated_property_taxes", "target_purchase_price", "actual_purchase_price", "acquisition_costs", "planning_contract_price", "sv_est_cost", "sv_actual_cost"].includes(k);
const PHONE_KEYS = ["mobile_phone", "business_phone", "phone"];
const EMAIL_KEYS = ["email"];
const READONLY_KEYS = ["created_at", "updated_at", "date_created", "date_converted", "sourced_provider", "google_rating", "google_review_count", "phone_line_type", "phone_carrier", "email_source", "email_class", "construction_budget", "est_net_profit", "estimated_new_construction_arv", "approved_arv", "approved_max_land_price", "approved_hard_construction", "approved_soft_costs", "approved_contingency", "approved_financing_carry", "approved_selling_costs", "approved_min_profit_pct", "approved_min_profit_amt", "approved_all_in_cost", "approved_cost_per_sqft", "approved_living_sqft", "approval_date", "approved_by", "planning_property_address", "planning_apn", "planning_lot_size_sqft", "planning_lot_acres", "planning_acquisition_status", "planning_contract_price", "due_diligence_deadline", "scheduled_closing_date"];
const PRETTY_KEYS = ["sourced_provider", "email_source", "phone_line_type", "email_class", "source_type"];
const prettify = (s) => String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const DATE_KEYS = ["last_contacted", "last_response", "next_followup", "start_date", "end_date", "renewal_date", "launch_date", "expected_close_date", "converted_date", "last_login", "last_contact", "date"];

function QuickLinks({ related, navigate, resource, onOwnerClick, onTasksClick, onNotesClick }) {
  const owner = related.owner;
  const isAccount = resource === "accounts";
  const items = [
    { key: "owner", icon: User, label: "Owner", value: owner ? owner.name : "Unassigned", onClick: onOwnerClick, testid: "quicklink-owner" },
  ];
  const pc = related.primary_contact;
  if (isAccount) {
    items.push({
      key: "contacts", icon: Users, label: "Contacts", value: `${related.contacts.count}`,
      onClick: related.contacts.id ? () => navigate(`/record/contacts/${related.contacts.id}`) : null, testid: "quicklink-contacts",
    });
  } else {
    const contactId = (pc && pc.id) || related.contacts.id;
    items.push({
      key: "contacts", icon: Users, label: "Primary Contact",
      value: pc ? pc.name : (related.contacts.count ? `${related.contacts.count} contact(s)` : "—"),
      onClick: contactId ? () => navigate(`/record/contacts/${contactId}`) : null, testid: "quicklink-contacts",
    });
  }
  if (isAccount && related.leads) {
    items.push({
      key: "leads", icon: UserPlus, label: "Leads", value: `${related.leads.count}`,
      onClick: related.leads.id ? () => navigate(`/record/analysis/${related.leads.id}`) : null, testid: "quicklink-leads",
    });
  }
  items.push({
    key: "opportunities", icon: Target, label: "Opportunities", value: `${related.opportunities.count}`,
    onClick: related.opportunities.id ? () => navigate(`/record/opportunities/${related.opportunities.id}`) : null, testid: "quicklink-opportunities",
  });
  if (!isAccount) {
    items.push({
      key: "account", icon: Building2, label: "Account", value: related.account ? related.account.name : "—",
      onClick: related.account ? () => navigate(`/record/accounts/${related.account.id}`) : null, testid: "quicklink-account",
    });
  }
  if (related.tasks) {
    items.push({
      key: "tasks", icon: CheckSquare, label: "Tasks", value: `${related.tasks.count}`,
      onClick: onTasksClick, testid: "quicklink-tasks",
    });
  }
  if (!isAccount && related.work_items) {
    items.push({
      key: "work_items", icon: Inbox, label: "Work Queue", value: `${related.work_items.count}`,
      onClick: related.work_items.id ? () => navigate(`/work-queue/${related.work_items.id}`) : () => navigate("/work-queue"),
      testid: "quicklink-work-items",
    });
  }
  if (related.notes) {
    items.push({
      key: "notes", icon: StickyNote, label: "Notes", value: `${related.notes.count}`,
      onClick: onNotesClick, testid: "quicklink-notes",
    });
  }
  items.push({
    key: "campaign", icon: Megaphone, label: "Campaign", value: related.campaign ? related.campaign.name : "—",
    onClick: related.campaign && related.campaign.id ? () => navigate(`/record/campaigns/${related.campaign.id}`) : null, testid: "quicklink-campaign",
  });
  return (
    <div className="rounded-lg border border-border bg-secondary/30 px-5 py-3.5 mb-6" data-testid="quick-links">
      <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        <Link2 className="h-3.5 w-3.5" />Quick Links
      </div>
      <div className="flex flex-wrap justify-between gap-x-4 gap-y-3">
        {items.map(({ key, icon: Icon, label, value, onClick, testid }) => (
          <div key={key} className="min-w-0 max-w-[200px]">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 whitespace-nowrap">
              <Icon className="h-3.5 w-3.5 shrink-0" />{label}
            </div>
            {onClick ? (
              <button onClick={onClick} data-testid={testid} className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1 text-left max-w-full">
                <span className="truncate">{value}</span>
              </button>
            ) : (
              <div className="text-sm font-medium text-foreground truncate" data-testid={testid}>{value}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectQuickLinks({ links, navigate, owner, onOwnerClick, resource }) {
  const L = links || {};
  const item = (key, icon, label, rec, path, valueFn) => {
    const isSelf = rec && (
      (key === "analysis" && resource === "leads") ||
      (key === "acquisition" && resource === "acquisition") ||
      (key === "planning" && resource === "planning") ||
      (key === "design" && resource === "opportunities") ||
      (key === "preconstruction" && resource === "preconstruction") ||
      (key === "construction" && resource === "construction") ||
      (key === "closeout" && resource === "closeout") ||
      (key === "project" && resource === "projects")
    );
    return {
      key, icon, label,
      value: rec ? (valueFn ? valueFn(rec) : "View") : "—",
      onClick: rec && !isSelf ? () => navigate(`${path}/${rec.id}`) : null,
      testid: `projectlink-${key}`,
    };
  };
  const items = [
    { key: "owner", icon: User, label: "Owner", value: owner ? owner.name : "Unassigned", onClick: onOwnerClick, testid: "projectlink-owner" },
  ];
  if (resource !== "projects") {
    items.push(item("project", Hammer, "Project", L.project, "/record/projects", (r) => r.project_name || r.first_name || "Project"));
  }
  items.push(
    item("analysis", UserPlus, "Property Analysis", L.analysis, "/record/analysis", (r) => r.property_analysis_name || r.first_name || "Analysis"),
    item("acquisition", Target, "Acquisition", L.acquisition, "/record/acquisition", (r) => r.lead_status || "Acquisition"),
    item("planning", Ruler, "Planning", L.planning, "/record/planning", (r) => r.lead_status || "Planning"),
    item("design", Home, "Design", L.design, "/record/opportunities", (r) => r.stage || "Design"),
    item("preconstruction", Ruler, "Preconstruction", L.preconstruction, "/record/preconstruction", (r) => r.lead_status || "Preconstruction"),
    item("construction", HardHat, "Construction", L.construction, "/record/construction", (r) => r.lead_status || "Construction"),
    item("closeout", CheckCircle2, "Closeout", L.closeout, "/record/closeout", (r) => r.lead_status || "Closeout"),
  );
  return (
    <div className="rounded-lg border border-border bg-secondary/30 px-5 py-3.5 mb-6" data-testid="quick-links">
      <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        <Link2 className="h-3.5 w-3.5" />Quick Links
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-3">
        {items.map(({ key, icon: Icon, label, value, onClick, testid }) => (
          <div key={key} className="min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 whitespace-nowrap">
              <Icon className="h-3.5 w-3.5 shrink-0" />{label}
            </div>
            {onClick ? (
              <button onClick={onClick} data-testid={testid} className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1 text-left max-w-full">
                <span className="truncate">{value}</span>
              </button>
            ) : (
              <div className="text-sm font-medium text-muted-foreground truncate" data-testid={testid}>{value}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function RelatedList({ title, testid, rows, columns, onRowClick, emptyLabel }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid={testid}>
      <div className="px-6 py-3.5 border-b border-border bg-secondary/30 flex items-center justify-between">
        <div className="text-sm font-semibold">{title}</div>
        <span className="text-xs text-muted-foreground">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="px-6 py-6 text-center text-sm text-muted-foreground">{emptyLabel}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                {columns.map((c) => <th key={c.key} className="px-6 py-2.5 font-medium">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => onRowClick(r)} className="border-b border-border/50 last:border-0 hover:bg-accent/50 cursor-pointer" data-testid={`${testid}-row-${r.id}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="px-6 py-3">
                      {c.badge ? <StatusBadge value={r[c.key]} /> : <span className="text-foreground">{isPhoneKey(c.key) ? (formatPhone(r[c.key]) || "—") : displayValue(c.render ? c.render(r) : r[c.key])}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ArqplusCard({ account }) {
  const [plan, setPlan] = useState("pro");
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const status = account.arqplus_status;
  const arqplusId = (account.external_refs || {}).arqplus_account_id;

  const load = (p) => {
    setLoading(true);
    api.get(`/accounts/${account.id}/arqplus-link?plan=${p || plan}`)
      .then((r) => setLink(r.data.url))
      .catch(() => setLink(""))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load("pro");
    api.get(`/activities?record_id=${account.id}&limit=200&sort_by=created_at&sort_dir=desc`)
      .then((r) => setEvents((r.data.items || []).filter((a) => a.action === "arqplus_webhook")))
      .catch(() => setEvents([]));
    /* eslint-disable-next-line */
  }, [account.id]);

  const copy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link).then(
      () => toast.success("Arq+ signup link copied"),
      () => toast.error("Could not copy link")
    );
  };

  const statusStyle = status === "Converted / Paying"
    ? "bg-primary/20 text-primary border-primary/40"
    : status === "Signed up with Arq+"
    ? "bg-blue-500/15 text-blue-400 border-blue-500/30"
    : "bg-secondary/50 text-muted-foreground border-border";

  const isPaying = (a) => /paying|customer|subscription/i.test(a.activity || "");

  return (
    <div className="rounded-lg border border-border bg-card p-5 mb-6" data-testid="arqplus-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Rocket className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Arq+ Product</h3>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full border ${statusStyle}`} data-testid="arqplus-status-badge">
          {status || "Not signed up"}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Share this signup link with the prospect. When they sign up and convert in Arq+, this status updates automatically.
      </p>
      <div className="flex items-center gap-2 mb-3">
        <label className="text-xs text-muted-foreground">Plan</label>
        <Select value={plan} onValueChange={(v) => { setPlan(v); load(v); }}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="arqplus-plan-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="starter">Starter</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="business">Business</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <Input readOnly value={loading ? "Generating…" : link} data-testid="arqplus-link-input" className="text-xs font-mono" />
        <Button size="sm" variant="outline" onClick={copy} disabled={!link} data-testid="arqplus-copy-btn">Copy</Button>
      </div>
      {arqplusId && (
        <p className="text-[11px] text-muted-foreground mt-3" data-testid="arqplus-account-id">
          Linked Arq+ account: <span className="font-mono">{arqplusId}</span>
        </p>
      )}

      <div className="mt-5 pt-4 border-t border-border" data-testid="arqplus-timeline">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Conversion Timeline</h4>
        {events.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="arqplus-timeline-empty">
            No Arq+ activity yet. It will appear here the moment this prospect signs up.
          </p>
        ) : (
          <ol className="relative border-l border-border ml-1.5 space-y-4">
            {events.map((a) => {
              const paying = isPaying(a);
              return (
                <li key={a.id} className="ml-4" data-testid={`arqplus-timeline-item-${a.id}`}>
                  <span
                    className={`absolute -left-[7px] mt-1 h-3 w-3 rounded-full border-2 border-card ${paying ? "bg-primary" : "bg-blue-400"}`}
                  />
                  <div className="text-sm text-foreground font-medium">{a.activity || (paying ? "Became a paying Arq+ customer" : "Signed up with Arq+")}</div>
                  <div className="text-[11px] text-muted-foreground">{formatDateTime(a.created_at)}</div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

function AccountRelatedLists({ accountId, navigate }) {
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [opps, setOpps] = useState([]);

  useEffect(() => {
    if (!accountId) return;
    api.get(`/contacts?account_id=${accountId}&limit=200`).then((r) => setContacts(r.data.items || [])).catch(() => setContacts([]));
    api.get(`/leads?account_id=${accountId}&limit=200`).then((r) => setLeads(r.data.items || [])).catch(() => setLeads([]));
    api.get(`/opportunities?account_id=${accountId}&limit=200`).then((r) => setOpps(r.data.items || [])).catch(() => setOpps([]));
  }, [accountId]);

  return (
    <div className="mt-6 space-y-5" data-testid="account-related-lists">
      <RelatedList
        title="Contacts" testid="account-contacts" rows={contacts}
        columns={[{ key: "name", label: "Name", render: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.email }, { key: "email", label: "Email" }, { key: "job_title", label: "Title" }, { key: "is_primary", label: "Primary", render: (r) => (r.is_primary ? "Yes" : "") }]}
        onRowClick={(r) => navigate(`/record/contacts/${r.id}`)} emptyLabel="No contacts linked to this account yet."
      />
      <RelatedList
        title="Leads" testid="account-leads" rows={leads}
        columns={[{ key: "name", label: "Name", render: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() }, { key: "email", label: "Email" }, { key: "lead_status", label: "Status", badge: true }]}
        onRowClick={(r) => navigate(`/record/analysis/${r.id}`)} emptyLabel="No leads linked to this account yet."
      />
      <RelatedList
        title="Opportunities" testid="account-opportunities" rows={opps}
        columns={[{ key: "opportunity_name", label: "Opportunity" }, { key: "product_plan", label: "Plan" }, { key: "stage", label: "Stage", badge: true }]}
        onRowClick={(r) => navigate(`/record/opportunities/${r.id}`)} emptyLabel="No opportunities linked to this account yet."
      />
    </div>
  );
}

function inferDef(key, value) {
  if (typeof value === "boolean") return { type: "boolean" };
  if (DATE_KEYS.includes(key) || (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value))) return { type: "date" };
  if (typeof value === "number") return { type: "number" };
  return { type: "text" };
}

function StatusPath({ statuses, current, onChange, saving }) {
  const idx = statuses.indexOf(current);
  const point = 13;
  const clipFor = (i, last) =>
    last
      ? `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${i === 0 ? 0 : point}px 50%)`
      : i === 0
      ? `polygon(0 0, calc(100% - ${point}px) 0, 100% 50%, calc(100% - ${point}px) 100%, 0 100%)`
      : `polygon(0 0, calc(100% - ${point}px) 0, 100% 50%, calc(100% - ${point}px) 100%, 0 100%, ${point}px 50%)`;

  return (
    <div className="rounded-lg border border-border bg-card p-1.5 mb-6" data-testid="lead-status-path">
      <div className="flex w-full">
        {statuses.map((s, i) => {
          const done = i < idx;
          const active = i === idx;
          const last = i === statuses.length - 1;
          const cls = active
            ? "bg-primary text-primary-foreground font-semibold"
            : done
            ? "bg-primary/25 text-foreground hover:bg-primary/35"
            : "bg-secondary/50 text-muted-foreground hover:bg-accent hover:text-foreground";
          return (
            <button
              key={s}
              onClick={() => !active && onChange(s)}
              disabled={saving}
              data-testid={`status-step-${s.replace(/\s+/g, "-").toLowerCase()}`}
              title={`Mark as ${stageLabel(s)}`}
              className={`flex-1 min-w-0 truncate text-center whitespace-nowrap text-xs transition-colors py-2 ${cls} ${i === 0 ? "pl-3 pr-4" : "pl-5 pr-4"}`}
              style={{ clipPath: clipFor(i, last), marginLeft: i ? -point : 0 }}
            >
              {stageLabel(s)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function displayValue(v) {
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return formatDate(v);
  return String(v);
}

// US ZIP -> city/state/county. Zippopotam.us gives city/state + lat/lng (no key);
// the FCC Census block API (no key) resolves the county from those coordinates.
async function lookupZip(zip) {
  const clean = (zip || "").match(/\d{5}/)?.[0];
  if (!clean) return null;
  try {
    const res = await fetch(`https://api.zippopotam.us/us/${clean}`);
    if (!res.ok) return null;
    const data = await res.json();
    const place = data.places?.[0];
    if (!place) return null;
    const out = { city: place["place name"], state: place["state abbreviation"] || place.state };
    try {
      const lat = place.latitude, lng = place.longitude;
      if (lat && lng) {
        const fcc = await fetch(`https://geo.fcc.gov/api/census/block/find?latitude=${lat}&longitude=${lng}&format=json`);
        if (fcc.ok) {
          const fd = await fcc.json();
          const county = fd?.County?.name;
          if (county) out.county = county.replace(/\s+County$/i, "");
        }
      }
    } catch { /* county is best-effort */ }
    return out;
  } catch {
    return null;
  }
}

function FieldRow({ fieldKey, value, def, onSave, quick, campaigns, computed, constructionTotal, scale = 1, cleanEdit = false, linkHref = null }) {
  const fdef = def || inferDef(fieldKey, value);
  const isCampaign = fieldKey === "campaign_name" && Array.isArray(campaigns);
  const NONE = "__none__";
  const editable = !READONLY_KEYS.includes(fieldKey) && !computed && !/^approved_/.test(fieldKey);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [ynChoice, setYnChoice] = useState("");
  const [ynText, setYnText] = useState("");
  const [saving, setSaving] = useState(false);
  const isBadge = BADGE_KEYS.includes(fieldKey) && value !== "" && value != null;
  const hasVal = value !== "" && value != null;
  // Multi-lot Property Budget: per-home budget lines scale by the lot count for the project view.
  const isBudgetKey = /^budget_/.test(fieldKey) && CONSTRUCTION_BUDGET_ALLOCATION[fieldKey] != null;
  const scaledValue = isBudgetKey && hasVal && scale && scale !== 1 ? Number(value) * scale : value;
  const cbPct = isBudgetKey
    ? (constructionTotal > 0 && hasVal ? (Number(scaledValue) / constructionTotal) * 100 : (CONSTRUCTION_BUDGET_ALLOCATION[fieldKey] / CB_RAW_TOTAL) * 100)
    : null;
  const COMMA_NUM_KEYS = ["lot_size_sqft", "lot_size_acres"];
  const shown = isMoneyKey(fieldKey) && hasVal ? formatCurrency(scaledValue)
    : isPhoneKey(fieldKey) && hasVal ? formatPhone(value)
    : COMMA_NUM_KEYS.includes(fieldKey) && hasVal && !isNaN(Number(String(value).replace(/,/g, ""))) ? Number(String(value).replace(/,/g, "")).toLocaleString("en-US", { maximumFractionDigits: 2 })
    : PRETTY_KEYS.includes(fieldKey) && hasVal ? prettify(value) : displayValue(value);
  const isPhone = quick && PHONE_KEYS.includes(fieldKey) && hasVal;
  const isEmail = quick && EMAIL_KEYS.includes(fieldKey) && hasVal;

  const start = () => {
    if (fdef.type === "multiselect") setDraft(Array.isArray(value) ? value : value ? [value] : []);
    else if (isCampaign) setDraft(value || NONE);
    else if (fdef.type === "yesno_other") {
      const v = value == null ? "" : String(value);
      if (v === "Yes" || v === "No") { setYnChoice(v); setYnText(""); }
      else if (v) { setYnChoice("Other"); setYnText(v); }
      else { setYnChoice(""); setYnText(""); }
    }
    else setDraft(fdef.type === "boolean" ? (value ? "Yes" : "No") : (value ?? ""));
    setEditing(true);
  };
  const cancel = () => setEditing(false);
  const persist = async (rawDraft, rawYnChoice, rawYnText) => {
    setSaving(true);
    let val = rawDraft;
    if (fdef.type === "number") val = rawDraft === "" ? null : Number(rawDraft);
    else if (fdef.type === "boolean") val = rawDraft === "Yes";
    else if (fdef.type === "yesno_other") val = rawYnChoice === "Other" ? (rawYnText?.trim() || "Other") : (rawYnChoice || null);
    else if (isCampaign) val = rawDraft === NONE ? "" : rawDraft;
    const ok = await onSave(fieldKey, val);
    setSaving(false);
    if (ok) setEditing(false);
    return ok;
  };
  const save = () => persist(draft, ynChoice, ynText);
  // clean-mode select/boolean/campaign save immediately on choose
  const chooseCommit = (v) => { setDraft(v); persist(v); };

  const renderValue = () => {
    if (isBadge) return <StatusBadge value={value} />;
    if (Array.isArray(value))
      return value.length ? (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v) => (
            <span key={v} className="rounded-full bg-primary/15 text-foreground px-2.5 py-0.5 text-xs font-medium">{v}</span>
          ))}
        </div>
      ) : <div className="text-sm font-medium">—</div>;
    if ((fieldKey === "website" || fieldKey === "listing_url" || fieldKey === "source_url") && hasVal) {
      const href = /^https?:\/\//i.test(String(value)) ? String(value) : `https://${value}`;
      return (
        <a href={href} target="_blank" rel="noreferrer" className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1.5 break-all" data-testid={`link-${fieldKey}`}>
          <ExternalLink className="h-3.5 w-3.5 shrink-0" />{value}
        </a>
      );
    }
    if (linkHref && hasVal) {
      const href = /^https?:\/\//i.test(String(linkHref)) ? String(linkHref) : `https://${linkHref}`;
      return (
        <a href={href} target="_blank" rel="noreferrer" className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1.5 break-words leading-snug" data-testid={`comp-source-link-${fieldKey}`} title="Verify original listing">
          {shown}<ExternalLink className="h-3 w-3 shrink-0 opacity-70" />
        </a>
      );
    }
    if (isPhone)
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => quick("call", value)} className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1.5" data-testid={`quick-call-${fieldKey}`}>
            <Phone className="h-3.5 w-3.5" />{formatPhone(value)}
          </button>
          {fieldKey === "mobile_phone" && (
            <button onClick={() => quick("sms", value)} className="p-1 rounded-md text-muted-foreground hover:text-primary hover:bg-accent" title="Send SMS" data-testid="quick-sms-mobile_phone">
              <MessageSquare className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      );
    if (isEmail)
      return (
        <button onClick={() => quick("email", value)} className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1.5 break-all" data-testid={`quick-email-${fieldKey}`}>
          <Mail className="h-3.5 w-3.5 shrink-0" />{value}
        </button>
      );
    if (!quick && isPhoneKey(fieldKey) && hasVal) {
      const dial = String(value).replace(/[^\d+]/g, "");
      return (
        <div className="flex items-center gap-2 flex-wrap">
          <a href={`tel:${dial}`} className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1.5" data-testid={`dial-${fieldKey}`}>
            <Phone className="h-3.5 w-3.5 shrink-0" />{formatPhone(value)}
          </a>
          {fieldKey === "mobile_phone" && (
            <a href={`sms:${dial}`} className="p-1 rounded-md text-muted-foreground hover:text-primary hover:bg-accent" title="Send text" data-testid="text-mobile_phone">
              <MessageSquare className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      );
    }
    if (!quick && EMAIL_KEYS.includes(fieldKey) && hasVal)
      return (
        <a href={`mailto:${value}`} className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1.5 break-all" data-testid={`mail-${fieldKey}`}>
          <Mail className="h-3.5 w-3.5 shrink-0" />{value}
        </a>
      );
    return <div className="text-sm font-medium break-words leading-snug">{shown}</div>;
  };

  const renderEditor = () => {
    if (isCampaign) {
      return (
        <Select value={String(draft ?? NONE)} onValueChange={cleanEdit ? chooseCommit : setDraft}>
          <SelectTrigger className="h-8 text-sm" data-testid={`inline-input-${fieldKey}`}><SelectValue placeholder="Select campaign…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>— None —</SelectItem>
            {campaigns.map((c) => {
              const n = c.name || c.campaign_name;
              return <SelectItem key={c.id} value={n}>{n}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      );
    }
    if (fdef.type === "multiselect") {
      return <MultiSelect options={fdef.options} value={Array.isArray(draft) ? draft : []} onChange={setDraft} testid={`inline-input-${fieldKey}`} />;
    }
    if (fdef.type === "select") {
      if (fdef.searchable || (fdef.options && fdef.options.length > 10)) {
        return (
          <SearchableSelect
            options={fdef.options}
            value={String(draft ?? "")}
            onChange={cleanEdit ? chooseCommit : setDraft}
            testid={`inline-input-${fieldKey}`}
          />
        );
      }
      return (
        <Select value={String(draft ?? "")} onValueChange={cleanEdit ? chooseCommit : setDraft}>
          <SelectTrigger className="h-8 text-sm" data-testid={`inline-input-${fieldKey}`}><SelectValue /></SelectTrigger>
          <SelectContent>{fdef.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (fdef.type === "boolean") {
      return (
        <Select value={String(draft ?? "No")} onValueChange={cleanEdit ? chooseCommit : setDraft}>
          <SelectTrigger className="h-8 text-sm" data-testid={`inline-input-${fieldKey}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
        </Select>
      );
    }
    if (fdef.type === "yesno_other") {
      return (
        <div className="flex items-center gap-2 flex-1">
          <Select value={ynChoice || ""} onValueChange={setYnChoice}>
            <SelectTrigger className="h-8 text-sm w-28 shrink-0" data-testid={`inline-input-${fieldKey}`}><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Yes">Yes</SelectItem>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
          {ynChoice === "Other" && (
            <Input value={ynText} onChange={(e) => setYnText(e.target.value)} placeholder="Specify…" className="h-8 text-sm flex-1 min-w-0" autoFocus data-testid={`inline-other-${fieldKey}`} />
          )}
        </div>
      );
    }
    if (fdef.type === "textarea") {
      return <Textarea value={draft ?? ""} onChange={(e) => setDraft(e.target.value)} rows={2} className="text-sm" data-testid={`inline-input-${fieldKey}`} />;
    }
    if (isPhoneKey(fieldKey)) {
      // Mask to (XXX)XXX-XXXX and SAVE that formatted value (same everywhere:
      // Directory + property records) so it renders and dials consistently.
      return (
        <Input
          type="tel"
          value={formatPhoneInput(draft)}
          onChange={(e) => setDraft(formatPhoneInput(e.target.value))}
          onBlur={cleanEdit ? () => persist(formatPhoneInput(draft)) : undefined}
          onKeyDown={cleanEdit ? (e) => { if (e.key === "Enter") persist(formatPhoneInput(draft)); if (e.key === "Escape") cancel(); } : undefined}
          placeholder="(XXX)XXX-XXXX"
          className="h-8 text-sm" autoFocus data-testid={`inline-input-${fieldKey}`}
        />
      );
    }
    return (
      <Input
        type={fdef.type === "number" ? "number" : fdef.type === "date" ? "date" : "text"}
        value={draft ?? ""} onChange={(e) => setDraft(e.target.value)}
        onBlur={cleanEdit ? () => persist(draft) : undefined}
        onKeyDown={cleanEdit ? (e) => { if (e.key === "Enter") persist(draft); if (e.key === "Escape") cancel(); } : undefined}
        className="h-8 text-sm" autoFocus data-testid={`inline-input-${fieldKey}`}
      />
    );
  };

  return (
    <div className="group flex items-start justify-between gap-3 py-2.5 border-b border-border/50" data-testid={`detail-${fieldKey}`}>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground mb-1 flex items-center gap-2">
          {fdef?.label || prettify(fieldKey)}
          {cbPct != null && (
            <span className="text-[10px] font-semibold text-primary/90 bg-primary/10 px-1.5 py-0.5 rounded" data-testid={`budget-pct-${fieldKey}`}>{cbPct.toFixed(2)}% of const.</span>
          )}
        </div>
        {editing ? (
          <div className="flex items-center gap-2">
            {renderEditor()}
            {(!cleanEdit || ["multiselect", "yesno_other", "textarea"].includes(fdef.type)) && (
              <>
                <button onClick={save} disabled={saving} className="p-1.5 rounded-md bg-primary text-primary-foreground shrink-0" data-testid={`inline-save-${fieldKey}`}>
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                </button>
                <button onClick={cancel} className="p-1.5 rounded-md border border-border text-muted-foreground shrink-0" data-testid={`inline-cancel-${fieldKey}`}><X className="h-3.5 w-3.5" /></button>
              </>
            )}
          </div>
        ) : renderValue()}
      </div>
      {editable && !editing && (
        <button onClick={start} className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-accent shrink-0" aria-label={`Edit ${fieldKey}`} data-testid={`edit-field-${fieldKey}`}>
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function CollapseCard({ title, right, banner, children, testid, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid={testid}>
      {banner}
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full px-6 py-3.5 border-b border-border bg-secondary/30 flex items-center justify-between gap-3 text-left hover:bg-secondary/50 transition-colors" data-testid={`${testid}-toggle`}>
        <div className="text-sm font-semibold">{title}</div>
        <div className="flex items-center gap-3">
          {right}
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && children}
    </div>
  );
}

function PortalViewsCard({ recordId }) {
  const [stats, setStats] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get(`/ai/portal_views/${recordId}`).then((r) => setStats(r.data)).catch(() => setStats({ unique_ips: 0 }));
    api.get(`/ai/deal_recipients/${recordId}`).then((r) => setRecipients(r.data.recipients || [])).catch(() => setRecipients([]));
  };
  useEffect(load, [recordId]);

  const addRecipient = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const res = await api.post("/ai/deal_recipients", { record_id: recordId, name: name.trim(), email: email.trim() || null });
      const url = `${window.location.origin}${res.data.path}`;
      await navigator.clipboard?.writeText(url).catch(() => {});
      toast.success(`Link for ${res.data.name} created & copied`);
      setName(""); setEmail("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  const copyLink = (r) => {
    navigator.clipboard?.writeText(`${window.location.origin}${r.path}`);
    toast.success(`Link for ${r.name} copied`);
  };
  const removeRecipient = async (r) => {
    try { await api.delete(`/ai/deal_recipients/${r.id}`); load(); toast.success("Recipient removed"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="portal-views-card">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-accent/40 transition-colors" data-testid="portal-views-card-toggle">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Investor Links</h3>
          {recipients.length > 0 && (
            <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-1.5 py-0.5">{recipients.length}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {stats && (
            <span className="text-[11px] text-muted-foreground" data-testid="portal-views-count">
              {stats.unique_ips ?? 0} unique view{(stats.unique_ips ?? 0) === 1 ? "" : "s"}
            </span>
          )}
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-border pt-4">
          <p className="text-xs text-muted-foreground mb-3">
            Create a unique link per investor to see exactly who opened the deal.
          </p>

          <div className="space-y-2 mb-4">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Investor name" className="h-8 text-sm" data-testid="recipient-name-input" />
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" className="h-8 text-sm" data-testid="recipient-email-input" />
            <Button size="sm" onClick={addRecipient} disabled={busy || !name.trim()} className="w-full h-8" data-testid="recipient-add-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5 mr-1.5" />}Create link
            </Button>
          </div>

          {recipients.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="recipients-empty">No investor links yet.</p>
          ) : (
            <div className="space-y-2" data-testid="recipients-list">
              {recipients.map((r) => {
                const opened = (r.opens || 0) > 0;
                return (
                  <div key={r.id} className="rounded-md border border-border/60 bg-secondary/20 px-3 py-2" data-testid={`recipient-${r.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">{r.name}</div>
                        {r.email && <div className="text-[11px] text-muted-foreground truncate">{r.email}</div>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => copyLink(r)} className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-accent" title="Copy link" data-testid={`recipient-copy-${r.id}`}>
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => removeRecipient(r)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-accent" title="Remove" data-testid={`recipient-delete-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-1.5">
                      {opened ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary" data-testid={`recipient-status-${r.id}`}>
                          <CheckCircle2 className="h-3 w-3" />Opened {r.opens}× · {formatDateTime(r.last_opened)}
                        </span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground" data-testid={`recipient-status-${r.id}`}>Not opened yet</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CostPerSqFtAnalysis({ record }) {
  const living = Number(record.living_sqft) || 0;
  const land = Number(record.max_land_purchase_price) || 0;
  const hard = Number(record.construction_budget) || 0;
  const soft = Number(record.soft_costs) || 0;
  const cont = Number(record.construction_contingency) || 0;
  const fin = Number(record.financing_carry) || 0;
  const sell = Number(record.selling_costs) || 0;
  const arv = Number(record.arv_market_value) || 0;
  const totalAllIn = land + hard + soft + cont + fin + sell;
  const netProfit = arv - totalAllIn;
  const has = living > 0;
  const psf = (v) => (has ? v / living : null);
  const hardPsf = psf(hard);
  const totalBuildPsf = psf(hard + soft + cont);
  const allInPsf = psf(totalAllIn);
  const arvPsf = psf(arv);
  const netProfitPsf = psf(netProfit);
  const margin = arv > 0 ? (netProfit / arv) * 100 : null;

  const fmtPsf = (v) => (v == null ? "—" : "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const tier =
    margin == null ? { label: "Enter data", cls: "bg-muted text-muted-foreground", Icon: ShieldCheck }
    : margin >= 15 ? { label: "Strong", cls: "bg-emerald-500/15 text-emerald-400", Icon: ShieldCheck }
    : margin >= 10 ? { label: "Acceptable", cls: "bg-primary/15 text-primary", Icon: ShieldCheck }
    : margin >= 5 ? { label: "Marginal", cls: "bg-amber-500/15 text-amber-400", Icon: AlertTriangle }
    : { label: "Deal Risk", cls: "bg-destructive/15 text-destructive", Icon: AlertTriangle };
  const costWarning = has && allInPsf != null && arvPsf != null && allInPsf >= arvPsf;

  const rows = [
    { key: "hard", label: "Hard Construction $/SF", value: fmtPsf(hardPsf) },
    { key: "total-build", label: "Total Build $/SF", value: fmtPsf(totalBuildPsf) },
    { key: "all-in", label: "All-In Development $/SF", value: fmtPsf(allInPsf) },
    { key: "break-even", label: "Break-Even $/SF", value: fmtPsf(allInPsf) },
    { key: "arv", label: "Market / GDV $/SF", value: fmtPsf(arvPsf) },
    { key: "net-profit", label: "Net Profit $/SF", value: fmtPsf(netProfitPsf), highlight: true },
    { key: "margin", label: "Net Profit Margin", value: margin == null ? "—" : `${margin.toFixed(1)}%`, highlight: true },
  ];

  return (
    <CollapseCard
      testid="cost-per-sqft-card"
      title="Cost Per Square Foot Analysis"
      right={
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">per proposed home size sq ft</span>
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded ${tier.cls}`} data-testid="cost-psf-status">
            <tier.Icon className="h-3 w-3" />{tier.label}
          </span>
        </div>
      }
    >
      {costWarning && (
        <div className="px-6 py-3 bg-destructive/10 border-b border-destructive/30 flex items-start gap-2 text-xs text-destructive" data-testid="cost-psf-warning">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Projected development cost meets or exceeds market value. Review land price, construction budget, financing, or GDV before proceeding.</span>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="px-6 py-2.5 font-medium">Metric</th>
            <th className="px-6 py-2.5 font-medium text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={`border-b border-border/50 ${r.highlight ? "bg-secondary/20" : ""}`} data-testid={`cost-psf-${r.key}`}>
              <td className={`px-6 py-3 ${r.highlight ? "font-semibold" : "text-foreground"}`}>{r.label}</td>
              <td className={`px-6 py-3 text-right font-semibold ${r.highlight ? (netProfit >= 0 ? "text-primary" : "text-destructive") : "text-foreground"}`}>{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Development Spread */}
      <div className="px-6 py-4 border-t border-border bg-secondary/20">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Development Spread</div>
        <div className="flex items-center gap-2 text-sm flex-wrap" data-testid="dev-spread">
          <span className="text-muted-foreground">Market / GDV $/SF</span>
          <span className="font-semibold">{fmtPsf(arvPsf)}</span>
          <span className="text-muted-foreground">−</span>
          <span className="text-muted-foreground">All-In $/SF</span>
          <span className="font-semibold">{fmtPsf(allInPsf)}</span>
          <span className="text-muted-foreground">=</span>
          <span className={`font-semibold ${netProfit >= 0 ? "text-primary" : "text-destructive"}`}>{fmtPsf(netProfitPsf)}{has ? "/SF" : ""}</span>
        </div>
        <div className="flex gap-8 mt-3 text-right">
          <div className="text-left">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total All-In Cost</div>
            <div className="text-sm font-semibold" data-testid="cost-psf-total-allin">{arv || totalAllIn ? formatCurrency(Math.round(totalAllIn)) : "—"}</div>
          </div>
          <div className="text-left">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Projected Net Profit</div>
            <div className={`text-sm font-semibold ${netProfit >= 0 ? "text-primary" : "text-destructive"}`} data-testid="cost-psf-net-profit">{arv ? formatCurrency(Math.round(netProfit)) : "—"}</div>
          </div>
        </div>
      </div>
    </CollapseCard>
  );
}

function PrelimCell({ amount, breached, onSave, testid, hint }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const begin = () => { setDraft(amount ? String(Math.round(amount)) : ""); setEditing(true); };
  const commit = async () => {
    setEditing(false);
    const num = Number(String(draft).replace(/[^0-9.]/g, ""));
    if (!isNaN(num)) await onSave(num);
  };
  return (
    <td className={`px-6 py-3 text-right font-semibold ${breached ? "text-amber-400" : "text-foreground"}`}>
      {editing ? (
        <input
          autoFocus type="number" value={draft}
          onChange={(e) => setDraft(e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") setEditing(false); }}
          className="w-28 bg-secondary/40 border border-border rounded px-2 py-1 text-right text-sm outline-none focus:border-primary"
          data-testid={`${testid}-input`}
        />
      ) : hint ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={begin} className="hover:underline decoration-dotted underline-offset-2" data-testid={testid}>{formatCurrency(Math.round(amount || 0))}</button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[260px] text-center leading-snug">{hint}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <button onClick={begin} className="hover:underline decoration-dotted underline-offset-2" title="Click to edit" data-testid={testid}>{formatCurrency(Math.round(amount || 0))}</button>
      )}
    </td>
  );
}

function UnderwritingModel({ record, onSave, defaultOpen = false }) {
  // Multi-lot: scale the underwriting model to the whole project — GDV = blended total,
  // per-home costs × lot count (land is bought ONCE), so caps/variances reflect the deal.
  const isSub = isSubdivisionActive(record);
  const roll = isSub ? computeSubdivision(record.subdivision) : null;
  const n = isSub ? Math.max(1, roll.count) : 1;
  const arv = isSub ? roll.totalArv : computeUnderwritingStatus(record).arv;
  const has = arv > 0;
  const money = (v) => (has ? formatCurrency(Math.round(v)) : "—");
  const signed = (v) => (v > 0 ? `+${formatCurrency(Math.round(v))}` : v < 0 ? `-${formatCurrency(Math.abs(Math.round(v)))}` : "$0");
  const feePer = has ? (Number(record.builder_gc_fee) || defaultBuilderFee(arv / n)) : 0;
  const fee = feePer * n;
  // Per-category caps use the SAME largest-remainder allocation as the auto-populated
  // defaults, so the Cap column always sums exactly to the 90% all-in cap (+ profit = GDV).
  const capDefaults = has ? computeArvUnderwriting(arv, fee) : {};
  // Preliminary (best-known) amounts. Land uses the real ask (list/asking) so any overage
  // above the 15% budget is surfaced immediately; other categories default to their cap.
  const landAsk = Number(record.list_price) || Number(record.asking_price) || Number(record.max_land_purchase_price) || (has ? Number(capDefaults.max_land_purchase_price) || 0 : 0);
  const prelim = {
    max_land_purchase_price: landAsk,
    construction_budget: (Number(record.construction_budget) * n) || (Number(capDefaults.construction_budget) || 0),
    soft_costs: (Number(record.soft_costs) * n) || (Number(capDefaults.soft_costs) || 0),
    construction_contingency: (Number(record.construction_contingency) * n) || (Number(capDefaults.construction_contingency) || 0),
    financing_carry: (Number(record.financing_carry) * n) || (Number(capDefaults.financing_carry) || 0),
    selling_costs: (Number(record.selling_costs) * n) || (Number(capDefaults.selling_costs) || 0),
  };
  const prelimAllIn = Object.values(prelim).reduce((a, b) => a + b, 0);
  const prelimProfit = arv - prelimAllIn;
  // All-in cost cap = sum of every non-profit cap (the 90% all-in budget).
  const capAllIn = has ? (arv - (Number(capDefaults.est_net_profit) || 0)) : 0;
  const overCats = has ? UW_CATEGORIES.filter((c) => !c.profit && prelim[c.key] > (Number(capDefaults[c.key]) || 0)) : [];
  const profitBreached = has && prelimProfit < (Number(capDefaults.est_net_profit) || Math.round(arv * 0.1));
  const outsideUw = overCats.length > 0 || profitBreached;
  // Comps drive the GDV. If a comparable was edited, estimated_new_construction_arv changes
  // but arv_market_value (which powers these hard caps) can go stale — offer a recalc.
  const compArv = Math.round(Number(record.estimated_new_construction_arv) || 0);
  const staleArv = !isSub && !!onSave && compArv > 0 && compArv !== Math.round(arv);
  // Split Hard Construction into the Available Hard Construction Cap + a separate
  // Builder / GC Fee row; the two together equal the unchanged 50% construction cap.
  // The Hard Construction PRELIMINARY is editable (its variance vs the available cap is
  // captured here); the Builder / GC Fee is a fixed set-aside (read-only, variance $0).
  const capHardTotal = has ? (Number(capDefaults.construction_budget) || 0) : 0;
  const availHardCap = Math.max(capHardTotal - fee, 0);
  const prelimHard = Math.max(prelim.construction_budget - fee, 0);
  const pctText = (amt) => (has && arv > 0 ? `${(amt / arv * 100).toFixed(1)}%` : "—");
  const displayRows = [];
  UW_CATEGORIES.forEach((c) => {
    if (c.key === "construction_budget") {
      displayRows.push({ key: "construction_budget", label: "Hard Construction", type: "MAX", pctText: has ? pctText(availHardCap) : "50%", cap: availHardCap, preliminary: prelimHard, editable: !isSub, saveKey: "hard_construction", profit: false });
      displayRows.push({ key: "builder_gc_fee", label: "Builder / GC Fee", type: "MAX", pctText: has ? pctText(fee) : "—", cap: fee, preliminary: fee, editable: !isSub, saveKey: "builder_gc_fee", profit: false, hint: "Defaults to 12% of the hard construction cost and sits inside the 50% construction cap. Changing this fee adjusts the Available Hard Construction Cap — so the Hard Construction variance moves to reflect any increase or decrease." });
    } else {
      displayRows.push({ key: c.key, label: c.label, type: c.type, pctText: `${Math.round(c.pct * 100)}%`, cap: has ? Number(capDefaults[c.key]) || 0 : 0, preliminary: c.profit ? prelimProfit : prelim[c.key], editable: !isSub && !c.profit, profit: c.profit, saveKey: c.key === "max_land_purchase_price" ? "list_price" : c.key });
    }
  });
  return (
    <CollapseCard
      testid="underwriting-model-card"
      defaultOpen={defaultOpen}
      title="Underwriting Model"
      right={
        <span className="flex items-center gap-2">
          {staleArv && (
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300" onClick={(e) => { e.stopPropagation(); onSave("arv_market_value", compArv); }} data-testid="uw-recalc-btn">
              <RotateCcw className="h-3.5 w-3.5 mr-1" />Recalculate · comps GDV {formatCurrency(compArv)}
            </Button>
          )}
          {has && (
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${outsideUw ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary"}`} data-testid="uw-status-badge">
              {outsideUw ? "Outside underwriting" : "Within underwriting"}
            </span>
          )}
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Hard caps · profit protected</span>
        </span>
      }
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="px-6 py-2.5 font-medium">Category</th>
            <th className="px-6 py-2.5 font-medium text-center">Cap</th>
            <th className="px-6 py-2.5 font-medium text-right">% of GDV</th>
            <th className="px-6 py-2.5 font-medium text-right">Cap Amount</th>
            <th className="px-6 py-2.5 font-medium text-right">Preliminary</th>
            <th className="px-6 py-2.5 font-medium text-right">Variance</th>
          </tr>
        </thead>
        <tbody>
          {displayRows.map((r) => {
            const cap = r.cap;
            const preliminary = r.preliminary;
            const variance = preliminary - cap; // MAX: +over cap (bad). MIN(profit): +above floor (good)
            const breached = has && (r.profit ? prelimProfit < cap : preliminary > cap);
            // Costs (MAX): over cap is bad -> +red / -green. Profit (MIN): above floor is
            // good, so the colours flip -> +green / -red.
            const positiveIsBad = !r.profit;
            const varCls = !has || variance === 0
              ? "text-muted-foreground"
              : (variance > 0) === positiveIsBad ? "text-destructive" : "text-primary";
            const totalVariance = prelimAllIn - capAllIn;
            const totalVarCls = !has || totalVariance === 0
              ? "text-muted-foreground"
              : totalVariance > 0 ? "text-destructive" : "text-primary";
            return (
              <Fragment key={r.key}>
              {r.profit && (
                <tr className="border-b border-border/50 border-t-2 border-t-border bg-secondary/10 font-semibold" data-testid="uw-row-total-cost">
                  <td className="px-6 py-3 text-foreground">Total Development Cost</td>
                  <td className="px-6 py-3" />
                  <td className="px-6 py-3 text-right text-muted-foreground">{has ? pctText(capAllIn) : "—"}</td>
                  <td className="px-6 py-3 text-right" data-testid="uw-total-cost-cap">{money(capAllIn)}</td>
                  <td className="px-6 py-3 text-right" data-testid="uw-total-cost-projected">{money(prelimAllIn)}</td>
                  <td className={`px-6 py-3 text-right ${totalVarCls}`} data-testid="uw-total-cost-variance">{has ? signed(totalVariance) : "—"}</td>
                </tr>
              )}
              <tr className={`border-b border-border/50 ${r.profit ? "bg-secondary/20" : ""}`} data-testid={`uw-row-${r.key}`}>
                <td className={`px-6 py-3 ${r.profit ? "font-semibold" : "text-foreground"}`}>{r.label}</td>
                <td className="px-6 py-3 text-center">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${r.type === "MIN" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>{r.type}</span>
                </td>
                <td className="px-6 py-3 text-right text-muted-foreground">{r.pctText}</td>
                <td className="px-6 py-3 text-right font-medium">{money(cap)}</td>
                {(!has || r.profit || !onSave || !r.editable) ? (
                  <td className={`px-6 py-3 text-right font-semibold ${breached ? "text-amber-400" : r.profit ? "text-primary" : "text-foreground"}`} data-testid={`uw-projected-${r.key}`}>{money(preliminary)}</td>
                ) : (
                  <PrelimCell amount={preliminary} breached={breached} testid={`uw-projected-${r.key}`} onSave={(v) => onSave(r.saveKey, v)} hint={r.hint} />
                )}
                <td className={`px-6 py-3 text-right font-semibold ${varCls}`} data-testid={`uw-variance-${r.key}`}>{has ? signed(variance) : "—"}</td>
              </tr>
              </Fragment>
            );
          })}
          <tr className="bg-secondary/30 font-semibold">
            <td className="px-6 py-3">Total GDV</td>
            <td className="px-6 py-3" />
            <td className="px-6 py-3 text-right">100%</td>
            <td className="px-6 py-3 text-right" data-testid="uw-total-arv">{money(arv)}</td>
            <td className="px-6 py-3 text-right" data-testid="uw-total-projected">{money(prelimAllIn + prelimProfit)}</td>
            <td className="px-6 py-3" />
          </tr>
        </tbody>
      </table>
      {has && outsideUw && (
        <div className="px-6 py-3 text-[11px] text-destructive border-t border-border/50" data-testid="uw-outside-note">
          {overCats.length > 0 && <>Over hard cap: {overCats.map((c) => c.label).join(", ")}. </>}
          {profitBreached && <>Projected profit is below the 10% minimum. </>}
          Negotiate the land price or costs down to bring this deal within Arqitek's underwriting.
        </div>
      )}
    </CollapseCard>
  );
}

function ActualConstructionCost({ record }) {
  const quoteTotal = Object.values(QUOTE_KEY_MAP).reduce(
    (sum, qk) => (record[qk] != null && record[qk] !== "" && !isNaN(Number(record[qk])) ? sum + Number(record[qk]) : sum),
    0
  );
  const totalConstruction = Number(record.construction_budget) || 0;
  const fee = totalConstruction > 0 ? (Number(record.builder_gc_fee) || defaultBuilderFee(Number(record.arv_market_value) || 0)) : 0;
  const budget = Math.max(totalConstruction - fee, 0); // Available Hard Construction Cap (excl. Builder/GC fee)
  const variance = budget - quoteTotal;
  const hasQuotes = quoteTotal > 0;
  return (
    <CollapseCard
      testid="actual-construction-cost-card"
      title="Actual Construction Cost"
      right={<span className="text-[11px] uppercase tracking-wide text-muted-foreground">Sum of quotes received</span>}
    >
      <table className="w-full text-sm">
        <tbody>
          <tr className="border-b border-border/50">
            <td className="px-6 py-3 text-foreground">Total Actual (Quotes)</td>
            <td className="px-6 py-3 text-right font-semibold" data-testid="actual-total">{hasQuotes ? formatCurrency(quoteTotal) : "—"}</td>
          </tr>
          <tr className="border-b border-border/50">
            <td className="px-6 py-3 text-muted-foreground">Available Hard Construction Cap</td>
            <td className="px-6 py-3 text-right font-medium" data-testid="actual-budget">{budget ? formatCurrency(budget) : "—"}</td>
          </tr>
          <tr className="border-b border-border/50">
            <td className="px-6 py-3 text-muted-foreground">Builder / GC Fee</td>
            <td className="px-6 py-3 text-right font-medium" data-testid="actual-builder-fee">{fee ? formatCurrency(fee) : "—"}</td>
          </tr>
          <tr className="bg-secondary/20">
            <td className="px-6 py-3 font-semibold">Variance vs Available Cap</td>
            <td className={`px-6 py-3 text-right font-semibold ${variance < 0 ? "text-destructive" : "text-primary"}`} data-testid="actual-variance">
              {hasQuotes && budget ? formatCurrency(variance) : "—"}
            </td>
          </tr>
        </tbody>
      </table>
    </CollapseCard>
  );
}

const RATING_HEX = { green: "#22c55e", yellow: "#eab308", red: "#ef4444" };

function RatingDot({ color, score, size = "sm" }) {
  const dim = size === "lg" ? "h-3 w-3" : "h-2.5 w-2.5";
  const txt = size === "lg" ? "text-base font-semibold" : "text-sm font-medium";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`${dim} rounded-full shrink-0`} style={{ backgroundColor: RATING_HEX[color] || RATING_HEX.yellow, boxShadow: `0 0 8px ${RATING_HEX[color] || RATING_HEX.yellow}66` }} />
      <span className={`${txt} text-foreground`}>{score}/10</span>
    </span>
  );
}

function ArqitekDecisionCard({ record }) {
  const d = record.screening_decision;
  const [open, setOpen] = useState(false);
  if (!d || !Array.isArray(d.categories)) return null;
  return (
    <div className="rounded-lg border border-border bg-card mb-5" data-testid="arqitek-decision-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-5 py-4 text-left"
        data-testid="arqitek-decision-toggle"
      >
        <h3 className="text-sm font-semibold text-foreground">Arqitek Decision</h3>
        {d.overall_score != null && (
          <span className="ml-2"><RatingDot color={d.overall_color} score={d.overall_score} /></span>
        )}
        <span className="text-[11px] text-muted-foreground ml-auto mr-2">Angela's screening</span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
      <div className="px-5 pb-5">
      <div className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-0 text-sm border-b border-border pb-1 mb-1">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Category</div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground text-right">Rating</div>
      </div>
      <div className="divide-y divide-border/60">
        {d.categories.map((c) => (
          <div key={c.name} className="grid grid-cols-[1fr_auto] items-center gap-x-6 py-2.5" data-testid={`decision-row-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="min-w-0">
              <div className="font-medium text-foreground">{c.name}</div>
              {c.note && <div className="text-xs text-muted-foreground truncate">{c.note}</div>}
            </div>
            <RatingDot color={c.color} score={c.score} />
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-foreground">Overall:</span>
          <RatingDot color={d.overall_color} score={d.overall_score} size="lg" />
          {d.headline && <span className="text-sm font-semibold text-foreground">— {d.headline}</span>}
        </div>
        {d.thesis && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{d.thesis}</p>}
        {d.recommendation && (
          <div className="mt-3">
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${d.recommendation === "Qualified" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`} data-testid="decision-recommendation">
              {d.recommendation === "Qualified" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
              Angela advanced this to {d.recommendation}
            </span>
          </div>
        )}
      </div>
      </div>
      )}
    </div>
  );
}


function PropertySnapshot({ record }) {
  const fmtLot = () => {
    const sqft = record.lot_size_sqft;
    const acres = record.lot_size_acres;
    const parts = [];
    if (sqft != null && sqft !== "") parts.push(`${Number(sqft).toLocaleString()} sq ft`);
    if (acres != null && acres !== "") parts.push(`${Number(acres).toLocaleString()} ac`);
    return parts.length ? parts.join(" · ") : "—";
  };
  const money = (v) => (v != null && v !== "" ? formatCurrency(v) : "—");
  const items = [
    { key: "asking-price", icon: DollarSign, label: "Asking Price", value: money(record.asking_price) },
    { key: "arv", icon: TrendingUp, label: "New Construction GDV", value: money(record.estimated_new_construction_arv) },
    { key: "property-type", icon: Home, label: "Property Type", value: record.property_type || "—" },
    { key: "lot-size", icon: Ruler, label: "Lot Size", value: fmtLot() },
    { key: "zoning", icon: MapPin, label: "Zoning", value: record.zoning || "—" },
  ];
  if (record.rating_score != null) {
    items.push({ key: "rating", icon: Gauge, label: "Rating", render: <RatingDot color={record.rating_color} score={record.rating_score} /> });
  }
  return (
    <div className="rounded-lg border border-border bg-secondary/30 px-5 py-3.5 mb-6" data-testid="property-snapshot">
      <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        <Radar className="h-3.5 w-3.5" />Property Snapshot
      </div>
      <div className="flex flex-wrap justify-between gap-x-4 gap-y-3">
        {items.map(({ key, icon: Icon, label, value, render }) => (
          <div key={key} className="min-w-0 max-w-[220px]" data-testid={`snapshot-${key}`}>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 whitespace-nowrap">
              <Icon className="h-3.5 w-3.5 shrink-0" />{label}
            </div>
            {render ? (
              <div data-testid={`snapshot-${key}-value`}>{render}</div>
            ) : (
              <div className="text-sm font-medium text-foreground truncate" data-testid={`snapshot-${key}-value`}>{value}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Top-of-funnel Lead snapshot: the record's title + the key points that actually
// matter for a land lead (location, price, lot, zoning, score, motivation, source,
// owner, rating) — replaces the generic CRM Quick Links for inbound leads.
function InboundLeadSnapshot({ record }) {
  const money = (v) => (v != null && v !== "" ? formatCurrency(v) : "—");
  const loc = [record.city, record.state].filter(Boolean).join(", ") || record.county || "—";
  const lot = (() => {
    const parts = [];
    if (record.lot_size_sqft != null && record.lot_size_sqft !== "") parts.push(`${Number(record.lot_size_sqft).toLocaleString()} sq ft`);
    if (record.lot_size_acres != null && record.lot_size_acres !== "") parts.push(`${Number(record.lot_size_acres).toLocaleString()} ac`);
    return parts.length ? parts.join(" · ") : "—";
  })();
  const items = [
    { key: "location", icon: MapPin, label: "Location", value: loc },
    { key: "asking-price", icon: DollarSign, label: "Asking Price", value: money(record.asking_price) },
    { key: "lot-size", icon: Ruler, label: "Lot Size", value: lot },
    { key: "zoning", icon: Compass, label: "Zoning", value: record.zoning || record.general_plan || "—" },
    { key: "source", icon: Megaphone, label: "Source", value: record.lead_source || record.source_platform || record.source_type || record.property_source || "—" },
  ];
  if (record.rating_score != null) {
    items.push({ key: "rating", icon: Gauge, label: "Rating", render: <RatingDot color={record.rating_color} score={record.rating_score} /> });
  }
  return (
    <div className="rounded-lg border border-border bg-secondary/30 px-5 py-3.5 mb-6" data-testid="lead-snapshot">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-3">
        <Radar className="h-3.5 w-3.5" />Lead Snapshot
      </div>
      <div className="flex flex-wrap justify-between gap-x-4 gap-y-3">
        {items.map(({ key, icon: Icon, label, value, render, onClick }) => (
          <div key={key} className="min-w-0 max-w-[220px]" data-testid={`lead-snapshot-${key}`}>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 whitespace-nowrap">
              <Icon className="h-3.5 w-3.5 shrink-0" />{label}
            </div>
            {render ? (
              <div data-testid={`lead-snapshot-${key}-value`}>{render}</div>
            ) : onClick ? (
              <button onClick={onClick} className="text-sm font-medium text-primary hover:underline truncate max-w-full text-left" data-testid={`lead-snapshot-${key}-value`}>{value}</button>
            ) : (
              <div className="text-sm font-medium text-foreground truncate" data-testid={`lead-snapshot-${key}-value`}>{value}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


const subNum = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };

// Roll up a subdivision (multiple lots + shared site costs) into combined economics.
// Land / horizontal site costs are paid ONCE for the whole deal: they sit on the first
// lot, so additional split lots carry no land cost. Combined totals are unaffected by
// how the site cost is attributed; only the per-lot breakdown changes.
function computeSubdivision(sub) {
  const lots = (sub && sub.lots) || [];
  const site = (sub && sub.site_costs) || [];
  const totalArv = lots.reduce((s, l) => s + subNum(l.arv), 0);
  const totalDirect = lots.reduce((s, l) => s + subNum(l.cost), 0);
  const totalSite = site.reduce((s, x) => s + subNum(x.amount), 0);
  const allIn = totalDirect + totalSite;
  const profit = totalArv - allIn;
  const margin = totalArv > 0 ? (profit / totalArv) * 100 : null;
  const perLot = lots.map((l, idx) => {
    const arv = subNum(l.arv);
    const allocSite = idx === 0 ? totalSite : 0; // land bought once — carried by the first lot only
    const lotAllIn = subNum(l.cost) + allocSite;
    const lotProfit = arv - lotAllIn;
    const lotMargin = arv > 0 ? (lotProfit / arv) * 100 : null;
    return { ...l, allocSite, lotAllIn, lotProfit, lotMargin };
  });
  return { perLot, totalArv, totalDirect, totalSite, allIn, profit, margin, count: lots.length };
}

// Real deal-level land acquisition for the WHOLE assembled deal — mirrors the
// Underwriting Model's landAsk: the actual ask (list / asking / stored), else 15%
// of the TOTAL (blended) GDV. Must NOT use the single-lot GDV, or land gets booked
// at ~1/N of its true value and the header margin is badly overstated.
function dealLandAcquisition(record, count) {
  const dealArv = (Number(record.arv_market_value) || 0) * Math.max(1, Number(count) || 1);
  return Math.round(
    Number(record.list_price) || Number(record.asking_price) || Number(record.max_land_purchase_price) || Math.round(dealArv * 0.15) || 0
  );
}

// Seed a subdivision straight from the property's own economics: cut the land into N
// equal lots, copy the property GDV + build cost onto every lot (each lot is a home),
// and add the land purchase ONCE as a shared cost that gets split across the lots.
// Result: land cost per lot drops by 1/N while GDV & build per lot stay the same.
function buildSubdivisionFromProperty(record, sub, n) {
  const count = Math.max(1, Math.min(20, parseInt(n) || 0));
  const uw = computeUnderwritingStatus(record);
  const totalSqft = Number(record.lot_size_sqft) || (Number(record.lot_size_acres) ? Math.round(Number(record.lot_size_acres) * 43560) : 0);
  const perLotSqft = totalSqft ? Math.round(totalSqft / count) : "";
  const arv = Number(record.arv_market_value) || 0;
  // Each additional lot is a full second house: repeat ALL non-land costs per lot
  // (hard construction + soft + contingency + financing/carry + selling). Only land is shared.
  const hard = Number(record.construction_budget) || uw.cost.construction_budget || 0;
  const build = hard
    + (Number(record.soft_costs) || 0)
    + (Number(record.construction_contingency) || 0)
    + (Number(record.financing_carry) || 0)
    + (Number(record.selling_costs) || 0);
  const home = Number(record.living_sqft) || "";
  const land = dealLandAcquisition(record, count);
  const lots = Array.from({ length: count }, (_, i) => ({
    id: `${Date.now()}-${i}`,
    name: `Lot ${i + 1}`,
    mode: "build",
    lot_size: perLotSqft || "",
    home_size: home || "",
    arv: arv || "",
    cost: build || "",
  }));
  // Replace any prior auto Land Acquisition line; keep other shared costs the user added.
  const otherSite = ((sub && sub.site_costs) || []).filter((x) => !/land acquisition/i.test(x.label || ""));
  const site_costs = land > 0 ? [{ label: "Land Acquisition", amount: land }, ...otherSite] : otherSite;
  return { ...(sub || {}), enabled: true, lots, site_costs, split_from_property: true };
}

const isSubdivisionActive = () => false;

// Development / property type on a Property Analysis. Duplex/Triplex/Fourplex + SFR have a
// fixed unit count; the others let the user enter the number of units. The development is a
// single building/site whose total living area, GDV and all-in cost are split across N units.
const DEVELOPMENT_TYPES = [
  { value: "Single-Family Residence", units: 1, lock: true },
  { value: "Multiple Detached SFRs", units: 2 },
  { value: "Attached SFR / Small-Lot Development", units: 2 },
  { value: "Duplex", units: 2, lock: true },
  { value: "Triplex", units: 3, lock: true },
  { value: "Fourplex", units: 4, lock: true },
  { value: "Townhomes", units: 3 },
  { value: "Multifamily", units: 6 },
  { value: "Mixed Unit Development", units: 4 },
];
const devTypeMeta = (v) => DEVELOPMENT_TYPES.find((t) => t.value === v) || DEVELOPMENT_TYPES[0];
const devTypeUnits = (record) => {
  const meta = devTypeMeta(record.development_type || "Single-Family Residence");
  return meta.lock ? meta.units : (Number(record.unit_count) || meta.units || 1);
};

// Roll up a multi-unit development. By default the property's single-building
// underwriting IS the total and per-unit figures are that total ÷ units. For
// detached types (perUnit=true) each home carries its own underwriting, so the
// underwritten GDV/cost/profit are PER HOME and the totals are × units.
function computePropertyType(record, units, perUnit = false) {
  const uw = computeUnderwritingStatus(record);
  const u = Math.max(1, Number(units) || 1);
  const totalLiving = Number(cleanNum(record.living_sqft)) || 0;
  const perUnitSqft = totalLiving > 0 ? Math.round(totalLiving / u) : null;
  if (perUnit) {
    // Detached homes on a split parcel: LAND is a one-time whole-parcel cost
    // (not multiplied); only the per-home build costs scale with unit count.
    const land = uw.cost.max_land_purchase_price || 0;
    const perHomeBuild = Math.max(uw.allIn - land, 0);
    const arv = uw.arv * u;
    const allIn = land + perHomeBuild * u;
    const profit = arv - allIn;
    const margin = arv > 0 ? (profit / arv) * 100 : null;
    return {
      arv, allIn, profit, margin, units: u, totalLiving, perUnitSqft,
      land, perHomeBuild,
      arvPerUnit: uw.arv > 0 ? Math.round(uw.arv) : null,
      costPerUnit: allIn > 0 ? Math.round(allIn / u) : null,
    };
  }
  const arv = uw.arv;
  const allIn = uw.allIn;
  const profit = uw.projectedProfit;
  const margin = arv > 0 ? (profit / arv) * 100 : null;
  return {
    arv, allIn, profit, margin, units: u, totalLiving, perUnitSqft,
    arvPerUnit: arv > 0 ? Math.round(arv / u) : null,
    costPerUnit: allIn > 0 ? Math.round(allIn / u) : null,
  };
}

// --- Attached / multi-unit auto-calc -----------------------------------------
// Applies only to attached/stacked types; Single-Family & Multiple Detached SFRs
// keep the simple single-home logic above.
const ATTACHED_TYPES = ["Attached SFR / Small-Lot Development", "Duplex", "Triplex",
  "Fourplex", "Townhomes", "Multifamily", "Mixed Unit Development"];
const isAttachedType = (t) => ATTACHED_TYPES.includes(t);
const DEFAULT_EFFICIENCY = 85;            // % of gross building that is net sellable/rentable
const DEFAULT_NEW_CONSTRUCTION_PSF = 350; // $/SF used for GDV when there are no comps

function parsePositiveNumbers(str) {
  const matches = String(str || "").match(/[\d][\d,]*\.?\d*/g) || [];
  return matches.map((m) => parseFloat(m.replace(/,/g, ""))).filter((n) => !isNaN(n) && n > 0);
}
// Estimated buildable area (SF) from Angela's free-text read; averages a range and
// ignores small stray numbers (setback feet, height, etc.).
function parseBuildableArea(record) {
  const nums = parsePositiveNumbers(record.est_buildable_area).filter((n) => n >= 500);
  if (!nums.length) return null;
  return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
}
// FAR parsed from the combined building_controls string (or a dedicated far field).
function parseFar(record) {
  const s = String(record.far || record.building_controls || "");
  const m = s.match(/far[^0-9]*([0-9]*\.?[0-9]+)/i);
  if (m) { const v = parseFloat(m[1]); if (v > 0 && v < 20) return v; }
  return null;
}
// Total gross building size with source: Angela buildable → lot × FAR → manual living area.
function computeBuildingSize(record) {
  const b = parseBuildableArea(record);
  if (b) return { sf: b, source: "Angela buildable area" };
  const far = parseFar(record);
  const lot = Number(cleanNum(record.lot_size_sqft)) || 0;
  if (far && lot) return { sf: Math.round(lot * far), source: `Lot × FAR (${far})` };
  const living = Number(cleanNum(record.living_sqft)) || 0;
  if (living) return { sf: living, source: "Manual living area" };
  return { sf: 0, source: null };
}

function computeAttachedType(record) {
  const units = devTypeUnits(record);
  const eff = Math.min(Math.max(Number(record.unit_efficiency_pct) || DEFAULT_EFFICIENCY, 50), 100) / 100;
  const psfDefault = Number(cleanNum(record.new_construction_psf)) || DEFAULT_NEW_CONSTRUCTION_PSF;
  const { sf: gross, source: buildingSource } = computeBuildingSize(record);
  const net = Math.round(gross * eff);
  const perUnitGross = units > 0 && gross ? Math.round(gross / units) : null;
  const perUnitNet = units > 0 && net ? Math.round(net / units) : null;

  const storedArv = Number(record.arv_market_value) || 0;
  const compPsf = Number(cleanNum(record.avg_nearby_price_sqft)) || 0;
  let effArv = 0, arvSource = null, psfUsed = null;
  if (storedArv > 0) { effArv = storedArv; arvSource = "Entered / comp GDV"; }
  else if (compPsf > 0 && net > 0) { effArv = Math.round(net * compPsf); psfUsed = compPsf; arvSource = `Comp avg $${compPsf}/SF`; }
  else if (net > 0) { effArv = Math.round(net * psfDefault); psfUsed = psfDefault; arvSource = `Est. $${psfDefault}/SF (no comps)`; }

  const uw = computeUnderwritingStatus({ ...record, arv_market_value: effArv });
  const hasStoredCosts = (Number(record.construction_budget) || Number(record.soft_costs)
    || Number(record.max_land_purchase_price) || Number(record.selling_costs)) > 0;
  const allIn = hasStoredCosts && uw.allIn > 0 ? uw.allIn : Math.round(effArv * UW_ALL_IN_CAP_PCT);
  const profit = effArv - allIn;
  const margin = effArv > 0 ? (profit / effArv) * 100 : null;

  return { units, effPct: Math.round(eff * 100), gross, net, buildingSource, perUnitGross,
    perUnitNet, effArv, arvSource, psfUsed, allIn, profit, margin,
    arvPerUnit: units > 0 && effArv ? Math.round(effArv / units) : null,
    costPerUnit: units > 0 && allIn ? Math.round(allIn / units) : null };
}

function PropertyTypeModule({ record, resource, id, onSaved }) {
  const devType = record.development_type || "Single-Family Residence";
  const meta = devTypeMeta(devType);
  const units = devTypeUnits(record);
  const attached = isAttachedType(devType);
  const [collapsed, setCollapsed] = useState(true);
  const [busy, setBusy] = useState(false);
  const [countInput, setCountInput] = useState(String(units));
  useEffect(() => { setCountInput(String(devTypeUnits(record))); }, [record.unit_count, record.development_type]);
  // "Multiple Detached SFRs": user enters the size of EACH home; total living
  // area = per-home size × units (rather than splitting one shared total).
  const perUnitEntry = devType === "Multiple Detached SFRs";
  const derivePerHome = (r) => {
    const stored = Number(cleanNum(r.unit_living_sqft)) || 0;
    if (stored) return stored;
    const tot = Number(cleanNum(r.living_sqft)) || 0;
    const u = Math.max(1, devTypeUnits(r));
    return tot ? Math.round(tot / u) : 0;
  };
  const [homeInput, setHomeInput] = useState(String(derivePerHome(record) || ""));
  useEffect(() => { setHomeInput(String(derivePerHome(record) || "")); }, [record.unit_living_sqft, record.living_sqft, record.unit_count, record.development_type]);

  const money = (v) => formatCurrency(Math.round(v || 0));
  const syncUnits = async (raw) => {
    let n = parseInt(String(raw).replace(/[^0-9]/g, ""), 10);
    if (!n || n < 1) n = 1;
    n = Math.min(n, 200);
    const patch = { unit_count: n };
    // For per-home types, keep each home's size fixed and rescale the total.
    if (perUnitEntry) {
      const ph = derivePerHome(record);
      if (ph) { patch.unit_living_sqft = ph; patch.living_sqft = ph * n; }
    }
    setBusy(true);
    try { const updated = await updateItem(resource, id, patch); onSaved(updated); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  // Per-home size entry: store the per-home SF and derive the total (size × units).
  const savePerHome = async (raw) => {
    let n = parseInt(String(raw).replace(/[^0-9]/g, ""), 10);
    if (!n || n < 0) n = 0;
    setBusy(true);
    try { const updated = await updateItem(resource, id, { unit_living_sqft: n, living_sqft: n * units }); onSaved(updated); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const marginColor = (m) => (m ?? -1) < 0 ? "text-destructive" : (m ?? 0) >= 10 ? "text-primary" : (m ?? 0) >= 5 ? "text-amber-400" : "text-destructive";

  const unitsTile = (
    meta.lock
      ? <div className="text-sm font-semibold text-foreground" data-testid="property-type-units-value">{units}</div>
      : <Input type="number" min="1" value={countInput} disabled={busy}
          onChange={(e) => setCountInput(e.target.value)}
          onBlur={(e) => syncUnits(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          className="h-7 -ml-1 px-1 text-sm font-semibold border-0 bg-transparent focus-visible:ring-1 focus-visible:ring-primary" data-testid="property-type-unit-count" title="Edit the number of units" />
  );
  const Tile = ({ label, children, cls }) => (
    <div className="rounded-md border border-border/60 bg-secondary/20 px-3 py-2 h-full flex flex-col">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground min-h-[28px]">{label}</div>
      <div className={`text-sm font-semibold mt-auto ${cls || "text-foreground"}`}>{children}</div>
    </div>
  );

  let tiles, detail, footnote;
  if (attached) {
    const a = computeAttachedType(record);
    tiles = (
      <>
        <div className="rounded-md border border-border/60 bg-secondary/20 px-3 py-2 h-full flex flex-col">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground min-h-[28px]">Units</div>{unitsTile}
        </div>
        <Tile label="Building (Gross)">{a.gross ? `${a.gross.toLocaleString()} SF` : "—"}</Tile>
        <Tile label={`Net Sellable (${a.effPct}%)`}>{a.net ? `${a.net.toLocaleString()} SF` : "—"}</Tile>
        <Tile label="Unit Size (Net)">{a.perUnitNet ? `${a.perUnitNet.toLocaleString()} SF` : "—"}</Tile>
        <Tile label="Total GDV">{money(a.effArv)}</Tile>
        <Tile label="Margin" cls={marginColor(a.margin)}>{a.margin == null ? "—" : `${a.margin.toFixed(1)}%`}</Tile>
      </>
    );
    detail = (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm" data-testid="property-type-perunit">
        <KVRow label="All-In Cost" value={money(a.allIn)} />
        <KVRow label="Net Profit" value={<span className={a.profit >= 0 ? "text-primary" : "text-destructive"}>{money(a.profit)}</span>} />
        <KVRow label="Unit Size (Gross)" value={a.perUnitGross ? `${a.perUnitGross.toLocaleString()} SF` : "—"} />
        <KVRow label="GDV / Unit" value={a.arvPerUnit ? money(a.arvPerUnit) : "—"} />
        <KVRow label="Cost / Unit" value={a.costPerUnit ? money(a.costPerUnit) : "—"} />
        <KVRow label="Efficiency" value={`${a.effPct}%`} />
      </div>
    );
    footnote = (
      <div className="text-[11px] text-muted-foreground border-t border-border/50 pt-2 space-y-0.5" data-testid="property-type-basis">
        <div>Building size: <span className="text-foreground/80">{a.buildingSource || "not enough data"}</span></div>
        <div>GDV basis: <span className="text-foreground/80">{a.arvSource || "needs GDV or $/SF"}</span>{a.psfUsed ? ` · net sellable × $${a.psfUsed}/SF` : ""}</div>
      </div>
    );
  } else {
    const roll = computePropertyType(record, units, perUnitEntry);
    const unitsTileWrap = (
      <div className="rounded-md border border-border/60 bg-secondary/20 px-3 py-2 h-full flex flex-col">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground min-h-[28px]">Units</div>{unitsTile}
      </div>
    );
    const unitSizeTileWrap = perUnitEntry ? (
      <div className="rounded-md border border-border/60 bg-secondary/20 px-3 py-2 h-full flex flex-col">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground min-h-[28px]">Unit Size (per home)</div>
        <Input type="number" min="0" value={homeInput} disabled={busy}
          onChange={(e) => setHomeInput(e.target.value)}
          onBlur={(e) => savePerHome(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          className="h-7 -ml-1 px-1 text-sm font-semibold border-0 bg-transparent focus-visible:ring-1 focus-visible:ring-primary" data-testid="property-type-unit-size-input" title="Living area of each detached home (SF)" />
      </div>
    ) : (
      <Tile label="Unit Size">{roll.perUnitSqft ? `${roll.perUnitSqft.toLocaleString()} SF` : "—"}</Tile>
    );
    if (perUnitEntry) {
      // Detached homes: show only the per-unit story. All-in / net profit / margin
      // live in the underwriting snapshot, so they're intentionally omitted here.
      tiles = (
        <>
          {unitsTileWrap}
          {unitSizeTileWrap}
          <Tile label="Total GDV">{money(roll.arv)}</Tile>
          <Tile label="GDV / Per Unit">{roll.arvPerUnit ? money(roll.arvPerUnit) : "—"}</Tile>
          <Tile label="Cost / Per Unit">{roll.costPerUnit ? money(roll.costPerUnit) : "—"}</Tile>
        </>
      );
      detail = null;
      footnote = null;
    } else {
      tiles = (
        <>
          {unitsTileWrap}
          {unitSizeTileWrap}
          <Tile label="Total GDV">{money(roll.arv)}</Tile>
          <Tile label="All-In Cost">{money(roll.allIn)}</Tile>
          <Tile label="Net Profit" cls={roll.profit >= 0 ? "text-primary" : "text-destructive"}>{money(roll.profit)}</Tile>
          <Tile label="Margin" cls={marginColor(roll.margin)}>{roll.margin == null ? "—" : `${roll.margin.toFixed(1)}%`}</Tile>
        </>
      );
      detail = (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm" data-testid="property-type-perunit">
          <KVRow label="GDV / Unit" value={roll.arvPerUnit ? money(roll.arvPerUnit) : "—"} />
          <KVRow label="Cost / Unit" value={roll.costPerUnit ? money(roll.costPerUnit) : "—"} />
          <KVRow label="Total Living Area" value={roll.totalLiving ? `${roll.totalLiving.toLocaleString()} SF` : "—"} />
        </div>
      );
      footnote = null;
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="property-type-module">
      <button type="button" onClick={() => setCollapsed((c) => !c)} className="w-full flex items-center justify-between gap-3 px-5 py-4 border-b border-border text-left hover:bg-accent/40 transition-colors" title={collapsed ? "Expand" : "Collapse"} data-testid="property-type-collapse-btn">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Property Type</h3>
          <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-1.5 py-0.5" data-testid="property-type-badge">{devType}</span>
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${collapsed ? "" : "rotate-180"}`} />
      </button>

      {!collapsed && (
        <div className="p-5 space-y-5">
          <div className={`grid grid-cols-2 sm:grid-cols-3 ${perUnitEntry ? "lg:grid-cols-5" : "lg:grid-cols-6"} gap-3`} data-testid="property-type-summary">{tiles}</div>
          {detail}
          {footnote}
        </div>
      )}
    </div>
  );
}


// Pad-Ready Land — the 20/60/20 feasibility lens (independent of the 90/50/15 Underwriting Model).
// 20% Pad-Ready Land (lot + site work) · 60% Vertical Construction · 20% Profit.
// Effective inputs prefill from record data (GDV/Land/Construction) and can be overridden per record.
const PAD_LAND_PCT = 0.20;
const PAD_BUILD_PCT = 0.60;
const PAD_PROFIT_PCT = 0.20;

function computePadReady(record) {
  const eff = (override, base) => {
    const o = Number(record[override]);
    if (override in record && record[override] !== null && record[override] !== "" && !isNaN(o)) return o;
    const b = Number(record[base]);
    return isNaN(b) ? 0 : b;
  };
  const gdv = eff("pad_gdv_override", "arv_market_value");
  const land = eff("pad_land_override", "max_land_purchase_price");
  const siteWork = Math.max(Number(record.pad_site_work) || 0, 0);
  const construction = eff("pad_construction_override", "construction_budget");
  const padReady = land + siteWork;
  const allIn = padReady + construction;
  // Forward targets from GDV.
  const targetLand = Math.round(gdv * PAD_LAND_PCT);
  const targetBuild = Math.round(gdv * PAD_BUILD_PCT);
  const targetProfit = Math.round(gdv * PAD_PROFIT_PCT);
  const maxLandPrice = targetLand - siteWork;                 // max land after reserving site work
  // Reverse: implied min GDV so costs are 80% and profit is the 20% target.
  const impliedGdv = allIn > 0 ? Math.round(allIn / (1 - PAD_PROFIT_PCT)) : 0;
  const impliedProfit = Math.round(impliedGdv * PAD_PROFIT_PCT);
  // Feasibility vs the entered GDV.
  const padReadyPct = gdv > 0 ? (padReady / gdv) * 100 : null;
  const buildPct = gdv > 0 ? (construction / gdv) * 100 : null;
  const profit = gdv - allIn;
  const profitPct = gdv > 0 ? (profit / gdv) * 100 : null;
  const feasible = gdv > 0 && profitPct != null && profitPct >= PAD_PROFIT_PCT * 100 - 0.5;
  return { gdv, land, siteWork, construction, padReady, allIn, targetLand, targetBuild, targetProfit,
    maxLandPrice, impliedGdv, impliedProfit, padReadyPct, buildPct, profit, profitPct, feasible };
}

// Module-scope tiles — stable identity so inputs don't remount (and lose focus) on every keystroke.
function PadTile({ label, children, cls, tid }) {
  return (
    <div className="rounded-md border border-border/60 bg-secondary/20 px-3 py-2 h-full flex flex-col">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground min-h-[28px]">{label}</div>
      <div className={`text-sm font-semibold mt-auto ${cls || "text-foreground"}`} data-testid={tid}>{children}</div>
    </div>
  );
}

function PadEditTile({ label, field, value, hint, onChange, onBlur }) {
  return (
    <div className="rounded-md border border-border/60 bg-secondary/20 px-3 py-2 h-full flex flex-col" title={hint}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground min-h-[28px]">{label}</div>
      <div className="flex items-center mt-auto">
        <span className="text-sm font-semibold text-muted-foreground">$</span>
        <Input type="text" inputMode="numeric" value={value}
          onChange={onChange} onBlur={onBlur}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          className="h-7 -ml-0.5 px-1 text-sm font-semibold border-0 bg-transparent focus-visible:ring-1 focus-visible:ring-primary"
          data-testid={`pad-${field}-input`} />
      </div>
    </div>
  );
}

// Land Offer — the deal decision engine. Turns land comps + development economics into a
// maximum supported land offer, a recommended opening offer, and a walk-away price.
// Guardrail: Actual > Max Supported is flagged red (don't overpay).
function landNum(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).replace(/[^0-9.-]/g, "");
  if (s === "" || s === "-" || s === ".") return null;
  const n = Number(s);
  return isNaN(n) ? null : n;
}

function computeLandOffer(record) {
  const num = landNum;
  const comps = [1, 2, 3].map((i) => {
    const price = num(record[`land_comp_${i}`]);
    const sf = num(record[`land_comp_${i}_lot`]) ?? (num(record[`land_comp_${i}_sqft`]) != null ? Math.round(num(record[`land_comp_${i}_sqft`]) * 43560) : null);
    const acres = sf != null ? sf / 43560 : (num(record[`land_comp_${i}_sqft`]) ?? null);
    return { i, address: record[`land_comp_${i}_address`] || "", price, sf, acres,
      ppa: (price && acres) ? price / acres : null, ppsf: (price && sf) ? price / sf : null,
      zoning: record[`land_comp_${i}_zoning`] || "", utilities: record[`land_comp_${i}_utilities`] || "", adj: record[`land_comp_${i}_adjustment`] || "" };
  }).filter((c) => c.price != null || c.address);
  const prices = comps.map((c) => c.price).filter((v) => v != null).sort((a, b) => a - b);
  const avgVal = prices.length ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : null;
  const medianVal = prices.length ? (prices.length % 2 ? prices[(prices.length - 1) / 2] : Math.round((prices[prices.length / 2 - 1] + prices[prices.length / 2]) / 2)) : null;
  const adjustedMarket = num(record.land_adjusted_market_value) ?? medianVal;
  const marketValue = adjustedMarket ?? medianVal ?? avgVal;

  const subActive = isSubdivisionActive(record);
  const sub = subActive ? computeSubdivision(record.subdivision) : null;
  const lots = num(record.potential_lots) ?? (sub ? sub.count : null);
  const finishedLotVal = num(record.finished_lot_value);
  const totalEntitled = (lots && finishedLotVal) ? lots * finishedLotVal : null;
  const avgHome = num(record.avg_home_price);
  const gdv = num(record.arv_market_value) ?? (sub ? sub.totalArv : null) ?? ((lots && avgHome) ? lots * avgHome : null);

  const reqPct = num(record.required_profit_pct) ?? 20;
  const flipPct = num(record.land_flip_margin_pct) ?? 30;
  const openPct = num(record.opening_offer_pct) ?? 50;
  const uw = computeUnderwritingStatus(record);
  const nonLand = Math.max(0, uw.allIn - (uw.cost.max_land_purchase_price || 0));
  const sellCarry = (uw.cost.selling_costs || 0) + (uw.cost.financing_carry || 0);
  const entCosts = num(record.entitlement_costs) ?? 0;
  const flipMax = marketValue != null ? Math.round(marketValue * (1 - flipPct / 100)) : null;
  const entMax = totalEntitled != null ? Math.round(totalEntitled - entCosts - sellCarry - totalEntitled * reqPct / 100) : null;
  const vertMax = (gdv && gdv > 0) ? Math.round(gdv - nonLand - gdv * reqPct / 100) : null;
  const strategies = [
    { key: "land-flip", label: "Land Flip", val: flipMax },
    { key: "entitlement", label: "Entitlement / Subdivision Flip", val: entMax },
    { key: "vertical", label: "Build Vertical", val: vertMax },
  ];
  const withVals = strategies.filter((s) => s.val != null);
  const best = withVals.length ? withVals.reduce((a, b) => (b.val > a.val ? b : a)) : null;
  const absoluteMax = best ? best.val : null;

  const opening = marketValue != null ? Math.round(marketValue * openPct / 100) : null;
  const targetHigh = absoluteMax != null ? Math.round(absoluteMax * 0.8) : null;
  const asking = num(record.list_price) ?? num(record.asking_price);
  const actualOffer = num(record.actual_land_offer_price);
  const maxOffer = num(record.max_land_purchase_price);
  const overMax = maxOffer != null && actualOffer != null && actualOffer > maxOffer;
  const shouldPass = absoluteMax != null && ((asking != null && asking > absoluteMax) || (actualOffer != null && actualOffer > absoluteMax));

  return { comps, avgVal, medianVal, adjustedMarket, marketValue, lots, finishedLotVal, totalEntitled,
    avgHome, gdv, reqPct, flipPct, openPct, nonLand, strategies, best, absoluteMax, opening, targetHigh,
    asking, actualOffer, maxOffer, overMax, shouldPass };
}

// GO / CAUTION / NO-GO verdict on the land offer — mirrors the Land Offer module's aligned
// max (20% GDV − site work) and opening (40% market land value). Compares the intended offer.
function computeOfferVerdict(record) {
  const num = landNum;
  const c = computeLandOffer(record);
  const siteWork = num(record.pad_site_work) ?? 0;
  const gdvForOffer = num(record.arv_market_value) ?? c.gdv;
  const primaryEcon = primaryStrategyEconomics(record);
  const maxLandOffer = primaryEcon && primaryEcon.maxLandBasis != null
    ? Math.max(0, primaryEcon.maxLandBasis)
    : (gdvForOffer ? Math.round(0.20 * gdvForOffer - siteWork) : c.absoluteMax);
  const opening = c.marketValue != null ? Math.round(0.40 * c.marketValue) : c.opening;
  const target = (opening != null && maxLandOffer != null && maxLandOffer > opening) ? Math.round((opening + maxLandOffer) / 2) : null;
  const actual = c.actualOffer ?? opening;
  if (actual == null || maxLandOffer == null) return { verdict: null, note: "Set an offer to get a verdict", maxLandOffer, opening, target, actual };
  if (actual > maxLandOffer) return { verdict: "NO-GO", note: "Offer exceeds the max supported land price — do not overpay", maxLandOffer, opening, target, actual };
  const cautionAbove = target ?? maxLandOffer;
  if (actual > cautionAbove) return { verdict: "CAUTION", note: "Approaching the max supported price — margin is getting thin", maxLandOffer, opening, target, actual };
  return { verdict: "GO", note: "Within budget — at or below your target offer", maxLandOffer, opening, target, actual };
}

// ===================== Development Strategies =====================
// Four INDEPENDENT development plans for a Property Analysis. Each keeps its own
// assumptions (record.strategies[key]) prefilled from the record, computes its own
// revenue / costs / profit / margin / Max Land Basis, and never borrows another
// strategy's numbers. The user selects ONE as record.primary_strategy — that
// strategy's economics become the source of truth for the deal (Snapshot, Offer,
// Acquisition conversion). They no longer collapse into "highest max wins".
const STRATEGY_KEYS = ["land_flip", "entitle", "subdivide", "build_sfr"];
const STRATEGY_META = {
  build_sfr: { key: "build_sfr", label: "Vertical Build", icon: Home,
    question: "If Arqitek builds one home on this parcel, what can we afford to pay for the land and still achieve the required return?" },
  subdivide: { key: "subdivide", label: "Build Ready", icon: Split,
    question: "If Arqitek subdivides this property and builds the resulting homes, what is the opportunity and what can we pay for the parent parcel?" },
  entitle: { key: "entitle", label: "Pad Ready", icon: FileSignature,
    question: "If Arqitek creates the lots but does not build the houses, how much value can entitlement create and what can we pay for the land?" },
  land_flip: { key: "land_flip", label: "Land Flip", icon: ArrowRightLeft,
    question: "Can Arqitek acquire this parcel at a sufficient discount to resell it profitably without substantial development?" },
};
// Read a per-strategy assumption override, else the prefilled default.
function stratNum(record, key, field, fallback) {
  const ov = record && record.strategies && record.strategies[key];
  if (ov && ov[field] !== undefined && ov[field] !== null && ov[field] !== "") {
    const n = Number(String(ov[field]).replace(/[^0-9.-]/g, ""));
    if (!isNaN(n)) return n;
  }
  return fallback;
}
const stratRound = (v) => (v == null || isNaN(v) ? null : Math.round(v));

// Effective Financing+Carrying and Selling cost ratios from the Underwriting Model
// (the source of truth) — uses the record's actual values (incl. user overrides)
// when present, else the cap %. Applied to each strategy's own exit GDV so the
// shared line items reconcile across the Underwriting Model and all exit strategies.
function uwCostPct(record) {
  const uw = computeUnderwritingStatus(record);
  const gdv = uw.arv || 0;
  const cap = gdv > 0 ? computeArvUnderwriting(gdv) : {};
  const fin = Number(record.financing_carry) || cap.financing_carry || 0;
  const sell = Number(record.selling_costs) || cap.selling_costs || 0;
  return {
    financingPct: gdv > 0 ? fin / gdv : 0.08,
    sellingPct: gdv > 0 ? sell / gdv : 0.07,
  };
}


function computeBuildSFR(record) {
  const uw = computeUnderwritingStatus(record);
  const cmBuild = angelaCostimate(record, "build_sfr");
  const gdv = stratNum(record, "build_sfr", "gdv", uw.arv || 0);
  const cap = gdv > 0 ? computeArvUnderwriting(gdv) : {};
  const homeSize = stratNum(record, "build_sfr", "home_size", Number(cleanNum(record.living_sqft)) || 0);
  const construction = stratNum(record, "build_sfr", "construction", Number(record.construction_budget) || cap.construction_budget || 0);
  const soft = stratNum(record, "build_sfr", "soft", Number(record.soft_costs) || cap.soft_costs || 0);
  const contingency = stratNum(record, "build_sfr", "contingency", Number(record.construction_contingency) || cap.construction_contingency || 0);
  const financing = stratNum(record, "build_sfr", "financing", Number(record.financing_carry) || cap.financing_carry || 0);
  const selling = stratNum(record, "build_sfr", "selling", Number(record.selling_costs) || cap.selling_costs || 0);
  const siteWork = stratNum(record, "build_sfr", "site_work", Number(record.pad_site_work) || cmBuild.horizontal.value);
  const profitPct = stratNum(record, "build_sfr", "profit_pct", 20);
  // Align the Vertical Build header to the embedded Underwriting Model (the source of
  // truth). Non-land cost and the net-developer-profit floor come straight from the
  // hard-cap allocation — site development lives INSIDE the 50% hard-construction cap
  // (no separate add-on), so Max Land Price = GDV − non-land − min net profit, which
  // equals the 15% Land Purchase cap.
  const uwFee = Number(record.builder_gc_fee) || defaultBuilderFee(gdv);
  const uwCap = gdv > 0 ? computeArvUnderwriting(gdv, uwFee) : {};
  const nonLand = gdv > 0
    ? (Number(record.construction_budget) || Number(uwCap.construction_budget) || 0)
      + (Number(record.soft_costs) || Number(uwCap.soft_costs) || 0)
      + (Number(record.construction_contingency) || Number(uwCap.construction_contingency) || 0)
      + (Number(record.financing_carry) || Number(uwCap.financing_carry) || 0)
      + (Number(record.selling_costs) || Number(uwCap.selling_costs) || 0)
    : (siteWork + construction + soft + contingency + financing + selling);
  const requiredProfit = gdv > 0 ? (Number(uwCap.est_net_profit) || Math.round(gdv * 0.1)) : stratRound(gdv * profitPct / 100);
  const effProfitPct = gdv > 0 ? Math.round(requiredProfit / gdv * 100) : profitPct;
  const maxLandBasis = gdv > 0 ? Math.round(gdv - nonLand - requiredProfit) : null;
  // Expected (preliminary) economics use the ACTUAL land ask + non-land cost — matching the
  // Underwriting Model's preliminary Total Development Cost / Net Developer Profit rows — so
  // the JV "Project Profit" equals the Net Developer Profit shown in the table. (Max Land
  // Price above stays the cap-based maximum; actual land may sit below it.)
  const landAsk = gdv > 0 ? (Number(record.list_price) || Number(record.asking_price) || Number(record.max_land_purchase_price) || Number(uwCap.max_land_purchase_price) || 0) : 0;
  const allIn = gdv > 0 ? nonLand + landAsk : nonLand;
  const profit = gdv > 0 ? gdv - allIn : null;
  const margin = gdv > 0 ? (profit / gdv) * 100 : null;
  const _vbInfo = resolveLots(record);
  const vertLotEcon = _vbInfo.count > 1 ? computeLotEconomics(record, "vertical", { gdv, homeSize, construction, product: record.product_type || record.home_type || null }, _vbInfo.count) : null;
  // Project-level roll-up (clearly labelled TOTAL PROJECT) — derived from the per-home
  // underwriting x N homes + land purchased once. The per-home Underwriting Model stays the
  // unit source of truth; Max Offer / maxLandBasis per-home are unchanged.
  let vertProject = null;
  if (_vbInfo.count > 1 && vertLotEcon) {
    const planHomes = _vbInfo.count;
    const totalBuildableSf = vertLotEcon.rows.reduce((a, r) => a + (r.homeSqft || 0), 0);
    const avgHomeSize = totalBuildableSf > 0 ? Math.round(totalBuildableSf / planHomes) : homeSize;
    const totalGDV = vertLotEcon.totalValue;
    const totalConstruction = vertLotEcon.totalBuildCost;
    const otherNonLandPerHome = Math.max(0, nonLand - construction);
    const projectNonLand = totalConstruction + otherNonLandPerHome * planHomes;
    const projectAllIn = projectNonLand + landAsk;
    const projectProfit = totalGDV - projectAllIn;
    const projectMargin = totalGDV > 0 ? (projectProfit / totalGDV) * 100 : null;
    const projectMaxLand = Math.round(totalGDV - projectNonLand - requiredProfit * planHomes);
    vertProject = { lots: planHomes, homes: planHomes, avgHomeSize, totalBuildableSf, totalGDV, totalConstruction, projectNonLand, projectAllIn, projectProfit, projectMargin, projectMaxLand };
  }
  const ret = {
    ...STRATEGY_META.build_sfr, profitPct: effProfitPct,
    revenueLabel: "Finished Home Value (GDV)", revenue: gdv, lotEconomics: vertLotEcon, vertProject, lotsCount: _vbInfo.count,
    costLines: [
      { label: "Site Development", value: siteWork, field: "site_work", cm: cmBuild.horizontal },
      { label: "Vertical Construction", value: construction },
      { label: "Soft Costs", value: soft },
      { label: "Contingency", value: contingency },
      { label: "Financing / Carry", value: financing },
      { label: "Selling Costs", value: selling },
    ],
    totalCost: nonLand, requiredProfit, maxLandBasis, allIn, profit, margin,
    extras: [
      { label: "Home Size", value: homeSize ? `${homeSize.toLocaleString()} SF` : "—" },
      { label: "Construction $/SF", value: homeSize > 0 ? `$${Math.round(construction / homeSize).toLocaleString()}` : "—" },
      { label: "Total Dev $/SF", value: homeSize > 0 && allIn ? `$${Math.round(allIn / homeSize).toLocaleString()}` : "—" },
    ],
    assumptions: [
      { field: "lots", label: "Potential Lots", value: _vbInfo.count },
      { field: "gdv", label: "Finished Home Value / GDV", value: gdv, money: true },
      { field: "home_size", label: "Home Size (SF)", value: homeSize },
      { field: "construction", label: "Vertical Construction", value: construction, money: true },
      { field: "site_work", label: "Site Development", value: siteWork, money: true },
      { field: "soft", label: "Soft Costs", value: soft, money: true },
      { field: "financing", label: "Financing / Carry", value: financing, money: true },
      { field: "selling", label: "Selling Costs", value: selling, money: true },
      { field: "profit_pct", label: "Required Profit %", value: profitPct, suffix: "%" },
    ],
  };
  // When Potential Lots > 1, the ENTIRE Vertical Build strategy underwrites the multi-home
  // development: revenue, cost, profit, margin, max land all become the TOTAL PROJECT, so the
  // existing Exit Metrics and JV Economics (which read these) reflect all homes. The embedded
  // Underwriting Model stays the labelled Per-Home unit model.
  if (vertProject) {
    ret.revenue = vertProject.totalGDV;
    ret.revenueLabel = `Total Finished Value (GDV) · ${vertProject.homes} homes`;
    ret.totalCost = vertProject.projectNonLand;
    ret.requiredProfit = requiredProfit * vertProject.homes;
    ret.maxLandBasis = vertProject.projectMaxLand;
    ret.allIn = vertProject.projectAllIn;
    ret.profit = vertProject.projectProfit;
    ret.margin = vertProject.projectMargin;
    ret.extras = [
      { label: "Avg Home Size", value: vertProject.avgHomeSize ? `${vertProject.avgHomeSize.toLocaleString()} SF` : "—" },
      { label: "Total Buildable SF", value: vertProject.totalBuildableSf ? `${vertProject.totalBuildableSf.toLocaleString()} SF` : "—" },
      { label: "Project Dev $/SF", value: vertProject.totalBuildableSf > 0 ? `$${Math.round(vertProject.projectAllIn / vertProject.totalBuildableSf).toLocaleString()}` : "—" },
    ];
  }
  return ret;
}

// Strict, non-hallucinated lot yield for Subdivide/Entitle prefill. Never invents a
// split from the zoning label. Priority: explicit potential_lots > active subdivision
// plan > Angela's verified zoning read (proposed_lots / potential_units) > parcel math
// (lot area / min lot size, gated by lot width / min width) — only when it yields
// >= 2 conforming lots > 1 (parcel as-is, no split supported).
function estimateLotYield(record) {
  const explicit = Number(record.potential_lots);
  if (explicit && explicit >= 1) return { lots: Math.floor(explicit), source: "Potential Lots (entered)" };
  if (isSubdivisionActive(record)) {
    const c = computeSubdivision(record.subdivision).count;
    if (c >= 1) return { lots: c, source: "Subdivision plan" };
  }
  const firstInt = (v) => { const n = parsePositiveNumbers(v); return n.length ? Math.floor(n[0]) : 0; };
  const pl = firstInt(record.proposed_lots);
  if (pl >= 2) return { lots: pl, source: "Zoning read — proposed lots" };
  const pu = firstInt(record.potential_units);
  if (pu >= 2) return { lots: pu, source: "Zoning read — potential units" };
  const lotSf = Number(record.lot_size_sqft) || (Number(record.lot_size_acres) * 43560) || 0;
  const minSf = Number(record.lz_min_lot_size) || Number(record.min_lot_size) || 0;
  if (lotSf > 0 && minSf > 0) {
    let yld = Math.floor(lotSf / minSf);
    const lotW = Number(record.lot_width) || 0;
    const minW = Number(record.lz_min_lot_width) || Number(record.min_lot_width) || 0;
    if (lotW > 0 && minW > 0) yld = Math.min(yld, Math.floor(lotW / minW));
    if (yld >= 2) return { lots: yld, source: "Parcel math — lot ÷ min lot size" };
  }
  return { lots: 1, source: "Parcel as-is — no split supported" };
}

// --- Lot-level subdivision economics (ADDITIVE — only engages when Potential Lots > 1) ---
function totalLotArea(record) {
  return Number(record.lot_size_sqft) || (Number(record.lot_size_acres) * 43560) || 0;
}

// Angela's independently-derived supported yield — ignores the user's entered Potential Lots
// so a conflict can be surfaced. Priority: zoning read > parcel math.
function verifiedLotYield(record) {
  const firstInt = (v) => { const n = parsePositiveNumbers(v); return n.length ? Math.floor(n[0]) : 0; };
  const pl = firstInt(record.proposed_lots);
  if (pl >= 1) return { lots: pl, source: "zoning read (proposed lots)" };
  const pu = firstInt(record.potential_units);
  if (pu >= 1) return { lots: pu, source: "zoning read (potential units)" };
  const lotSf = totalLotArea(record);
  const minSf = Number(record.lz_min_lot_size) || Number(record.min_lot_size) || 0;
  if (lotSf > 0 && minSf > 0) {
    let yld = Math.floor(lotSf / minSf);
    const lotW = Number(record.lot_width) || 0;
    const minW = Number(record.lz_min_lot_width) || Number(record.min_lot_width) || 0;
    if (lotW > 0 && minW > 0) yld = Math.min(yld, Math.floor(lotW / minW));
    if (yld >= 1) return { lots: yld, source: "parcel math (lot area ÷ min lot size)" };
  }
  return null;
}

// The master subdivision trigger. Potential Lots = 1 → single-lot (unchanged) experience.
// Potential Lots > 1 → lot-level economics engage. Honors both the global Potential Lots
// field AND the per-strategy "Lot yield" override the user can edit inside a strategy.
function resolveLots(record) {
  const entered = Math.floor(Number(record.potential_lots) || 0);
  let stratLots = 0;
  const sg = record && record.strategies;
  if (sg) ["entitle", "subdivide", "build_sfr"].forEach((k) => {
    const v = sg[k] ? Math.floor(Number(sg[k].lots) || 0) : 0;
    if (v > stratLots) stratLots = v;
  });
  const est = Math.floor(estimateLotYield(record).lots || 1);
  const count = Math.max(entered, stratLots, est, 1);
  const v = verifiedLotYield(record);
  const verified = v ? v.lots : null;
  const conflict = verified != null && count >= 2 && verified < count;
  return { count, entered: count, verified, verifiedSource: v ? v.source : null, conflict, active: count > 1 };
}

function lotNumOv(ov, def) {
  if (ov === undefined || ov === null || ov === "") return def;
  const n = Number(String(ov).replace(/[^0-9.-]/g, ""));
  return isNaN(n) ? def : n;
}
function lotOverride(record, lotId, field) {
  const le = record && record.lot_economics && record.lot_economics.lots;
  const l = le && (le[lotId] || le[String(lotId)]);
  if (!l) return undefined;
  const v = l[field];
  return (v === undefined || v === null || v === "") ? undefined : v;
}

// Distribute a strategy's aggregate across the resulting lots (even split = Angela default)
// and layer per-lot user overrides on top. Lot identity (Lot 1..N) persists across Pad-Ready,
// Build-Ready and Vertical Build. `count` is the strategy's own resolved lot yield.
function computeLotEconomics(record, type, agg, count) {
  count = Math.max(1, Math.floor(count || resolveLots(record).count || 1));
  const totalSf = totalLotArea(record);
  const totalFront = Number(record.lot_width) || 0;
  const conf = confFromSpec(costimateContext(record).spec);
  const rows = [];
  let anyValueOv = false, anyTargetOv = false;
  for (let i = 1; i <= count; i++) {
    const sizeDef = totalSf > 0 ? Math.round(totalSf / count) : null;
    const frontDef = totalFront > 0 ? Math.round(totalFront / count) : null;
    const size = lotNumOv(lotOverride(record, i, "size_sqft"), sizeDef);
    const frontage = lotNumOv(lotOverride(record, i, "frontage"), frontDef);
    const row = {
      id: i, size, frontage,
      sizeOv: lotOverride(record, i, "size_sqft") !== undefined || lotOverride(record, i, "frontage") !== undefined,
      confidence: conf,
    };
    if (type === "vertical") {
      const vcOv = lotOverride(record, i, "vertical_cost");
      const fvOv = lotOverride(record, i, "finished_value");
      const ftOv = lotOverride(record, i, "finished_target");
      row.product = lotOverride(record, i, "home_product") || agg.product || "Standard Home";
      row.homeSqft = lotNumOv(lotOverride(record, i, "home_sqft"), agg.homeSize || null);
      row.buildCost = lotNumOv(vcOv, agg.construction != null ? Math.round(agg.construction) : null);
      row.finishedValue = lotNumOv(fvOv, agg.gdv != null ? Math.round(agg.gdv) : null);
      row.target = lotNumOv(ftOv, agg.gdv != null ? Math.round(agg.gdv) : null);
      row.valueOv = fvOv !== undefined || vcOv !== undefined || ftOv !== undefined || lotOverride(record, i, "home_product") !== undefined || lotOverride(record, i, "home_sqft") !== undefined;
    } else {
      const field = type === "build" ? "build" : "pad";
      const vOv = lotOverride(record, i, `${field}_value`);
      const tOv = lotOverride(record, i, `${field}_target`);
      const cOv = lotOverride(record, i, `${field}_cost`);
      if (vOv !== undefined) anyValueOv = true;
      if (tOv !== undefined) anyTargetOv = true;
      row.value = lotNumOv(vOv, agg.newMarketValue != null ? Math.round(agg.newMarketValue / count) : null);
      row.target = lotNumOv(tOv, agg.targetSale != null ? Math.round(agg.targetSale / count) : null);
      row.cost = lotNumOv(cOv, agg.totalCost != null ? Math.round(agg.totalCost / count) : null);
      row.valueOv = vOv !== undefined || tOv !== undefined || cOv !== undefined;
    }
    rows.push(row);
  }
  const totalValue = rows.reduce((a, r) => a + (type === "vertical" ? (r.finishedValue || 0) : (r.value || 0)), 0);
  const totalTarget = rows.reduce((a, r) => a + (r.target || 0), 0);
  const totalBuildCost = rows.reduce((a, r) => a + (r.buildCost || 0), 0);
  const totalLotCost = rows.reduce((a, r) => a + (r.cost || 0), 0);
  return { type, count, rows, totalValue, totalTarget, totalBuildCost, totalLotCost, anyValueOv, anyTargetOv };
}

function computeSubdivideStrategy(record) {
  const est = estimateLotYield(record);
  const ovLots = record.strategies && record.strategies.subdivide ? record.strategies.subdivide.lots : undefined;
  const lots = resolveLots(record).count;
  const lotSource = ovLots !== undefined && ovLots !== null && ovLots !== "" ? "Custom" : est.source;
  const rawLand = computeLandOffer(record).marketValue || Number(record.land_estimated_land_value) || 0;
  const landBasis = stratNum(record, "subdivide", "land_basis", rawLand);
  const costLines = buildReadyCostimate(record, lots).map((it) => ({
    label: it.label, field: it.field, cm: it.cm,
    value: stratNum(record, "subdivide", it.field, it.cm.value),
  }));
  const buildReadyCosts = costLines.reduce((a, l) => a + (l.value || 0), 0);
  const totalBasis = landBasis + buildReadyCosts;
  // Build-Ready creates finished, buildable lots — the exit is the finished-lot value, a larger
  // uplift than pad-ready because horizontal infrastructure (roads/utilities) is complete.
  const upliftPct = stratNum(record, "subdivide", "build_ready_uplift_pct", 100);
  // Build-Ready value is created over the LAND BASIS (what you pay), not the comp market value,
  // so the finished-lot uplift reflects horizontal development work (and stays above Pad-Ready).
  const perLotRaw = lots > 0 ? landBasis / lots : landBasis;
  const finishedPerLot = Number(record.finished_lot_value) || Math.round(perLotRaw * (1 + upliftPct / 100));
  const newMarketValue = stratNum(record, "subdivide", "build_ready_value", Number(record.finished_lot_value) ? finishedPerLot * lots : Math.round(landBasis * (1 + upliftPct / 100)));
  const targetSaleBase = stratNum(record, "subdivide", "target_sale_price", newMarketValue);
  // Subdivision-aware: when this strategy's lot yield > 1 and the user has edited individual
  // lots, the aggregate New Build-Ready Value / Target Sale become the sum of the lots.
  let buildLotEcon = null, effMarketValue = newMarketValue, effTargetSale = targetSaleBase;
  if (lots > 1) {
    buildLotEcon = computeLotEconomics(record, "build", { newMarketValue, targetSale: targetSaleBase, totalCost: buildReadyCosts }, lots);
    if (buildLotEcon.anyValueOv) effMarketValue = buildLotEcon.totalValue;
    if (buildLotEcon.anyTargetOv) effTargetSale = buildLotEcon.totalTarget;
    buildLotEcon.totalCost = buildReadyCosts;
    buildLotEcon.avgCostPerLot = lots > 0 ? Math.round(buildReadyCosts / lots) : null;
  }
  const targetSale = effTargetSale;
  const projectedProfit = targetSale > 0 ? targetSale - landBasis - buildReadyCosts : null;
  const valueCreated = effMarketValue - landBasis;
  const margin = targetSale > 0 && projectedProfit != null ? (projectedProfit / targetSale) * 100 : null;
  // Land is contributed by the landowner via the JV — Arqitek's capital required is the
  // non-land build-ready cost only, so Total Capital Required = Total Non-Land Cost.
  const allIn = buildReadyCosts;
  const maxLandBasis = landBasis;
  const profitPerLot = lots > 0 && projectedProfit != null ? Math.round(projectedProfit / lots) : null;
  const costToCreateLot = lots > 0 ? Math.round(buildReadyCosts / lots) : null;
  const valueCreation = {
    name: "Build-Ready",
    landBasis, padReadyCosts: buildReadyCosts, totalBasis, newMarketValue: effMarketValue, targetSale,
    rawLandValue: landBasis, workValueAdd: targetSale - landBasis, netWorkValue: (targetSale - landBasis) - buildReadyCosts,
    projectedProfit, valueCreated, margin, upliftPct, lots,
    finishedLotSet: !!Number(record.finished_lot_value), compCount: computeLandOffer(record).comps.filter((c) => c.price != null).length,
  };
  return {
    ...STRATEGY_META.subdivide,
    revenueLabel: "New Build-Ready Value", revenue: effMarketValue, note: `Lot yield: ${lots} — ${lotSource}`,
    metricCostLabel: "Build-Ready Costs",
    metricProfitLabel: "Total Build-Ready Basis", metricProfitValue: totalBasis, metricProfitCls: "text-foreground",
    metricLandLabel: "Projected Profit", metricLandValue: projectedProfit,
    valueCreation, lotEconomics: buildLotEcon,
    costLines,
    totalCost: buildReadyCosts, requiredProfit: projectedProfit, maxLandBasis, allIn, profit: projectedProfit, margin,
    extras: [
      { label: "Potential Lots", value: lots || "—" },
      { label: "Value Created (uplift)", value: formatCurrency(valueCreated) },
      { label: "Cost to Create Lot", value: costToCreateLot != null ? formatCurrency(costToCreateLot) : "—" },
      { label: "Profit / Lot", value: profitPerLot != null ? formatCurrency(profitPerLot) : "—" },
    ],
    assumptions: [
      { field: "lots", label: "Potential Lots", value: lots },
      { field: "land_basis", label: "Land Basis (contract / land cost)", value: landBasis, money: true },
      { field: "build_ready_uplift_pct", label: "Build-Ready Value Uplift %", value: upliftPct, suffix: "%" },
      { field: "build_ready_value", label: "New Build-Ready Market Value", value: newMarketValue, money: true },
      { field: "target_sale_price", label: "Target Sale Price", value: targetSale, money: true },
    ],
  };
}

function computeEntitleStrategy(record) {
  const est = estimateLotYield(record);
  const ovLots = record.strategies && record.strategies.entitle ? record.strategies.entitle.lots : undefined;
  const lots = resolveLots(record).count;
  const lotSource = ovLots !== undefined && ovLots !== null && ovLots !== "" ? "Custom" : est.source;
  const flip = isSubdivisionActive(record) ? (record.subdivision.land_flip || null) : null;
  const cm = angelaCostimate(record, "entitle", lots);
  const rawLand = computeLandOffer(record).marketValue || Number(record.land_estimated_land_value) || (flip && Number(flip.total_market_value)) || 0;
  const landBasis = stratNum(record, "entitle", "land_basis", rawLand);
  const entitlementBase = Number(record.entitlement_costs) || (flip && Number(flip.entitlement_cost_total)) || cm.entitlement.value;
  const entitlement = stratNum(record, "entitle", "entitlement_cost", entitlementBase);
  const planning = stratNum(record, "entitle", "planning_cost", cm.planning.value);
  const engineering = stratNum(record, "entitle", "engineering_cost", cm.engineering.value);
  const mapping = stratNum(record, "entitle", "mapping_cost", cm.mapping.value);
  const padReadyCosts = entitlement + planning + engineering + mapping;
  const totalBasis = landBasis + padReadyCosts;
  // Pad-Ready creates a NEW, higher-value asset — the exit is the pad-ready/buildable-lot value,
  // NOT the current raw-land value. Angela estimates the entitlement uplift (editable).
  const upliftPct = stratNum(record, "entitle", "pad_ready_uplift_pct", 50);
  // Value creation is measured over the LAND BASIS (what you actually pay/contract), not the
  // comp market value — so the uplift and "value add" reflect the entitlement WORK, not land
  // arbitrage. When no land_basis override is entered, landBasis defaults to raw land (unchanged).
  const perLotRaw = lots > 0 ? landBasis / lots : landBasis;
  const padReadyPerLot = Number(record.finished_lot_value) || Math.round(perLotRaw * (1 + upliftPct / 100));
  const newMarketValue = stratNum(record, "entitle", "pad_ready_value", Number(record.finished_lot_value) ? padReadyPerLot * lots : Math.round(landBasis * (1 + upliftPct / 100)));
  const targetSaleBase = stratNum(record, "entitle", "target_sale_price", newMarketValue);
  // Subdivision-aware: when this strategy's lot yield > 1 and the user has edited individual
  // lots, the aggregate New Pad-Ready Value / Target Sale become the sum of the resulting lots.
  let padLotEcon = null, effMarketValue = newMarketValue, effTargetSale = targetSaleBase;
  if (lots > 1) {
    padLotEcon = computeLotEconomics(record, "pad", { newMarketValue, targetSale: targetSaleBase, totalCost: padReadyCosts }, lots);
    if (padLotEcon.anyValueOv) effMarketValue = padLotEcon.totalValue;
    if (padLotEcon.anyTargetOv) effTargetSale = padLotEcon.totalTarget;
    padLotEcon.totalCost = padReadyCosts;
    padLotEcon.avgCostPerLot = lots > 0 ? Math.round(padReadyCosts / lots) : null;
  }
  const targetSale = effTargetSale;
  const projectedProfit = targetSale > 0 ? targetSale - landBasis - padReadyCosts : null;
  const valueCreated = effMarketValue - landBasis;
  const margin = targetSale > 0 && projectedProfit != null ? (projectedProfit / targetSale) * 100 : null;
  const allIn = totalBasis;  // capital required = land basis + pad-ready costs
  const maxLandBasis = landBasis;
  const profitPerLot = lots > 0 && projectedProfit != null ? Math.round(projectedProfit / lots) : null;
  const valueCreation = {
    name: "Pad-Ready",
    landBasis, padReadyCosts, totalBasis, newMarketValue: effMarketValue, targetSale,
    rawLandValue: landBasis, workValueAdd: targetSale - landBasis, netWorkValue: (targetSale - landBasis) - padReadyCosts,
    projectedProfit, valueCreated, margin, upliftPct, lots,
    finishedLotSet: !!Number(record.finished_lot_value), compCount: computeLandOffer(record).comps.filter((c) => c.price != null).length,
  };
  return {
    ...STRATEGY_META.entitle,
    revenueLabel: "New Pad-Ready Value", revenue: effMarketValue, note: `Lot yield: ${lots} — ${lotSource}`,
    metricCostLabel: "Pad-Ready Costs",
    metricProfitLabel: "Total Pad-Ready Basis", metricProfitValue: totalBasis, metricProfitCls: "text-foreground",
    metricLandLabel: "Projected Profit", metricLandValue: projectedProfit,
    valueCreation, lotEconomics: padLotEcon,
    costLines: [
      { label: "Entitlement", value: entitlement, field: "entitlement_cost", cm: cm.entitlement },
      { label: "Planning", value: planning, field: "planning_cost", cm: cm.planning },
      { label: "Engineering", value: engineering, field: "engineering_cost", cm: cm.engineering },
      { label: "Mapping", value: mapping, field: "mapping_cost", cm: cm.mapping },
    ],
    totalCost: padReadyCosts, requiredProfit: projectedProfit, maxLandBasis, allIn, profit: projectedProfit, margin,
    extras: [
      { label: "Potential Lots", value: lots || "—" },
      { label: "Value Created (uplift)", value: formatCurrency(valueCreated) },
      { label: "Profit / Lot", value: profitPerLot != null ? formatCurrency(profitPerLot) : "—" },
    ],
    assumptions: [
      { field: "lots", label: "Potential Lots", value: lots },
      { field: "land_basis", label: "Land Basis (contract / land cost)", value: landBasis, money: true },
      { field: "entitlement_cost", label: "Entitlement", value: entitlement, money: true },
      { field: "planning_cost", label: "Planning", value: planning, money: true },
      { field: "engineering_cost", label: "Engineering", value: engineering, money: true },
      { field: "mapping_cost", label: "Mapping", value: mapping, money: true },
      { field: "pad_ready_uplift_pct", label: "Pad-Ready Value Uplift %", value: upliftPct, suffix: "%" },
      { field: "pad_ready_value", label: "New Pad-Ready Market Value", value: newMarketValue, money: true },
      { field: "target_sale_price", label: "Target Sale Price", value: targetSale, money: true },
    ],
  };
}

function computeLandFlipStrategy(record) {
  const lo = computeLandOffer(record);
  const flip = isSubdivisionActive(record) ? (record.subdivision.land_flip || null) : null;
  const marketLandValue = stratNum(record, "land_flip", "market_land_value", lo.marketValue || Number(record.land_estimated_land_value) || (flip && Number(flip.total_market_value)) || (flip && Number(flip.original_land_cost)) || 0);
  // SELL = what the market will pay (comp-derived market value). BUY = Angela's realistic
  // acquisition at a discount below market. Profit is the DERIVED result — not a fixed margin.
  const resaleValue = stratNum(record, "land_flip", "resale_value", marketLandValue);
  const acqDiscountPct = stratNum(record, "land_flip", "acq_discount_pct", Number(record.land_flip_margin_pct) || 30);
  const contractPrice = stratNum(record, "land_flip", "buy_price", Math.round(resaleValue * (1 - acqDiscountPct / 100)));
  // CONTRACT / ENTITLEMENT-RIGHTS FLIP: Arqitek ties the parcel up under contract and ASSIGNS the
  // rights to an end buyer — it never closes or owns the land. So there is NO acquisition closing,
  // financing, property tax, insurance or carry, and the contract price is NOT Arqitek's capital.
  // Profit = assignment spread (resale − contract) minus the small transaction costs to tie up & assign.
  const dueDiligence = stratNum(record, "land_flip", "due_diligence", Math.round(contractPrice * 0.005));  // title/survey/inspection in option period
  const legal = stratNum(record, "land_flip", "legal_costs", Math.round(resaleValue * 0.005));              // legal / assignment & closing
  const transactionCosts = dueDiligence + legal;
  const grossSpread = resaleValue > 0 ? resaleValue - contractPrice : null;    // assignment fee
  const profit = grossSpread != null ? grossSpread - transactionCosts : null;  // net assignment profit
  const capitalAtRisk = transactionCosts;                                      // what Arqitek actually fronts
  const margin = resaleValue > 0 && profit != null ? (profit / resaleValue) * 100 : null;
  const roi = capitalAtRisk > 0 && profit != null ? (profit / capitalAtRisk) * 100 : null;
  const maxLandBasis = contractPrice;  // the contract price feeds Offer / Acquisition downstream
  const compCount = lo.comps.filter((c) => c.price != null).length;
  const recommendation = {
    buy: contractPrice, sell: resaleValue || null, profit, margin, roi,
    compCount, marketValue: lo.marketValue || null, discountPct: acqDiscountPct,
    spread: grossSpread, capitalAtRisk, contract: true,
  };
  return {
    ...STRATEGY_META.land_flip,
    question: "Can Arqitek tie up this parcel under contract and assign the rights to an end buyer for a profit — without ever closing or owning the land?",
    revenueLabel: "Assignment / Resale Price", revenue: resaleValue,
    metricCostLabel: "Transaction Costs",
    metricProfitLabel: `Assignment Profit · ${margin == null ? 0 : margin.toFixed(0)}% of resale`, metricProfitValue: profit, metricProfitCls: profit != null && profit >= 0 ? "text-emerald-400" : "text-destructive",
    metricLandLabel: "Contract Price", metricLandValue: contractPrice,
    costLines: [
      { label: "Due Diligence (title / survey)", value: dueDiligence, tip: "~0.5% of contract price — title search, survey & inspection during the option / due-diligence period" },
      { label: "Legal / Assignment & Closing", value: legal, tip: "~0.5% of resale — assignment agreement & closing coordination" },
    ],
    totalCost: transactionCosts, requiredProfit: profit, maxLandBasis, allIn: capitalAtRisk, profit, margin, recommendation,
    extras: [
      { label: "Gross Assignment Spread", value: grossSpread != null ? formatCurrency(grossSpread) : "—" },
      { label: "Capital at Risk", value: formatCurrency(capitalAtRisk) },
      { label: "Acquisition Discount", value: `${acqDiscountPct}% below market` },
    ],
    assumptions: [
      { field: "market_land_value", label: "Market Land Value", value: marketLandValue, money: true },
      { field: "resale_value", label: "Assignment / Resale Price", value: resaleValue, money: true },
      { field: "acq_discount_pct", label: "Contract Discount Below Market %", value: acqDiscountPct, suffix: "%" },
      { field: "buy_price", label: "Contract Price (override)", value: contractPrice, money: true },
      { field: "due_diligence", label: "Due Diligence", value: dueDiligence, money: true },
      { field: "legal_costs", label: "Legal / Assignment", value: legal, money: true },
    ],
  };
}

function computeStrategy(record, key) {
  switch (key) {
    case "subdivide": return computeSubdivideStrategy(record);
    case "entitle": return computeEntitleStrategy(record);
    case "land_flip": return computeLandFlipStrategy(record);
    case "build_sfr":
    default: return computeBuildSFR(record);
  }
}

// Economics of the record's PRIMARY strategy — source of truth for the deal.
// Returns null when no primary strategy has been selected (keeps legacy behaviour).
function strategyEconomicsFor(record, key) {
  if (!key || !STRATEGY_KEYS.includes(key)) return null;
  const s = computeStrategy(record, key);
  return { key, label: s.label, revenue: s.revenue, allIn: s.allIn, profit: s.profit, margin: s.margin, maxLandBasis: s.maxLandBasis };
}

function primaryStrategyEconomics(record) {
  return strategyEconomicsFor(record, record && record.primary_strategy);
}

function StratTile({ label, value, cls, tid, big }) {
  return (
    <div className={`rounded-md border px-3 py-2 h-full flex flex-col ${big ? "border-primary/40 bg-primary/5" : "border-border/60 bg-secondary/20"}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground min-h-[26px]">{label}</div>
      <div className={`text-sm font-semibold mt-auto ${cls || "text-foreground"}`} data-testid={tid}>{value}</div>
    </div>
  );
}

const COST_STATUS_BADGE = {
  angela: { label: "Angela", full: "Angela estimate", cls: "bg-primary/15 text-primary border-primary/30" },
  user: { label: "Edited", full: "User estimate", cls: "bg-secondary/60 text-muted-foreground border-border/60" },
  verified: { label: "Verified", full: "Verified / actual", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
};
const CONF_CLS = { High: "text-emerald-400", Medium: "text-amber-400", Low: "text-muted-foreground" };

// A single Non-Land cost row. Angela-estimated rows (those with a `cm` payload)
// show a status/confidence badge and open a Costimate popover (basis, range,
// override, mark-verified, revert). Non-estimated rows render as plain text.
function CostLineRow({ line, status, money, lastCalc, onSave, onRevert }) {
  const [val, setVal] = useState("");
  const cm = line.cm;
  if (!cm || !line.field) {
    return (
      <div className="flex justify-between text-sm py-0.5">
        <span className={`text-muted-foreground ${line.tip ? "underline decoration-dotted decoration-muted-foreground/40 underline-offset-2 cursor-help" : ""}`} title={line.tip || undefined}>{line.label}</span><span className="font-medium">{money(line.value)}</span>
      </div>
    );
  }
  const badge = COST_STATUS_BADGE[status] || COST_STATUS_BADGE.angela;
  return (
    <div className="flex items-center justify-between text-sm py-1 gap-2">
      <span className="text-muted-foreground flex items-center gap-1.5 min-w-0">
        <span className="truncate">{line.label}</span>
        <span className={`shrink-0 inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${badge.cls}`} data-testid={`cost-badge-${line.field}`}>
          {status === "angela" && <Sparkles className="h-2.5 w-2.5" />}{badge.label}
        </span>
      </span>
      <Popover onOpenChange={(o) => { if (o) setVal(String(line.value ?? "")); }}>
        <PopoverTrigger asChild>
          <button className="font-medium hover:underline decoration-dotted underline-offset-2 inline-flex items-center gap-1" data-testid={`cost-value-${line.field}`}>
            {money(line.value)}
            {status === "angela" && <span className={`text-[9px] ${CONF_CLS[cm.confidence] || ""}`}>{(cm.confidence || "")[0]}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72" align="end" data-testid={`costimate-pop-${line.field}`}>
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">Angela Costimate</span>
          </div>
          <div className="space-y-1.5 text-[12px]">
            <div className="flex justify-between"><span className="text-muted-foreground">Working Estimate</span><span className="font-semibold" data-testid={`costimate-working-${line.field}`}>{money(line.value)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Estimated Range</span><span className="font-medium">{money(cm.low)} – {money(cm.high)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Confidence</span><span className={`font-medium ${CONF_CLS[cm.confidence] || ""}`}>{cm.confidence}</span></div>
            <div><span className="text-muted-foreground">Basis</span><p className="text-foreground/90 mt-0.5 leading-snug">{cm.basis}</p></div>
            <div className="flex justify-between gap-2"><span className="text-muted-foreground shrink-0">Source Type</span><span className="font-medium text-right">{cm.source}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="font-medium">{badge.full}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Last Calculated</span><span className="font-medium">{lastCalc}</span></div>
          </div>
          <div className="mt-3 pt-2 border-t border-border/60">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Override Estimate</label>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-sm text-muted-foreground">$</span>
              <input value={val} onChange={(e) => setVal(e.target.value)} inputMode="decimal"
                className="flex-1 bg-secondary/40 border border-border/60 rounded px-2 py-1 text-sm outline-none" data-testid={`costimate-input-${line.field}`} />
            </div>
            <div className="flex items-center gap-1.5 mt-2">
              <Button size="sm" className="h-7 text-xs flex-1" onClick={() => onSave(line.field, val, null)} data-testid={`costimate-save-${line.field}`}>Save</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onSave(line.field, val, "verified")} data-testid={`costimate-verify-${line.field}`}>Mark Verified</Button>
            </div>
            {status !== "angela" && (
              <button className="text-[11px] text-primary hover:underline mt-2 inline-flex items-center gap-1" onClick={() => onRevert(line.field)} data-testid={`costimate-revert-${line.field}`}>
                <RotateCcw className="h-3 w-3" />Revert to Angela estimate
              </button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ── Exit Analysis: enrich every strategy with comparable, decision-grade metrics ──
const STRATEGY_LADDER = ["land_flip", "entitle", "subdivide", "build_sfr"];
const STRATEGY_TIMELINE = { land_flip: 3, entitle: 12, subdivide: 18, build_sfr: 9 };
const STRATEGY_RISK = { land_flip: "Low–Med", entitle: "Medium", subdivide: "Med–High", build_sfr: "High" };
const STRATEGY_COMPLEXITY = { land_flip: 1, entitle: 2, subdivide: 4, build_sfr: 3 };
const JV_SPLIT_DEFAULT = { landowner: 0, investor: 0, arqitek: 100 };

// ── Angela Costimate engine ──────────────────────────────────────────────────
// Deterministic Arqitek cost model. Produces realistic working estimates (never
// $0) for the development soft-cost categories from whatever property-specific
// information is available, with a confidence band, plain-English basis and
// source type. Runs instantly on every analysis so underwriting always recalcs.
const COSTIMATE_ANNUAL_INTEREST = 0.10; // financing assumption for carrying cost

function parseMoneyLoose(v) {
  if (v == null) return 0;
  const n = Number(String(v).replace(/[^0-9.]/g, ""));
  return isNaN(n) ? 0 : n;
}

function costimateContext(record, lotsOverride) {
  const acres = Number(record.lot_size_acres) || (Number(record.lot_size_sqft) / 43560) || 0;
  const lots = Math.max(1, Math.floor(lotsOverride || estimateLotYield(record).lots || 1));
  const yesish = (v) => { const s = String(v == null ? "" : v).toLowerCase(); return s.startsWith("y") || s === "true" || s.includes("public") || s.includes("avail") || s.includes("connect"); };
  const waterPublic = record.water_available === true || yesish(record.lot_utility_water) || yesish(record.water_status) || yesish(record.water_source);
  const sewerPublic = record.sewer_available === true || yesish(record.lot_utility_sewer) || yesish(record.sewer_status);
  const pavedAccess = record.paved_road_access === true || record.legal_access === true;
  const topo = String(record.topography || "").toLowerCase();
  const steep = topo.includes("steep") || topo.includes("hillside");
  const env = String(record.environmental_concerns || "").toLowerCase();
  const wet = topo.includes("wetland") || env.includes("wetland") || env.includes("flood");
  const jurisdiction = record.jurisdiction || record.county || record.city || "";
  const zoning = record.zoning || record.zoning_district || "";
  const taxes = parseMoneyLoose(record.estimated_property_taxes || record.land_estimated_property_taxes);
  const landValue = computeLandOffer(record).marketValue || Number(record.land_estimated_land_value) || 0;
  let complexity = 1;
  if (steep) complexity += 0.25;
  if (wet) complexity += 0.35;
  if (!pavedAccess) complexity += 0.12;
  let spec = 0;
  if (jurisdiction) spec += 1;
  if (zoning) spec += 1;
  if (acres > 0) spec += 1;
  if (record.lot_utility_water != null || record.water_available != null || record.water_status) spec += 1;
  if (record.topography) spec += 1;
  if (Number(record.potential_lots) || Number(record.proposed_lots) || Number(record.potential_units)) spec += 1;
  return { acres, lots, waterPublic, sewerPublic, pavedAccess, steep, wet, jurisdiction, zoning, taxes, landValue, complexity, spec };
}

function confFromSpec(spec, bump = 0) {
  const s = spec + bump;
  if (s >= 5) return "High";
  if (s >= 3) return "Medium";
  return "Low";
}

function sumCm(items, basis, conf) {
  const value = items.reduce((a, b) => a + (b.value || 0), 0);
  const low = items.reduce((a, b) => a + (b.low || 0), 0);
  const high = items.reduce((a, b) => a + (b.high || 0), 0);
  return { value, low, high, confidence: conf || (items[0] && items[0].confidence) || "Low", source: (items[0] && items[0].source) || "Arqitek cost model", basis };
}

function angelaCostimate(record, key, lotsOverride) {
  const c = costimateContext(record, lotsOverride);
  const src = c.jurisdiction && (c.zoning || c.acres) ? "Jurisdiction + property data" : c.acres ? "Property data + Arqitek cost model" : "Arqitek cost model";
  const r = (v) => Math.round(v);
  const band = (v, pct) => ({ low: r(v * (1 - pct)), high: r(v * (1 + pct)) });
  const loc = c.jurisdiction ? ` in ${c.jurisdiction}` : "";
  const lotsLabel = `${c.lots} lot${c.lots === 1 ? "" : "s"}`;
  const subdiv = c.lots > 1;

  let ent = (3500 + 550 * c.lots + (subdiv ? 2800 : 0)) * c.complexity;
  const entitlement = { value: r(ent), ...band(ent, 0.3), confidence: confFromSpec(c.spec, c.jurisdiction ? 0 : -1), source: src,
    basis: `Zoning/${subdiv ? "subdivision" : "land-use"} application, planning-department fees & hearings for ${lotsLabel}${loc}${c.zoning ? `, ${c.zoning} zoning` : ""}.` };

  let plan = (2800 + 650 * c.lots) * (1 + (c.complexity - 1) * 0.5);
  const planning = { value: r(plan), ...band(plan, 0.3), confidence: confFromSpec(c.spec, -1), source: src,
    basis: `Concept & preliminary site planning / land-use consulting for ${lotsLabel}.` };

  let eng = 6000 + 1600 * c.lots + 900 * c.acres;
  if (!c.sewerPublic) eng += 1500;
  if (!c.waterPublic) eng += 1000;
  eng *= c.complexity;
  const engineering = { value: r(eng), ...band(eng, 0.35), confidence: confFromSpec(c.spec, -1), source: src,
    basis: `Civil engineering — grading, drainage, stormwater${!c.sewerPublic ? ", on-site sewer/septic" : ""}${!c.waterPublic ? ", water" : ""} design for ${c.acres ? c.acres.toFixed(2) + " ac" : lotsLabel}.` };

  let map = 3500 + 850 * c.lots + 300 * c.acres;
  if (c.acres >= 5) map += 2500;
  const mapping = { value: r(map), ...band(map, 0.25), confidence: confFromSpec(c.spec), source: src,
    basis: `Boundary & topographic survey, ${subdiv ? "preliminary & final plat" : "parcel map"}, legal descriptions${c.acres >= 5 ? ", ALTA" : ""}.` };

  const months = stratNum(record, key, "timeline_months", STRATEGY_TIMELINE[key]) || STRATEGY_TIMELINE[key] || 0;
  const monthlyTax = c.taxes > 0 ? c.taxes / 12 : (c.landValue > 0 ? c.landValue * 0.012 / 12 : 0);
  const monthlyIns = Math.max(35, c.acres * 6);
  const monthlyInterest = c.landValue > 0 ? c.landValue * COSTIMATE_ANNUAL_INTEREST / 12 : 0;
  const monthly = monthlyTax + monthlyIns + monthlyInterest;
  const carryVal = monthly * months;
  const carrying = { value: r(carryVal), ...band(carryVal, 0.2), confidence: c.taxes > 0 ? "High" : confFromSpec(c.spec, -1),
    source: c.taxes > 0 ? "Property data + Arqitek cost model" : src,
    basis: `${months} mo hold × (${c.taxes > 0 ? "property taxes" : "est. taxes"} + insurance + ${Math.round(COSTIMATE_ANNUAL_INTEREST * 100)}% financing on ${c.landValue ? formatCurrency(Math.round(c.landValue)) : "land"}).` };

  let horiz = (12000 * c.acres + 9000 * c.lots);
  if (!c.sewerPublic) horiz += 8000 * c.lots;
  if (!c.waterPublic) horiz += 6000 * c.lots;
  horiz *= c.complexity;
  const horizontal = { value: r(horiz), ...band(horiz, 0.4), confidence: confFromSpec(c.spec, -2), source: src,
    basis: `Site work — grading, roads/access, stormwater, utility extensions for ${lotsLabel}${!c.sewerPublic ? " (sewer/septic)" : ""}.` };

  return { entitlement, planning, engineering, mapping, carrying, horizontal, ctx: c };
}

// Build-Ready itemized cost ladder — 19 property-aware development line items.
function buildReadyCostimate(record, lotsOverride) {
  const c = costimateContext(record, lotsOverride);
  const src = c.jurisdiction && (c.zoning || c.acres) ? "Jurisdiction + property data" : c.acres ? "Property data + Arqitek cost model" : "Arqitek cost model";
  const r = (v) => Math.round(v);
  const band = (v, pct) => ({ low: r(v * (1 - pct)), high: r(v * (1 + pct)) });
  const a = c.acres, L = c.lots, cx = c.complexity, subdiv = c.lots > 1;
  const mk = (value, pct, confBump, basis) => ({ value: r(value), ...band(value, pct), confidence: confFromSpec(c.spec, confBump), source: src, basis });
  return [
    { label: "Entitlement & Approvals", field: "br_entitlement", cm: mk((3500 + 400 * L + (subdiv ? 2500 : 0)) * cx, 0.3, c.jurisdiction ? 0 : -1, `Zoning/${subdiv ? "subdivision" : "land-use"} applications, planning-department fees & hearings${c.jurisdiction ? ` in ${c.jurisdiction}` : ""}.`) },
    { label: "Planning & Site Design", field: "br_planning", cm: mk((2500 + 500 * L) * (1 + (cx - 1) * 0.5), 0.3, -1, "Concept & preliminary site/subdivision design and land-use consulting.") },
    { label: "Preliminary Engineering", field: "br_prelim_eng", cm: mk((3500 + 700 * L + 400 * a) * cx, 0.35, -1, "Preliminary civil / feasibility engineering & layout.") },
    { label: "Surveying & Mapping", field: "br_survey", cm: mk(2800 + 600 * L + 250 * a + (a >= 5 ? 2000 : 0), 0.25, 0, "Boundary & topographic survey and legal descriptions.") },
    { label: "Final Map / Legal Lots", field: "br_final_map", cm: mk(1500 + 450 * L + (subdiv ? 1200 : 0), 0.25, 0, `${subdiv ? "Final plat / subdivision map" : "Parcel map"} preparation & recording.`) },
    { label: "Final Civil Engineering", field: "br_final_civil", cm: mk((4000 + 1100 * L + 500 * a) * cx, 0.35, -1, "Final construction drawings — grading, drainage & utility design.") },
    { label: "Grading & Finished Building Pad", field: "br_grading_pad", cm: mk((6000 * a + 3500 * L) * cx, 0.4, -2, `Mass grading & finished building pads for ${L} lot${L === 1 ? "" : "s"}${c.steep ? " (steep terrain)" : ""}.`) },
    { label: "Drainage & Stormwater", field: "br_drainage", cm: mk((2500 * a + 1200 * L) * cx, 0.4, -2, `Stormwater management, detention & drainage${c.wet ? " (wet / flood constraints)" : ""}.`) },
    { label: "Road & Site Access", field: "br_road_access", cm: mk((8000 + 4000 * L) * cx + (c.pavedAccess ? 0 : 6000), 0.4, -2, `Internal roads & site access${c.pavedAccess ? "" : " (no existing paved access)"}.`) },
    { label: "Water", field: "br_water", cm: mk((c.waterPublic ? 2500 : 6000) * L, 0.35, -1, c.waterPublic ? "Public water main extension & lot connections." : "On-site well(s) / private water supply.") },
    { label: "Sewer / Septic", field: "br_sewer", cm: mk((c.sewerPublic ? 3000 : 9000) * L, 0.4, -1, c.sewerPublic ? "Public sewer main extension & lot connections." : "On-site septic systems / drain fields.") },
    { label: "Electric & Other Utilities", field: "br_electric", cm: mk(2500 * L + 1500, 0.35, -1, "Electric, gas & telecom extensions and trenching.") },
    { label: "Fire & Emergency Access", field: "br_fire_access", cm: mk(1500 + 500 * L, 0.3, -1, "Fire hydrants, turnarounds & emergency access.") },
    { label: "Off-Site Improvements", field: "br_offsite", cm: mk(subdiv ? 3000 + 1000 * L : 2000, 0.4, -2, "Off-site road / utility improvements & dedications required by the jurisdiction.") },
    { label: "Geotechnical / Soils", field: "br_geotech", cm: mk((2500 + 300 * a) * (c.steep ? 1.3 : 1), 0.3, -1, "Geotechnical borings & soils investigation reports.") },
    { label: "Environmental / Flood / Special Constraints", field: "br_environmental", cm: mk(c.wet ? 6000 : 1500, 0.4, -2, c.wet ? "Wetland / flood delineation, mitigation & permitting." : "Environmental screening / Phase I review.") },
    { label: "Development & Utility Fees", field: "br_dev_fees", cm: mk(1500 * L, 0.3, -1, "Impact, tap & development fees per lot.") },
    { label: "Final Lot & Building Envelope Verification", field: "br_envelope_verify", cm: mk(800 * L + 500, 0.25, 0, "As-built verification of lots & building envelopes.") },
    { label: "Legal Parcel / Addressing", field: "br_legal_parcel", cm: mk(500 + 150 * L, 0.2, 0, "Legal parcel creation, addressing & recordation.") },
  ];
}


// Status of a cost line: 'verified' > 'user' (override present) > 'angela' (model).
function costLineStatus(record, key, field) {
  const ov = record && record.strategies && record.strategies[key];
  const statusMap = (ov && ov._status) || {};
  if (statusMap[field] === "verified") return "verified";
  if (ov && ov[field] !== undefined && ov[field] !== null && ov[field] !== "") return "user";
  return "angela";
}

function bucketFromCostLines(costLines, keywords) {
  return (costLines || []).reduce((sum, c) => {
    const l = (c.label || "").toLowerCase();
    return keywords.some((k) => l.includes(k)) ? sum + (Number(c.value) || 0) : sum;
  }, 0);
}

function enrichStrategy(record, key) {
  const s = computeStrategy(record, key);
  const exitValue = Number(s.revenue) || 0;
  const landBasis = s.maxLandBasis;
  const entitlement = bucketFromCostLines(s.costLines, ["entitlement", "planning", "engineering", "mapping"]);
  const horizontal = bucketFromCostLines(s.costLines, ["horizontal", "site development", "infrastructure"]);
  const vertical = bucketFromCostLines(s.costLines, ["vertical", "construction"]);
  const financingCarry = bucketFromCostLines(s.costLines, ["financing", "carry", "carrying"]);
  const totalCapital = Number(s.allIn) || 0;
  const profit = s.profit == null ? null : Math.round(s.profit);
  const roi = totalCapital > 0 && profit != null ? profit / totalCapital : null;
  const equityMultiple = totalCapital > 0 && profit != null ? (totalCapital + profit) / totalCapital : null;
  const timelineMonths = stratNum(record, key, "timeline_months", STRATEGY_TIMELINE[key]);
  const profitPerMonth = profit != null && timelineMonths > 0 ? profit / timelineMonths : null;
  const hardCapOk = s.margin != null && s.margin >= 10 && landBasis != null && landBasis >= 0;
  return {
    key, label: s.label, exitValue, landBasis, entitlement, horizontal, vertical, financingCarry,
    totalCapital, profit, roi, equityMultiple, timelineMonths, profitPerMonth, margin: s.margin,
    risk: STRATEGY_RISK[key], complexity: STRATEGY_COMPLEXITY[key], hardCapOk,
  };
}

function jvEconomics(e, split, arqitekCash, defaultCash) {
  const pp = e.profit != null ? Math.max(0, e.profit) : 0;
  const landowner = Math.round(pp * (split.landowner || 0) / 100);
  const investor = Math.round(pp * (split.investor || 0) / 100);
  const arqitek = Math.round(pp * (split.arqitek || 0) / 100);
  const fallback = defaultCash != null ? Math.round(defaultCash) : Math.round(e.totalCapital * 0.05);
  const cash = arqitekCash != null && arqitekCash !== "" ? Number(arqitekCash) : fallback;
  const arqitekRoi = cash > 0 ? arqitek / cash : null;
  return { projectProfit: e.profit, landowner, investor, arqitek, arqitekCash: cash, arqitekRoi };
}

// ===================== Investor Exit-Strategy Snapshot =====================
// A serializable snapshot of ALL FOUR exit strategies' TRUE economics, computed with the
// EXACT same engine the Exit Analysis uses (computeStrategy / enrichStrategy / lot-level
// helpers). It is persisted to record.exit_strategy_snapshot and read by the public
// Investor Portal, which is purely a presentation layer — never a second underwriting engine.
const SNAP_DESC = {
  land_flip: "Tie the parcel up under contract and assign the rights to an end buyer — Arqitek never closes or owns the land.",
  entitle: "Entitle and subdivide the property, then sell the resulting pad-ready lots to builders.",
  subdivide: "Complete horizontal development and sell finished, build-ready lots to builders.",
  build_sfr: "Build and sell the finished homes — full vertical development of the site.",
};
const SNAP_EMPHASIS = {
  land_flip: ["Speed", "Contract basis", "Assignment economics", "Low capital exposure"],
  entitle: ["Entitlement", "Subdivision", "Lot values", "Builder takeout"],
  subdivide: ["Horizontal development", "Infrastructure", "Finished lot values", "Builder takeout"],
  build_sfr: ["Homes", "Construction", "GDV", "Total development economics"],
};

function snapStrategy(record, key) {
  const e = enrichStrategy(record, key);
  const s = computeStrategy(record, key);
  const le = s.lotEconomics || null;
  const n = (v) => (v == null || isNaN(v) ? null : Math.round(v));
  let lots = null, homes = null, lotRows = null, lotTotals = null;
  const leCount = le && le.count ? le.count : null;
  if (key !== "land_flip") {
    // Mirror the SAME lot count each strategy's own Exit Analysis uses so the header yield,
    // lot rows and economics always agree. entitle/subdivide use their own per-strategy lots
    // (reflected in their lotEconomics / valueCreation); build_sfr uses the resolved parcel
    // yield (lotsCount). This keeps the investor view consistent with the source of truth.
    lots = (key === "build_sfr")
      ? (s.lotsCount || leCount || 1)
      : (leCount || (s.valueCreation && s.valueCreation.lots) || 1);
    if (key === "build_sfr") homes = lots;
  }
  const vp = s.vertProject || null;
  if (le && Array.isArray(le.rows) && le.rows.length > 1) {
    const perHomeAllIn = vp && vp.homes ? vp.projectAllIn / vp.homes : null;
    if (key === "build_sfr") {
      lotRows = le.rows.map((r) => ({
        id: r.id, size: n(r.size), product: r.product || null, home_sqft: r.homeSqft ?? null,
        build_cost: n(r.buildCost), finished_value: n(r.finishedValue), target: n(r.target),
        profit: (r.finishedValue != null && perHomeAllIn != null) ? Math.round(r.finishedValue - perHomeAllIn)
          : (r.finishedValue != null && r.buildCost != null ? Math.round(r.finishedValue - r.buildCost) : null),
        status: (r.valueOv || r.sizeOv) ? "User override" : `Angela · ${r.confidence || "Medium"}`,
      }));
      lotTotals = { total_value: n(le.totalValue), total_build_cost: n(le.totalBuildCost), total_profit: n(vp ? vp.projectProfit : null), count: le.count };
    } else {
      lotRows = le.rows.map((r) => ({
        id: r.id, size: n(r.size), frontage: n(r.frontage), value: n(r.value), target: n(r.target), cost: n(r.cost),
        status: (r.valueOv || r.sizeOv) ? "User override" : `Angela · ${r.confidence || "Medium"}`,
      }));
      lotTotals = { total_value: n(le.totalValue), total_target: n(le.totalTarget), total_cost: n(le.totalLotCost), count: le.count };
    }
  }
  // JV economics (identical to the Exit Analysis) — hidden for a straight land flip.
  let jv = null;
  if (key !== "land_flip") {
    const split = { ...JV_SPLIT_DEFAULT, ...(record.jv_split || {}) };
    const j = jvEconomics(e, split, record.strategies?.[key]?.arqitek_cash, s.totalCost);
    jv = {
      project_profit: n(j.projectProfit), landowner: n(j.landowner), investor: n(j.investor), arqitek: n(j.arqitek),
      arqitek_cash: n(j.arqitekCash), arqitek_roi: (j.arqitekRoi == null || isNaN(j.arqitekRoi)) ? null : Math.round(j.arqitekRoi * 1000) / 1000,
      split: { landowner: split.landowner || 0, investor: split.investor || 0, arqitek: split.arqitek || 0 },
    };
  }
  const cost_lines = (s.costLines || []).map((c) => ({ label: c.label, value: n(c.value) }));
  const extras = (s.extras || []).map((x) => ({ label: x.label, value: x.value }));
  const profit = n(e.profit);
  const marginPct = (e.margin == null || isNaN(e.margin)) ? null : Math.round(e.margin * 10) / 10;
  const roi = (e.roi == null || isNaN(e.roi)) ? null : Math.round(e.roi * 1000) / 1000;
  const status_label = (profit != null && profit > 0) ? ((marginPct != null && marginPct >= 10) ? "Viable" : "Thin margin") : "Not viable";
  // Concise, strategy-specific Angela insight — derived only from this strategy's numbers.
  const $ = (v) => (v == null ? "—" : "$" + Math.round(v).toLocaleString());
  const pc = (v) => (v == null ? "—" : Math.round(v * 100) + "%");
  const mg = marginPct == null ? "—" : Math.round(marginPct) + "%";
  let insight_text = "";
  const yieldN = lots || 1;
  if (key === "land_flip") {
    insight_text = `Put the parcel under contract near ${$(e.landBasis)} and assign to an end buyer around ${$(e.exitValue)} for roughly ${$(profit)} profit (${mg} margin, ${pc(roi)} ROI) in about ${e.timelineMonths || "—"} months — with only ~${$(e.totalCapital)} of capital at risk.`;
  } else if (key === "entitle") {
    insight_text = `${yieldN > 1 ? yieldN + " Pad-Ready lots produce" : "Pad-Ready entitlement produces"} a combined target exit of ${$(e.exitValue)} against ${$(e.totalCapital)} of development capital — roughly ${$(profit)} projected profit at ${mg} margin and ${pc(roi)} ROI over ~${e.timelineMonths || "—"} months.`;
  } else if (key === "subdivide") {
    insight_text = `${yieldN > 1 ? yieldN + " finished Build-Ready lots carry" : "Build-Ready development carries"} a combined exit of ${$(e.exitValue)} on ${$(e.totalCapital)} all-in — about ${$(profit)} projected profit (${mg} margin, ${pc(roi)} ROI) over ~${e.timelineMonths || "—"} months.`;
  } else {
    insight_text = `${yieldN > 1 ? yieldN + " homes" : "A single home"} with total GDV of ${$(e.exitValue)} and ${$(e.totalCapital)} all-in — roughly ${$(profit)} projected profit at ${mg} margin and ${pc(roi)} ROI over ~${e.timelineMonths || "—"} months.`;
  }
  return {
    key, label: e.label, description: SNAP_DESC[key] || "", emphasis: SNAP_EMPHASIS[key] || [],
    exit_value: n(e.exitValue),
    revenue_label: s.revenueLabel || "Exit Value",
    land_basis: n(e.landBasis),
    capital_required: n(e.totalCapital),
    total_cost: n(s.totalCost),
    required_profit: n(s.requiredProfit),
    profit, margin_pct: marginPct, roi,
    equity_multiple: (e.equityMultiple == null || isNaN(e.equityMultiple)) ? null : Math.round(e.equityMultiple * 100) / 100,
    timeline_months: e.timelineMonths || null,
    status_label, risk: e.risk || null,
    lots, homes, lot_rows: lotRows, lot_totals: lotTotals,
    project: vp ? {
      lots: vp.lots, homes: vp.homes, avg_home_size: vp.avgHomeSize, total_buildable_sf: vp.totalBuildableSf,
      total_gdv: n(vp.totalGDV), total_construction: n(vp.totalConstruction), project_all_in: n(vp.projectAllIn),
      project_profit: n(vp.projectProfit), project_margin: vp.projectMargin == null ? null : Math.round(vp.projectMargin * 10) / 10,
      project_max_land: n(vp.projectMaxLand),
    } : null,
    cost_lines, extras, jv, insight_text,
  };
}

function buildExitSnapshot(record) {
  const strategies = STRATEGY_LADDER.map((k) => snapStrategy(record, k));
  const pickBest = (arr, val, dir) => {
    let best = null;
    arr.forEach((s) => {
      const v = val(s);
      if (v == null || isNaN(v)) return;
      if (best == null || dir * (v - val(best)) > 0) best = s;
    });
    return best;
  };
  const hp = pickBest(strategies, (s) => s.profit, 1);
  const hr = pickBest(strategies, (s) => s.roi, 1);
  const lc = pickBest(strategies.filter((s) => s.capital_required > 0), (s) => s.capital_required, -1);
  const se = pickBest(strategies.filter((s) => s.timeline_months > 0), (s) => s.timeline_months, -1);
  const ppm = (s) => (s.profit != null && s.timeline_months > 0 ? s.profit / s.timeline_months : null);
  const bra = pickBest(strategies.filter((s) => s.profit > 0), ppm, 1);
  const fmt$ = (v) => (v == null ? "—" : "$" + Math.round(v).toLocaleString());
  let text = "";
  if (hp && bra && hp.key !== bra.key) {
    text = `${hp.label} produces the highest total profit (${fmt$(hp.profit)}) but requires the most capital (${fmt$(hp.capital_required)}) over ${hp.timeline_months || "—"} months. ${bra.label} returns a strong ${bra.roi != null ? Math.round(bra.roi * 100) + "%" : "—"} ROI with ${lc && lc.key === bra.key ? "the least capital" : "substantially less capital"} and a ${se && se.key === bra.key ? "shorter" : "competitive"} timeline — a more capital-efficient, risk-adjusted exit.`;
  } else if (hp) {
    text = `${hp.label} is the strongest exit on the current analysis — ${fmt$(hp.profit)} projected profit at ${hp.margin_pct != null ? Math.round(hp.margin_pct) + "%" : "—"} margin.`;
  }
  return {
    strategies,
    primary_strategy: record.primary_strategy && STRATEGY_KEYS.includes(record.primary_strategy) ? record.primary_strategy : null,
    potential_lots: resolveLots(record).count,
    subdivision_active: resolveLots(record).active,
    insight: {
      highest_profit: hp ? { key: hp.key, label: hp.label, value: hp.profit } : null,
      highest_roi: hr ? { key: hr.key, label: hr.label, value: hr.roi } : null,
      lowest_capital: lc ? { key: lc.key, label: lc.label, value: lc.capital_required } : null,
      shortest_exit: se ? { key: se.key, label: se.label, value: se.timeline_months } : null,
      best_risk_adjusted: bra ? { key: bra.key, label: bra.label } : null,
      text,
    },
    generated_at: new Date().toISOString(),
  };
}

// Comparable-driven land valuation shared by the Land Comparables Summary. The estimate is
// the "true price" of a comparable parcel: Land Price/SF = total comp price ÷ total comp size
// (blended rate), and Estimated Land Value = that rate × the comps' AVERAGE lot size, which
// equals the mean comp sale price. The subject's own (often much larger) lot size is NOT
// extrapolated — doing so overstated value. Returns { est, ppsf, note } or null.
function landValueFromComps(pairs, prices, subjSqft) {
  const fmt$ = (v) => "$" + Math.round(v).toLocaleString();
  if (pairs.length) {
    const totalPrice = pairs.reduce((a, p) => a + p.price, 0);
    const totalSize = pairs.reduce((a, p) => a + p.sqft, 0);
    const avgSize = totalSize / pairs.length;
    const ppsf = totalSize ? totalPrice / totalSize : null;
    const est = Math.round(totalPrice / pairs.length); // = ppsf × avgSize = mean comp price
    const ppsfVals = pairs.map((p) => p.price / p.sqft);
    const rng = ppsfVals.length > 1 ? ` Comp $/sf $${Math.min(...ppsfVals).toFixed(2)}–$${Math.max(...ppsfVals).toFixed(2)}.` : "";
    const acres = avgSize / 43560;
    return { est, ppsf: ppsf != null ? Math.round(ppsf * 100) / 100 : null, note: `Comp average: mean lot ${Math.round(avgSize).toLocaleString()} sf (${acres.toFixed(2)} ac) at $${ppsf ? ppsf.toFixed(2) : "—"}/sf = ${fmt$(est)} (${pairs.length} comps).${rng}` };
  }
  if (prices.length) {
    const avg = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
    return { est: avg, ppsf: null, note: `Average of ${prices.length} comp sale price${prices.length !== 1 ? "s" : ""} (lot sizes unavailable).` };
  }
  return null;
}

// Development Exit Analysis — compares Land Flip → Pad-Ready → Build-Ready → Vertical Build
// side by side, with incremental return, JV economics, value creation and decision flags.

function LotField({ label, value, onSave, money, hint, tid }) {
  return (
    <div className="rounded-md border border-border/50 bg-background/40 px-2.5 py-1.5">
      <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-0.5">{label}</label>
      <div className="flex items-center">
        {money && <span className="text-xs font-semibold text-muted-foreground">$</span>}
        <input key={`${tid}-${value}`} defaultValue={value != null ? String(value) : ""} inputMode="decimal"
          onBlur={(e) => onSave(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
          className="w-full bg-transparent text-xs font-semibold text-foreground outline-none min-w-0" data-testid={tid} />
      </div>
      {hint && <div className="text-[9px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}
function LotFieldText({ label, value, onSave, tid }) {
  return (
    <div className="rounded-md border border-border/50 bg-background/40 px-2.5 py-1.5">
      <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-0.5">{label}</label>
      <input key={`${tid}-${value}`} defaultValue={value || ""}
        onBlur={(e) => onSave(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        className="w-full bg-transparent text-xs font-semibold text-foreground outline-none min-w-0" data-testid={tid} />
    </div>
  );
}
function LotSummTile({ label, value, cls, tid }) {
  return (
    <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-sm font-bold ${cls || "text-foreground"}`} data-testid={tid}>{value}</div>
    </div>
  );
}

// Lot-Level Economics — per-lot editable breakdown shown when Potential Lots > 1.
function LotEconomics({ econ, onSaveLot, money }) {
  const [open, setOpen] = useState(true);
  if (!econ || !econ.rows || econ.rows.length <= 1) return null;
  const type = econ.type;
  const fmtAc = (sf) => (sf ? `${(sf / 43560).toFixed(2)} ac` : "—");
  const valLabel = type === "build" ? "Build-Ready Value" : "Pad-Ready Value";
  const valField = type === "build" ? "build_value" : "pad_value";
  const tgtField = type === "build" ? "build_target" : "pad_target";
  const costField = type === "build" ? "build_cost" : "pad_cost";
  return (
    <div className="rounded-lg border border-primary/30 bg-background/40 p-4 mb-4" data-testid={`lot-economics-${type}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center justify-between w-full" data-testid={`lot-economics-toggle-${type}`}>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
          <Split className="h-3.5 w-3.5" />Lot-Level Economics <span className="text-muted-foreground normal-case font-normal">· {econ.count} proposed lots</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="space-y-2 mt-3">
            {econ.rows.map((r) => (
              <div key={r.id} className="rounded-md border border-border/60 bg-secondary/20 p-3" data-testid={`lot-row-${type}-${r.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-foreground">Lot {r.id}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${r.valueOv || r.sizeOv ? "border-primary/40 text-primary bg-primary/10" : "border-border/60 text-muted-foreground"}`} data-testid={`lot-conf-${type}-${r.id}`}>
                    {r.valueOv || r.sizeOv ? "User override" : `Angela · ${r.confidence}`}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <LotField label="Lot Size (SF)" tid={`lot-size-${type}-${r.id}`} value={r.size} hint={fmtAc(r.size)} onSave={(v) => onSaveLot(r.id, "size_sqft", v)} />
                  <LotField label="Frontage (ft)" tid={`lot-front-${type}-${r.id}`} value={r.frontage} onSave={(v) => onSaveLot(r.id, "frontage", v)} />
                  {type !== "vertical" ? (
                    <>
                      <LotField label={valLabel} money tid={`lot-value-${type}-${r.id}`} value={r.value} onSave={(v) => onSaveLot(r.id, valField, v)} />
                      <LotField label="Target Sale Price" money tid={`lot-target-${type}-${r.id}`} value={r.target} onSave={(v) => onSaveLot(r.id, tgtField, v)} />
                      <LotField label="Lot Cost" money tid={`lot-cost-${type}-${r.id}`} value={r.cost} onSave={(v) => onSaveLot(r.id, costField, v)} />
                    </>
                  ) : (
                    <>
                      <LotFieldText label="Home / Product" tid={`lot-product-${r.id}`} value={r.product} onSave={(v) => onSaveLot(r.id, "home_product", v)} />
                      <LotField label="Home SF" tid={`lot-homesf-${r.id}`} value={r.homeSqft} onSave={(v) => onSaveLot(r.id, "home_sqft", v)} />
                      <LotField label="Build Cost" money tid={`lot-buildcost-${r.id}`} value={r.buildCost} onSave={(v) => onSaveLot(r.id, "vertical_cost", v)} />
                      <LotField label="Finished Value" money tid={`lot-finished-${r.id}`} value={r.finishedValue} onSave={(v) => onSaveLot(r.id, "finished_value", v)} />
                      <LotField label="Target Sale Price" money tid={`lot-vtarget-${r.id}`} value={r.target} onSave={(v) => onSaveLot(r.id, "finished_target", v)} />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
            {type !== "vertical" ? (
              <>
                <LotSummTile label={`Total ${type === "build" ? "Build-Ready" : "Pad-Ready"} Lot Value`} value={money(econ.totalValue)} cls="text-primary" tid={`lot-total-value-${type}`} />
                <LotSummTile label="Total Target Sale" value={money(econ.totalTarget)} tid={`lot-total-target-${type}`} />
                <LotSummTile label="Resulting Lots" value={String(econ.count)} tid={`lot-total-count-${type}`} />
                {econ.avgCostPerLot != null && (
                  <LotSummTile label={`Avg ${type === "build" ? "Build-Ready" : "Pad-Ready"} Cost / Lot`} value={money(econ.avgCostPerLot)} tid={`lot-avg-cost-${type}`} />
                )}
              </>
            ) : (
              <>
                <LotSummTile label="Total Homes" value={String(econ.count)} tid="lot-total-homes" />
                <LotSummTile label="Total Vertical Build Cost" value={money(econ.totalBuildCost)} tid="lot-total-buildcost" />
                <LotSummTile label="Total GDV" value={money(econ.totalValue)} cls="text-primary" tid="lot-total-gdv" />
              </>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3 leading-snug">
            Angela splits the parcel evenly across {econ.count} lots as a starting point — edit any lot's size, value or target sale and Arqitek recalculates the strategy from the sum of the lots. Lot identity (Lot 1–{econ.count}) persists across Pad-Ready, Build-Ready and Vertical Build.
          </p>
        </>
      )}
    </div>
  );
}

// Compact parcel → lots → homes value-creation ribbon (Potential Lots > 1).
function SubdivisionSummary({ record, money }) {
  const info = resolveLots(record);
  if (!info.active) return null;
  const pad = computeStrategy(record, "entitle");
  const build = computeStrategy(record, "subdivide");
  const vert = computeStrategy(record, "build_sfr");
  const padVal = pad.lotEconomics ? pad.lotEconomics.totalValue : (pad.valueCreation ? pad.valueCreation.newMarketValue : 0);
  const buildVal = build.lotEconomics ? build.lotEconomics.totalValue : (build.valueCreation ? build.valueCreation.newMarketValue : 0);
  const gdv = vert.lotEconomics ? vert.lotEconomics.totalValue : (Number(vert.revenue) || 0) * info.count;
  const rawParcel = (pad.valueCreation && pad.valueCreation.landBasis) || computeLandOffer(record).marketValue || Number(record.land_estimated_land_value) || 0;
  const stages = [
    { label: "1 Raw Parcel", sub: "today", val: money(rawParcel) },
    { label: `${info.count} Pad-Ready Lots`, sub: "entitled", val: money(padVal) },
    { label: `${info.count} Build-Ready Lots`, sub: "finished lots", val: money(buildVal) },
    { label: `${info.count} Potential Homes`, sub: "vertical GDV", val: money(gdv) },
  ];
  return (
    <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 mb-4" data-testid="subdivision-summary">
      <div className="flex items-center gap-1.5 mb-3">
        <Split className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs font-semibold uppercase tracking-wide text-primary">Subdivision Value Creation</span>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-stretch gap-2">
        {stages.map((st, i) => (
          <Fragment key={i}>
            <div className="flex-1 rounded-md border border-border/60 bg-background/40 px-3 py-2" data-testid={`subdiv-stage-${i}`}>
              <div className="text-xs font-semibold text-foreground">{st.label}</div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{st.sub}</div>
              <div className="text-sm font-bold text-primary mt-1">{st.val}</div>
            </div>
            {i < stages.length - 1 && (
              <div className="hidden sm:flex items-center justify-center text-muted-foreground"><ArrowRight className="h-4 w-4" /></div>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

// Development Strategy panel — four independent scenarios + Primary selection.
function StrategyModule({ record, resource, id, onSaved, openSignal, onSaveField, onStrategyChange }) {
  const primaryKey = record.primary_strategy && STRATEGY_KEYS.includes(record.primary_strategy) ? record.primary_strategy : null;
  const [selKey, setSelKey] = useState(primaryKey || "build_sfr");
  const effectiveKey = STRATEGY_KEYS.includes(selKey) ? selKey : (primaryKey || "build_sfr");
  const [showAssumptions, setShowAssumptions] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  useEffect(() => {
    if (openSignal) {
      setCollapsed(false);
      const el = document.getElementById("strategy-anchor");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [openSignal]);
  // Surface the currently-selected strategy so the Property Snapshot header can mirror it.
  useEffect(() => { if (onStrategyChange) onStrategyChange(effectiveKey); }, [effectiveKey, onStrategyChange]);
  const pickStrategy = (k) => { setSelKey(k); setCollapsed(false); };
  const s = computeStrategy(record, effectiveKey);
  const lotsInfo = resolveLots(record);
  const ex = enrichStrategy(record, effectiveKey);
  const money = (v) => (v == null || isNaN(v) ? "—" : formatCurrency(Math.round(v)));
  const pct = (v) => (v == null || isNaN(v) ? "—" : `${(v * 100).toFixed(0)}%`);
  const xx = (v) => (v == null || isNaN(v) ? "—" : `${v.toFixed(2)}×`);
  const isPrimary = primaryKey === effectiveKey;
  const split = { ...JV_SPLIT_DEFAULT, ...(record.jv_split || {}) };
  const jv = jvEconomics(ex, split, record.strategies?.[effectiveKey]?.arqitek_cash, s.totalCost);
  const ladderIdx = STRATEGY_LADDER.indexOf(effectiveKey);
  const prior = ladderIdx > 0 ? enrichStrategy(record, STRATEGY_LADDER[ladderIdx - 1]) : null;
  const inc = prior ? {
    fromLabel: prior.label,
    invDelta: (ex.totalCapital || 0) - (prior.totalCapital || 0),
    profitDelta: (ex.profit || 0) - (prior.profit || 0),
    timeDelta: (ex.timelineMonths || 0) - (prior.timelineMonths || 0),
    ret: ((ex.totalCapital || 0) - (prior.totalCapital || 0)) > 0 ? ((ex.profit || 0) - (prior.profit || 0)) / ((ex.totalCapital || 0) - (prior.totalCapital || 0)) : null,
  } : null;

  const saveAssumption = async (field, raw) => {
    const cleaned = String(raw).replace(/[^0-9.-]/g, "");
    const val = cleaned === "" ? null : Number(cleaned);
    // Lot count is a SINGLE parcel-wide value shared by all stages (Pad / Build / Vertical).
    // Persist it to potential_lots and clear any per-strategy lot overrides, so lowering it on
    // one stage isn't overridden by another stage's stale value (resolveLots takes the max
    // across stored lot counts). All stages read resolveLots().count, so they stay in sync.
    if (field === "lots") {
      const strategies = { ...(record.strategies || {}) };
      ["entitle", "subdivide", "build_sfr"].forEach((k) => {
        if (strategies[k] && strategies[k].lots != null) {
          const c = { ...strategies[k] }; delete c.lots; strategies[k] = c;
        }
      });
      try { const updated = await updateItem(resource, id, { potential_lots: val, strategies }); onSaved(updated); }
      catch (e) { toast.error(apiError(e)); }
      return;
    }
    const strategies = { ...(record.strategies || {}) };
    const cur = { ...(strategies[effectiveKey] || {}) };
    if ((cur[field] ?? null) === (val ?? null)) return;
    cur[field] = val;
    strategies[effectiveKey] = cur;
    try { const updated = await updateItem(resource, id, { strategies }); onSaved(updated); }
    catch (e) { toast.error(apiError(e)); }
  };
  // Persist a per-lot override under record.lot_economics (additive; lot identity persists
  // across Pad-Ready / Build-Ready / Vertical Build).
  const saveLotField = async (lotId, field, raw) => {
    const isText = field === "home_product";
    const cleaned = isText ? String(raw) : String(raw).replace(/[^0-9.-]/g, "");
    const val = cleaned === "" ? null : (isText ? cleaned : Number(cleaned));
    const le = { ...(record.lot_economics || {}) };
    const lots = { ...(le.lots || {}) };
    const cur = { ...(lots[lotId] || {}) };
    if ((cur[field] ?? null) === (val ?? null)) return;
    cur[field] = val;
    lots[lotId] = cur;
    le.lots = lots;
    try { const updated = await updateItem(resource, id, { lot_economics: le }); onSaved(updated); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setPrimary = async (key) => {
    try { const updated = await updateItem(resource, id, { primary_strategy: key }); onSaved(updated); toast.success(`${STRATEGY_META[key].label} set as Primary Strategy`); }
    catch (e) { toast.error(apiError(e)); }
  };
  // Arqitek is always the remainder so the three splits always total 100%.
  const saveSplit = async (field, raw) => {
    if (field === "arqitek") return; // computed, not directly editable
    let val = Number(String(raw).replace(/[^0-9.]/g, "")) || 0;
    if (val < 0) val = 0;
    const cur = { ...JV_SPLIT_DEFAULT, ...(record.jv_split || {}) };
    const other = field === "landowner" ? (cur.investor || 0) : (cur.landowner || 0);
    if (val + other > 100) val = Math.max(0, 100 - other); // cap so total never exceeds 100
    const next = { ...cur, [field]: val };
    next.arqitek = Math.max(0, 100 - (next.landowner || 0) - (next.investor || 0));
    try { onSaved(await updateItem(resource, id, { jv_split: next })); }
    catch (e) { toast.error(apiError(e)); }
  };
  // Save a cost override (status 'user', or 'verified'). Angela never overwrites these.
  // Stamp the lot count this override was made at, so we can flag a scope change later.
  const saveCost = async (field, raw, status) => {
    const cleaned = String(raw).replace(/[^0-9.-]/g, "");
    const val = cleaned === "" ? null : Number(cleaned);
    const strategies = { ...(record.strategies || {}) };
    const cur = { ...(strategies[effectiveKey] || {}) };
    const statusMap = { ...(cur._status || {}) };
    cur[field] = val;
    if (status) statusMap[field] = status; else delete statusMap[field];
    cur._status = statusMap;
    cur.cost_basis_lots = (s.valueCreation && s.valueCreation.lots) || resolveLots(record).count || 1;
    strategies[effectiveKey] = cur;
    try { onSaved(await updateItem(resource, id, { strategies })); toast.success(status === "verified" ? "Marked as verified / actual" : "Estimate updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const revertCost = async (field) => {
    const strategies = { ...(record.strategies || {}) };
    const cur = { ...(strategies[effectiveKey] || {}) };
    delete cur[field];
    if (cur._status) { const sm = { ...cur._status }; delete sm[field]; cur._status = sm; }
    strategies[effectiveKey] = cur;
    try { onSaved(await updateItem(resource, id, { strategies })); toast.success("Reverted to Angela estimate"); }
    catch (e) { toast.error(apiError(e)); }
  };
  // Clear this strategy's cost-line overrides so Angela re-Costimates the whole project for
  // the current lot yield. User-initiated (never silent) — genuine overrides stay until click.
  const reCostimate = async (fields, toLots) => {
    const strategies = { ...(record.strategies || {}) };
    const cur = { ...(strategies[effectiveKey] || {}) };
    const sm = { ...(cur._status || {}) };
    (fields || []).forEach((f) => { delete cur[f]; delete sm[f]; });
    cur._status = sm;
    cur.cost_basis_lots = toLots;
    strategies[effectiveKey] = cur;
    try { onSaved(await updateItem(resource, id, { strategies })); toast.success(`Re-Costimated for ${toLots} lot${toLots === 1 ? "" : "s"}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const lastCalc = record.updated_at ? formatDate(record.updated_at) : new Date().toLocaleDateString();

  return (
    <div id="strategy-anchor" className="rounded-lg border border-border bg-secondary/30 overflow-hidden" data-testid="strategy-module">
      <div onClick={() => setCollapsed((c) => !c)} className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border cursor-pointer hover:bg-accent/40 transition-colors" title={collapsed ? "Expand" : "Collapse"} data-testid="strategy-collapse-btn">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <h3 className="text-sm font-semibold text-foreground shrink-0">Exit Analysis</h3>
          {primaryKey && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30 shrink-0" data-testid="strategy-primary-label">
              <CheckCircle2 className="h-3 w-3" />Primary · {STRATEGY_META[primaryKey].label}
            </span>
          )}
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${collapsed ? "" : "rotate-180"}`} />
      </div>
      {!collapsed && (
      <div className="p-4">
      <div className="flex items-center gap-1.5 flex-wrap mb-4">
        {STRATEGY_KEYS.map((k) => {
          const meta = STRATEGY_META[k]; const Icon = meta.icon;
          const active = effectiveKey === k; const prim = primaryKey === k;
          return (
            <button key={k} type="button" onClick={() => pickStrategy(k)} data-testid={`strategy-cta-${k}`}
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border transition-colors ${active ? "bg-primary/15 border-primary/40 text-primary" : prim ? "border-primary/50 text-primary hover:bg-primary/10" : "border-border/60 text-muted-foreground hover:bg-accent/40 hover:text-foreground"}`}>
              <Icon className="h-3.5 w-3.5" />{meta.label}
              {prim && <CheckCircle2 className="h-3 w-3 text-primary" />}
            </button>
          );
        })}
      </div>
      <p className="text-[13px] text-muted-foreground mb-4" data-testid="strategy-question">{s.question}</p>
      {s.note && <p className="text-[11px] text-muted-foreground -mt-3 mb-4 inline-flex items-center gap-1" data-testid="strategy-lot-note"><Ruler className="h-3 w-3" />{s.note}</p>}
      {lotsInfo.conflict && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 mb-4 flex items-start gap-2 text-[11px]" data-testid="yield-conflict">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
          <span className="text-amber-200/90">Yield conflict — you entered <b>{lotsInfo.entered}</b> potential lots, but Angela's {lotsInfo.verifiedSource} supports about <b>{lotsInfo.verified}</b>. You can still model the {lotsInfo.entered}-lot scenario; confirm the supported yield with the jurisdiction.</span>
        </div>
      )}
      {lotsInfo.active && <SubdivisionSummary record={record} money={money} />}
      {s.valueCreation && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 mb-4" data-testid="padready-value-creation">
          <div className="flex items-center gap-1.5 mb-3">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">{s.valueCreation.name} Value Creation</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="rounded-md border border-border/50 bg-background/30 p-3">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">Cost Basis</div>
              <div className="space-y-1.5">
                <div className="flex justify-between"><span className="text-muted-foreground">Land Basis <span className="text-[10px]">(contract / land cost)</span></span><span className="font-medium" data-testid="pr-land-basis">{money(s.valueCreation.landBasis)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Cost to Get {s.valueCreation.name}</span><span className="font-medium" data-testid="pr-cost">{money(s.valueCreation.padReadyCosts)}</span></div>
                <div className="flex justify-between border-t border-border/40 pt-1.5"><span className="text-muted-foreground">Total {s.valueCreation.name} Basis</span><span className="font-semibold" data-testid="pr-total-basis">{money(s.valueCreation.totalBasis)}</span></div>
              </div>
            </div>
            <div className="rounded-md border border-border/50 bg-background/30 p-3">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">Value Created</div>
              <div className="space-y-1.5">
                <div className="flex justify-between"><span className="text-muted-foreground">Starting Land Basis <span className="text-[10px]">(what you pay)</span></span><span className="font-medium" data-testid="pr-raw-land">{money(s.valueCreation.rawLandValue)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Value Add from Work <span className="text-[10px]">(sale − land basis)</span></span><span className="font-medium" data-testid="pr-value-add">{money(s.valueCreation.workValueAdd)}</span></div>
                <div className="flex justify-between border-t border-border/40 pt-1.5"><span className="text-muted-foreground">New {s.valueCreation.name} Market Value</span><span className="font-semibold text-primary" data-testid="pr-market-value">{money(s.valueCreation.newMarketValue)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Target Sale Price</span><span className="font-medium" data-testid="pr-target-sale">{money(s.valueCreation.targetSale)}</span></div>
              </div>
            </div>
          </div>
          <div className="text-sm mt-3">
            <div className="flex justify-between rounded-md border border-border/50 bg-background/30 px-3 py-2"><span className="text-muted-foreground">Projected Profit <span className="text-[10px]">(sale − total {s.valueCreation.name.toLowerCase()} basis)</span></span><span className={`font-bold ${s.valueCreation.projectedProfit != null && s.valueCreation.projectedProfit >= 0 ? "text-emerald-400" : "text-destructive"}`} data-testid="pr-profit">{money(s.valueCreation.projectedProfit)}{s.valueCreation.margin != null ? ` · ${s.valueCreation.margin.toFixed(0)}%` : ""}</span></div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3 leading-snug">
            {s.valueCreation.name} creates a new, higher-value asset. Angela estimates the entitlement uplift at <b className="text-foreground">{s.valueCreation.upliftPct}%</b> over the land basis{s.valueCreation.finishedLotSet ? " (using your entered buildable-lot value)" : s.valueCreation.compCount > 0 ? ` (sanity-checked against ${s.valueCreation.compCount} land comp${s.valueCreation.compCount === 1 ? "" : "s"})` : ""} — creating <b className="text-primary">{money(s.valueCreation.valueCreated)}</b> of gross value. Edit the uplift, market value or target sale under <b>Edit assumptions</b>.
          </p>
        </div>
      )}
      {s.recommendation && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 mb-4" data-testid="flip-recommendation">
          <div className="flex items-center gap-1.5 mb-3">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">Angela's Contract Flip Recommendation</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="rounded-md border border-border/60 bg-background/40 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Contract At (or below)</div>
              <div className="text-base font-bold text-primary" data-testid="flip-buy">{money(s.recommendation.buy)}</div>
              {s.recommendation.discountPct != null && <div className="text-[10px] text-muted-foreground">{s.recommendation.discountPct}% below market</div>}
            </div>
            <div className="rounded-md border border-border/60 bg-background/40 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Assign / Resell At</div>
              <div className="text-base font-bold text-foreground" data-testid="flip-sell">{money(s.recommendation.sell)}</div>
              {s.recommendation.compCount > 0 && <div className="text-[10px] text-muted-foreground">from {s.recommendation.compCount} land comp{s.recommendation.compCount === 1 ? "" : "s"}</div>}
            </div>
            <div className="rounded-md border border-border/60 bg-background/40 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Assignment Profit</div>
              <div className={`text-base font-bold ${s.recommendation.profit != null && s.recommendation.profit >= 0 ? "text-emerald-400" : "text-destructive"}`} data-testid="flip-profit">{money(s.recommendation.profit)}</div>
              <div className="text-[10px] text-muted-foreground">{s.recommendation.roi != null ? `${s.recommendation.roi.toFixed(0)}% on ${money(s.recommendation.capitalAtRisk)} at risk` : "—"}</div>
            </div>
          </div>
          <p className="text-[12px] text-foreground/90 leading-snug" data-testid="flip-rec-text">
            {s.recommendation.sell && s.recommendation.buy != null ? (
              <>Angela: based on {s.recommendation.compCount > 0 ? `${s.recommendation.compCount} comparable land sale${s.recommendation.compCount === 1 ? "" : "s"}` : "available market data"}
              {s.recommendation.marketValue ? ` (~${money(s.recommendation.marketValue)} market value)` : ""}, put this parcel under contract at ~{s.recommendation.discountPct}% below market (<b className="text-primary">{money(s.recommendation.buy)}</b>) and assign the rights to an end buyer near <b>{money(s.recommendation.sell)}</b> for a <b className={s.recommendation.profit != null && s.recommendation.profit >= 0 ? "text-emerald-400" : "text-destructive"}>{money(s.recommendation.profit)}</b> assignment profit (spread {money(s.recommendation.spread)} less transaction costs). Arqitek never closes or owns the land — only ~{money(s.recommendation.capitalAtRisk)} of capital is at risk.</>
            ) : "Add land comparables so Angela can recommend a contract and assignment price."}
          </p>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StratTile label={s.revenueLabel} value={money(s.revenue)} tid="strategy-revenue" />
        <StratTile label={s.metricCostLabel || "Non-Land Costs"} value={money(s.totalCost)} tid="strategy-costs" />
        <StratTile label={s.metricProfitLabel || `Required Profit · ${s.profitPct}%`} value={money(s.metricProfitValue != null ? s.metricProfitValue : s.requiredProfit)} cls={s.metricProfitCls || "text-emerald-400"} tid="strategy-required-profit" />
        <StratTile label={s.metricLandLabel || "Max Land Price"} value={money(s.metricLandValue != null ? s.metricLandValue : s.maxLandBasis)} cls="text-primary" tid="strategy-max-land-basis" big />
      </div>
      {effectiveKey !== "build_sfr" && (
      <div className="rounded-md border border-border/60 bg-secondary/20 p-3 mb-4" data-testid="strategy-cost-lines">
        {s.costLines.some((c) => c.cm) && (
          <div className="flex items-center gap-1.5 mb-1.5 pb-1.5 border-b border-border/40">
            <Sparkles className="h-3 w-3 text-primary" />
            <span className="text-[10px] uppercase tracking-wide text-primary font-medium">Angela Costimate</span>
            <span className="text-[10px] text-muted-foreground">— click any value for basis & override</span>
          </div>
        )}
        {(() => {
          const stratLots = (s.valueCreation && s.valueCreation.lots) || 1;
          const stratObj = (record.strategies && record.strategies[effectiveKey]) || {};
          const costFields = s.costLines.filter((c) => c.field).map((c) => c.field);
          const hasCostOv = costFields.some((f) => stratObj[f] != null && stratObj[f] !== "");
          const basisLots = stratObj.cost_basis_lots != null ? Number(stratObj.cost_basis_lots) : null;
          const scopeChanged = hasCostOv && stratLots > 1 && basisLots !== stratLots;
          if (!scopeChanged) return null;
          return (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 mb-2 flex items-start gap-2" data-testid="cost-scope-changed">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-amber-200/90 leading-snug">
                  {basisLots != null
                    ? `These costs were estimated for a ${basisLots}-lot scope. Potential Lots is now ${stratLots}.`
                    : `Some costs were manually set and may not reflect the current ${stratLots}-lot subdivision scope.`}
                </p>
                <Button size="sm" className="h-7 text-xs mt-1.5" onClick={() => reCostimate(costFields, stratLots)} data-testid="recostimate-btn">
                  <Sparkles className="h-3 w-3 mr-1" />Re-Costimate for {stratLots} Lots
                </Button>
              </div>
            </div>
          );
        })()}
        {s.costLines.map((c, i) => (
          <CostLineRow key={`${effectiveKey}-${c.field || i}`} line={c}
            status={c.field ? costLineStatus(record, effectiveKey, c.field) : "angela"}
            money={money} lastCalc={lastCalc} onSave={saveCost} onRevert={revertCost} />
        ))}
        <div className="flex justify-between text-sm py-1 mt-1 border-t border-border/60 font-semibold">
          <span>Total Non-Land Cost</span><span data-testid="strategy-total-nonland">{money(s.totalCost)}</span>
        </div>
      </div>
      )}
      {effectiveKey === "build_sfr" && (
        <div className="rounded-lg border border-primary/30 bg-background/40 p-4 mb-4" data-testid="vertical-lot-summary">
          <div className="flex items-center gap-1.5 mb-3">
            <Split className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">Subdivision — Vertical Build</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StratTile label="Potential Lots" value={String(s.lotsCount || 1)} tid="vb-potential-lots" />
            <StratTile label="Planned Homes" value={String(s.lotsCount || 1)} tid="vb-planned-homes" />
            {s.vertProject ? (
              <>
                <StratTile label="Avg Home Size" value={`${(s.vertProject.avgHomeSize || 0).toLocaleString()} SF`} tid="vb-avg-home-size" />
                <StratTile label="Total Buildable SF" value={`${(s.vertProject.totalBuildableSf || 0).toLocaleString()} SF`} tid="vb-total-buildable" />
              </>
            ) : (
              <>
                <StratTile label="Home Size" value={s.extras[0] ? s.extras[0].value : "—"} tid="vb-home-size" />
                <StratTile label="Finished Value (GDV)" value={money(s.revenue)} tid="vb-finished-value" />
              </>
            )}
          </div>
          {s.vertProject && (
            <>
              <div className="text-[10px] uppercase tracking-wide text-primary font-medium mt-4 mb-2">Total Project · {s.vertProject.homes} Homes</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <StratTile label="Total GDV" value={money(s.vertProject.totalGDV)} cls="text-primary" tid="vb-total-gdv" />
                <StratTile label="Total Development Cost" value={money(s.vertProject.projectAllIn)} tid="vb-total-devcost" />
                <StratTile label="Project Profit" value={`${money(s.vertProject.projectProfit)}${s.vertProject.projectMargin != null ? ` · ${s.vertProject.projectMargin.toFixed(0)}%` : ""}`} cls={s.vertProject.projectProfit >= 0 ? "text-emerald-400" : "text-destructive"} tid="vb-project-profit" />
                <StratTile label="Project Max Land" value={money(s.vertProject.projectMaxLand)} tid="vb-project-maxland" />
              </div>
              <p className="text-[11px] text-muted-foreground mt-2 leading-snug">Project totals combine all {s.vertProject.homes} homes (land purchased once). The Underwriting Model and metrics below show the economics <b>per home</b>; edit individual homes in Lot-Level Economics.</p>
            </>
          )}
        </div>
      )}
      {effectiveKey === "build_sfr" && (
        <div className="mb-4" data-testid="strategy-underwriting">
          {s.vertProject && <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 inline-flex items-center gap-1"><span className="px-1.5 py-0.5 rounded-full border border-border/60">Per Home</span> unit economics</div>}
          <UnderwritingModel record={record} onSave={onSaveField} defaultOpen />
        </div>
      )}
      {s.lotEconomics && s.lotEconomics.rows && s.lotEconomics.rows.length > 1 && (
        <LotEconomics econ={s.lotEconomics} onSaveLot={saveLotField} money={money} />
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        {s.extras.map((e, i) => <StratTile key={i} label={e.label} value={typeof e.value === "number" ? e.value.toLocaleString() : e.value} tid={`strategy-extra-${i}`} />)}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
        <span className="text-muted-foreground">Projected Margin: <b className={s.margin != null && s.margin >= 10 ? "text-primary" : "text-destructive"}>{s.margin == null ? "—" : `${s.margin.toFixed(1)}%`}</b></span>
      </div>

      {/* Exit metrics for this strategy */}
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Exit Metrics</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="strategy-exit-metrics">
        <StratTile label="Estimated Profit" value={money(ex.profit)} cls="text-emerald-400" tid="strategy-exit-profit" />
        <StratTile label="Total Capital Required" value={money(ex.totalCapital)} tid="strategy-exit-capital" />
        <StratTile label="ROI" value={pct(ex.roi)} cls="text-primary" tid="strategy-roi" />
        <StratTile label="Equity Multiple" value={xx(ex.equityMultiple)} tid="strategy-equity-multiple" />
      </div>

      {/* JV economics for this strategy — N/A for a straight land flip (Arqitek buys from the seller) */}
      {effectiveKey !== "land_flip" && (
      <>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2" data-testid="strategy-jv">JV Economics — land contribution</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StratTile label="Project Profit" value={money(jv.projectProfit)} cls="text-emerald-400" tid="strategy-jv-project-profit" />
        {["landowner", "investor", "arqitek"].map((f) => (
          <div key={f} className="rounded-md border border-border/60 bg-secondary/20 px-3 py-2 h-full flex flex-col" data-testid={`strategy-jv-${f}`}>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground min-h-[26px] flex items-center gap-1">
              <span className="capitalize">{f}</span>
              <span className="inline-flex items-center text-muted-foreground">(
                {f === "arqitek" ? (
                  <span className="text-primary font-semibold px-0.5" data-testid={`strategy-split-inline-${f}`}>{split[f]}</span>
                ) : (
                  <input key={`jvp-${effectiveKey}-${f}-${split[f]}`} defaultValue={String(split[f])} inputMode="decimal"
                    onBlur={(e) => saveSplit(f, e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    className="w-7 bg-transparent text-center text-primary font-semibold outline-none border-b border-dashed border-primary/40 focus:border-primary"
                    data-testid={`strategy-split-inline-${f}`} />
                )}%)
              </span>
            </div>
            <div className="text-sm font-semibold mt-auto text-foreground" data-testid={`strategy-jv-${f}-value`}>{money(jv[f])}</div>
          </div>
        ))}
        <StratTile label="Arqitek Cash Req." value={money(jv.arqitekCash)} tid="strategy-jv-cash" />
        <StratTile label="Arqitek ROI" value={pct(jv.arqitekRoi)} cls="text-primary" tid="strategy-arqitek-roi" />
      </div>
      </>
      )}

      <button onClick={() => setShowAssumptions((v) => !v)} className="text-xs text-primary hover:underline" data-testid="strategy-assumptions-toggle">
        {showAssumptions ? "Hide assumptions" : "Edit assumptions"}
      </button>
      {showAssumptions && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3" data-testid="strategy-assumptions">
          {s.assumptions.map((a) => (
            <div key={a.field} className="rounded-md border border-border/60 bg-secondary/30 px-3 py-2">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 block">{a.label}</label>
              <div className="flex items-center">
                {a.money && <span className="text-sm font-semibold text-muted-foreground">$</span>}
                <input key={`${effectiveKey}-${a.field}-${a.value}`} defaultValue={a.value != null ? String(a.value) : ""} inputMode="decimal"
                  onBlur={(e) => saveAssumption(a.field, e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  className="w-full bg-transparent text-sm font-semibold text-foreground outline-none placeholder:text-muted-foreground"
                  data-testid={`strategy-input-${a.field}`} />
                {a.suffix && <span className="text-sm text-muted-foreground">{a.suffix}</span>}
              </div>
            </div>
          ))}
          <div className="rounded-md border border-border/60 bg-secondary/30 px-3 py-2">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 block">Timeline (months)</label>
            <input key={`tl-${effectiveKey}-${ex.timelineMonths}`} defaultValue={String(ex.timelineMonths)} inputMode="decimal"
              onBlur={(e) => saveAssumption("timeline_months", e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              className="w-full bg-transparent text-sm font-semibold text-foreground outline-none" data-testid="strategy-input-timeline_months" />
          </div>
          <div className="rounded-md border border-border/60 bg-secondary/30 px-3 py-2">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 block">Arqitek Cash Required</label>
            <div className="flex items-center"><span className="text-sm font-semibold text-muted-foreground">$</span>
              <input key={`ac-${effectiveKey}-${record.strategies?.[effectiveKey]?.arqitek_cash ?? ""}`} defaultValue={record.strategies?.[effectiveKey]?.arqitek_cash != null ? String(record.strategies[effectiveKey].arqitek_cash) : ""} placeholder={String(Math.round(s.totalCost || 0))} inputMode="decimal"
                onBlur={(e) => saveAssumption("arqitek_cash", e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                className="w-full bg-transparent text-sm font-semibold text-foreground outline-none placeholder:text-muted-foreground" data-testid="strategy-input-arqitek_cash" />
            </div>
          </div>
          {["landowner", "investor", "arqitek"].map((f) => (
            <div key={`split-${f}`} className="rounded-md border border-border/60 bg-secondary/30 px-3 py-2">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 block">JV {f} %{f === "arqitek" ? " (auto)" : ""}</label>
              {f === "arqitek" ? (
                <div className="text-sm font-semibold text-foreground" data-testid={`strategy-split-${f}`}>{split[f]}</div>
              ) : (
                <input key={`sp-${f}-${split[f]}`} defaultValue={String(split[f])} inputMode="decimal"
                  onBlur={(e) => saveSplit(f, e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  className="w-full bg-transparent text-sm font-semibold text-foreground outline-none" data-testid={`strategy-split-${f}`} />
              )}
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Button onClick={() => setPrimary(effectiveKey)} disabled={isPrimary} variant="outline" data-testid="strategy-set-primary">
          {isPrimary ? <><CheckCircle2 className="h-4 w-4 mr-1.5" />Primary Strategy</> : <><Target className="h-4 w-4 mr-1.5" />Set as Primary Strategy</>}
        </Button>
        {isPrimary && <span className="text-xs text-muted-foreground">Driving the Property Snapshot, Offer &amp; Acquisition economics.</span>}
      </div>
      </div>
      )}
    </div>
  );
}

function LandOfferModule({ record, resource, id, onSaved, openSignal }) {
  const [collapsed, setCollapsed] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const num = landNum;
  const money = (v) => (v == null ? "—" : formatCurrency(Math.round(v)));

  useEffect(() => {
    if (openSignal) {
      setCollapsed(false);
      const el = document.getElementById("land-offer-anchor");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [openSignal]);

  // Persist the engine's computed story so the investor portal shows identical numbers.
  const lastSummaryRef = useRef(null);
  useEffect(() => {
    const c = computeLandOffer(record);
    const summary = {
      avg_comp: c.avgVal, true_median: c.medianVal, adjusted_market: c.adjustedMarket,
      market_value: c.marketValue, max_supported: c.absoluteMax,
      best_strategy: c.best ? c.best.label : null,
      strategies: c.strategies.filter((s) => s.val != null).map((s) => ({ label: s.label, value: s.val })),
      opening: c.opening, target_high: c.targetHigh, actual_offer: c.actualOffer,
      max_offer: c.maxOffer, asking: c.asking, req_pct: c.reqPct,
      comps_count: c.comps.filter((x) => x.price != null).length,
    };
    const json = JSON.stringify(summary);
    const existing = JSON.stringify(record.land_offer_summary || null);
    if (json !== existing && json !== lastSummaryRef.current) {
      lastSummaryRef.current = json;
      updateItem(resource, id, { land_offer_summary: summary }).then((u) => onSaved(u)).catch(() => {});
    }
  }, [record, resource, id, onSaved]);

  const save = async (key, raw) => {
    const cleaned = String(raw).replace(/[^0-9.-]/g, "");
    const val = cleaned === "" ? null : Number(cleaned);
    if (num(record[key]) === (val == null ? null : val)) return;
    try { const updated = await updateItem(resource, id, { [key]: val }); onSaved(updated); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveText = async (key, raw) => {
    const val = String(raw || "").trim();
    if ((record[key] || "") === val) return;
    try { const updated = await updateItem(resource, id, { [key]: val }); onSaved(updated); }
    catch (e) { toast.error(apiError(e)); }
  };

  const [angelaBusy, setAngelaBusy] = useState(false);
  const runAngelaAdjust = async () => {
    setAngelaBusy(true);
    try {
      const { data } = await api.post("/ai/adjust-land-value", { resource, record_id: id });
      onSaved({ ...record, land_adjusted_market_value: data.adjusted_market_land_value, land_value_adjustment_notes: data.rationale });
      toast.success("Angela set the adjusted land value");
    } catch (e) { toast.error(apiError(e)); }
    finally { setAngelaBusy(false); }
  };

  const EditNum = ({ label, k, testid }) => (
    <div className="rounded-md border border-border/60 bg-secondary/30 px-3 py-2">
      <label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 block">{label}</label>
      <input key={String(record[k] ?? "")} defaultValue={num(record[k]) != null ? String(num(record[k])) : ""} inputMode="decimal" onBlur={(e) => save(k, e.target.value)} placeholder="—" className="w-full bg-transparent text-sm font-semibold text-foreground outline-none placeholder:text-muted-foreground" data-testid={testid} />
    </div>
  );

  const { comps, avgVal, medianVal, marketValue, totalEntitled, gdv, reqPct, flipPct, openPct,
    nonLand, strategies, best, absoluteMax, actualOffer, maxOffer } = computeLandOffer(record);

  // Aligned to the investor portal: Max = 20% GDV − site work, Opening = 40% market land value.
  const siteWork = num(record.pad_site_work) ?? 0;
  const gdvForOffer = num(record.arv_market_value) ?? gdv;
  const maxLandOffer = gdvForOffer ? Math.round(0.20 * gdvForOffer - siteWork) : absoluteMax;
  const openingOffer = marketValue != null ? Math.round(0.40 * marketValue) : null;
  const targetOffer = (openingOffer != null && maxLandOffer != null && maxLandOffer > openingOffer)
    ? Math.round((openingOffer + maxLandOffer) / 2) : null;
  const overMaxAligned = maxLandOffer != null && actualOffer != null && actualOffer > maxLandOffer;

  // Max land offer each exit strategy can support (from each strategy's Max Land Basis).
  const sfr = computeStrategy(record, "build_sfr");
  const stratMax = {
    land_flip: computeStrategy(record, "land_flip").maxLandBasis,
    entitle: computeStrategy(record, "entitle").maxLandBasis,
    subdivide: computeStrategy(record, "subdivide").maxLandBasis,
    build_sfr: sfr.maxLandBasis,
  };
  // Over-Asking JV Max: max land CONTRIBUTION value under the 24-month JV. The land is
  // contributed (not bought for cash), so the project can bear land up to its break-even
  // ceiling = GDV − non-land costs = Vertical Build max land basis + the required-profit reserve.
  const overAskingJvMax = (sfr.maxLandBasis != null && sfr.requiredProfit != null)
    ? sfr.maxLandBasis + sfr.requiredProfit : null;
  const offerCards = [
    { k: "flip", label: "Flip Max Offer", desc: "Maximum contract price for your 90-day DD/assignment flip.", val: stratMax.land_flip },
    { k: "pad-ready", label: "Pad-Ready Max Offer", desc: "Maximum land value you can support if you take the property to Pad-Ready and sell it.", val: stratMax.entitle },
    { k: "build-ready", label: "Build-Ready Max Offer", desc: "Maximum land value you can support if you take it through horizontal development to Build-Ready and sell it.", val: stratMax.subdivide },
    { k: "vertical-build", label: "Vertical Build Max Offer", desc: "Maximum land value the full home-development strategy can support based on GDV, construction costs, development costs, and required profit.", val: stratMax.build_sfr },
    { k: "over-asking-jv", label: "Over-Asking JV Max", desc: "Maximum land contribution value you can offer the seller under your 24-month JV structure.", val: overAskingJvMax },
  ];
  const selectedCard = offerCards.find((c) => c.k === record.land_offer_strategy) || null;
  const saveOffer = async (val, strategyKey) => {
    try { const updated = await updateItem(resource, id, { actual_land_offer_price: val, land_offer_strategy: strategyKey }); onSaved(updated); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [recBusy, setRecBusy] = useState(false);
  const runAngelaRecommend = async () => {
    setRecBusy(true);
    try {
      const { data } = await api.post("/ai/recommend-strategy", {
        resource, record_id: id,
        strategies: offerCards.map((c) => ({ key: c.k, label: c.label, max_land_offer: c.val, description: c.desc })),
        context: { gdv, asking_price: record.list_price || record.asking_price || null, market_land_value: marketValue, potential_lots: record.potential_lots || null, current_selected: record.land_offer_strategy || null },
      });
      onSaved({ ...record, angela_strategy_recommendation: data });
      toast.success(`Angela recommends ${data.label}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setRecBusy(false); }
  };

  const th = "text-[10px] uppercase tracking-wide text-muted-foreground font-medium px-2 py-1 text-right";
  const td = "px-2 py-1.5 text-right text-foreground/90 whitespace-nowrap";

  const OfferChip = ({ label, value, pct, active, highlight }) => (
    <button type="button" onClick={() => value != null && save("actual_land_offer_price", String(value))}
      className={`flex-1 min-w-[130px] rounded-md border px-3 py-2 text-left transition-colors ${active ? "border-primary/60 bg-primary/10" : highlight ? "border-primary/40 bg-primary/[0.06] hover:bg-primary/10" : "border-border/60 bg-secondary/30 hover:bg-accent/40"}`}
      data-testid={`land-offer-option-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">{label}{pct && <span className="text-muted-foreground/70">· {pct}</span>}</div>
      <div className={`text-base font-bold mt-0.5 ${highlight ? "text-primary" : "text-foreground"}`}>{money(value)}</div>
    </button>
  );

  return (
    <div id="land-offer-anchor" className="rounded-lg border border-border bg-card overflow-hidden" data-testid="land-offer-module">
      <button type="button" onClick={() => setCollapsed((c) => !c)} className="w-full flex items-center gap-3 px-5 py-4 border-b border-border text-left hover:bg-accent/40 transition-colors" data-testid="land-offer-collapse-btn">
        <h3 className="text-sm font-semibold text-foreground">Land Offer</h3>
        <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 text-primary bg-primary/10" data-testid="land-offer-selected-strategy">{selectedCard ? selectedCard.label : "Select a strategy"}</span>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground hidden sm:inline">Your Offer</span>
          <span className="text-sm font-bold text-primary" data-testid="land-offer-header-max">{money(actualOffer)}</span>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${collapsed ? "" : "rotate-180"}`} />
        </div>
      </button>

      {!collapsed && (
        <div className="p-5 space-y-6">
          {/* Max offer by strategy — tap a card to set your land offer price */}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2 font-semibold">Max Offer by Strategy — tap a card to set your offer</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="land-offer-strategy-maxes">
              {offerCards.map((row) => {
                const active = record.land_offer_strategy === row.k;
                return (
                  <button type="button" key={row.k}
                    onClick={() => row.val != null && saveOffer(Math.round(row.val), row.k)}
                    className={`text-left rounded-lg border px-4 py-3 h-full flex flex-col transition-colors ${active ? "border-primary bg-primary/10" : "border-border/60 bg-secondary/30 hover:bg-accent/40 hover:border-primary/40"}`}
                    data-testid={`land-offer-strategy-max-${row.k}`}>
                    <div className="text-xs font-semibold uppercase tracking-wide text-foreground">{row.label}</div>
                    <div className={`text-2xl font-bold mt-1 ${active ? "text-primary" : "text-foreground"}`} data-testid={`land-offer-strategy-max-${row.k}-value`}>{money(row.val)}</div>
                    <div className="text-[11px] text-muted-foreground mt-1.5 leading-snug">{row.desc}</div>
                  </button>
                );
              })}
              {(() => {
                const rec = record.angela_strategy_recommendation;
                const recCard = rec ? offerCards.find((c) => c.k === rec.key) : null;
                const active = rec && record.land_offer_strategy === rec.key;
                return (
                  <div className={`rounded-lg border px-4 py-3 h-full flex flex-col ${active ? "border-primary bg-primary/10" : "border-primary/40 bg-primary/[0.05]"}`} data-testid="land-offer-angela-rec">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-primary">Angela's Pick</span>
                    </div>
                    {rec ? (
                      <>
                        <button type="button" onClick={() => recCard && recCard.val != null && saveOffer(Math.round(recCard.val), recCard.k)} className="text-left mt-1" data-testid="land-offer-angela-rec-apply">
                          <div className="text-lg font-bold text-foreground">{rec.label}</div>
                          {recCard && recCard.val != null && <div className="text-sm font-semibold text-primary">{money(recCard.val)}</div>}
                        </button>
                        <div className="text-[11px] text-muted-foreground mt-1.5 leading-snug flex-1" data-testid="land-offer-angela-rec-rationale">{rec.rationale}</div>
                        <button type="button" onClick={runAngelaRecommend} disabled={recBusy} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 mt-2 disabled:opacity-50" data-testid="land-offer-angela-rerun">
                          {recBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}Re-assess
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="text-[11px] text-muted-foreground mt-1.5 leading-snug flex-1">Let Angela assess the deal and recommend which strategy to pursue.</div>
                        <button type="button" onClick={runAngelaRecommend} disabled={recBusy} className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-md border border-primary/50 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20 transition-colors disabled:opacity-50" data-testid="land-offer-angela-ask">
                          {recBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}Ask Angela
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Your land offer price — populated when you tap a card above */}
          <div className="rounded-md border border-border/60 bg-secondary/30 px-4 py-3 max-w-sm" data-testid="land-offer-your-price">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 block">Your Land Offer Price</label>
            <input key={`ac${record.actual_land_offer_price ?? ""}`} defaultValue={actualOffer != null ? String(actualOffer) : ""} inputMode="decimal" onBlur={(e) => save("actual_land_offer_price", e.target.value)} placeholder="—" className="w-full bg-transparent text-xl font-bold outline-none placeholder:text-muted-foreground text-foreground" data-testid="land-offer-actual-input" />
          </div>
        </div>
      )}
    </div>
  );
}

function PadReadyLand({ record, resource, id, onSaved }) {
  const [collapsed, setCollapsed] = useState(true);
  const [advOpen, setAdvOpen] = useState(false);
  const p = computePadReady(record);
  const money = (v) => formatCurrency(Math.round(v || 0));

  // Editable input tiles — save on blur to the pad-specific override keys (base field untouched).
  const [inputs, setInputs] = useState({
    gdv: String(p.gdv || ""), land: String(p.land || ""),
    siteWork: String(p.siteWork || ""), construction: String(p.construction || ""),
  });
  useEffect(() => {
    const np = computePadReady(record);
    setInputs({ gdv: String(np.gdv || ""), land: String(np.land || ""),
      siteWork: String(np.siteWork || ""), construction: String(np.construction || "") });
  }, [record.pad_gdv_override, record.pad_land_override, record.pad_site_work, record.pad_construction_override,
      record.arv_market_value, record.max_land_purchase_price, record.construction_budget]);

  const saveField = async (key, raw) => {
    const cleaned = String(raw).replace(/[^0-9.]/g, "");
    const val = cleaned === "" ? null : Number(cleaned);
    try { const updated = await updateItem(resource, id, { [key]: val }); onSaved(updated); }
    catch (e) { toast.error(apiError(e)); }
  };

  const pctStr = (v) => (v == null ? "—" : `${v.toFixed(1)}%`);
  const profitCls = (v) => (v == null ? "text-muted-foreground" : v >= PAD_PROFIT_PCT * 100 - 0.5 ? "text-primary" : "text-destructive");

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="pad-ready-module">
      <button type="button" onClick={() => setCollapsed((c) => !c)} className="w-full flex items-center justify-between gap-3 px-5 py-4 border-b border-border text-left hover:bg-accent/40 transition-colors" title={collapsed ? "Expand" : "Collapse"} data-testid="pad-ready-collapse-btn">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Pad-Ready Land</h3>
          <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-1.5 py-0.5">20 / 60 / 20</span>
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${collapsed ? "" : "rotate-180"}`} />
      </button>

      {!collapsed && (
        <div className="p-5 space-y-6">
          {p.gdv <= 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="pad-feasibility-verdict">Enter a GDV to assess feasibility.</div>
          ) : (
          <>
          {/* 1 — Deal Snapshot */}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Deal Snapshot</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pad-deal-snapshot">
              <PadTile label="GDV" tid="pad-snap-gdv">{money(p.gdv)}</PadTile>
              <PadTile label="Total Project Cost" tid="pad-snap-cost">{money(p.allIn)}</PadTile>
              <PadTile label="Projected Profit" cls={p.profit >= 0 ? "text-primary" : "text-destructive"} tid="pad-snap-profit">{money(p.profit)}</PadTile>
              <PadTile label="Profit Margin" cls={profitCls(p.profitPct)} tid="pad-snap-margin">{pctStr(p.profitPct)}</PadTile>
            </div>
          </div>

          {/* 2 — 20/60/20 allocation bar */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">20 / 60 / 20 Check</div>
              <div className="text-[11px] text-muted-foreground">GDV <span className="text-foreground/80 font-medium">{money(p.gdv)}</span></div>
            </div>
            <div className="flex h-9 w-full overflow-hidden rounded-md border border-border/60" data-testid="pad-alloc-bar">
              <div className={`flex items-center justify-center text-[11px] font-semibold text-background ${p.padReadyPct > 20.5 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${Math.max(p.padReadyPct, 4)}%` }} title={`Land + Site ${pctStr(p.padReadyPct)}`}>
                {p.padReadyPct >= 9 ? `${p.padReadyPct.toFixed(1)}%` : ""}
              </div>
              <div className={`flex items-center justify-center text-[11px] font-semibold text-background border-l border-background/30 ${p.buildPct > 60.5 ? "bg-destructive" : "bg-teal-600"}`} style={{ width: `${Math.max(p.buildPct, 4)}%` }} title={`Construction ${pctStr(p.buildPct)}`}>
                {p.buildPct >= 9 ? `${p.buildPct.toFixed(1)}%` : ""}
              </div>
              <div className={`flex items-center justify-center text-[11px] font-semibold text-background border-l border-background/30 ${p.profitPct >= 19.5 ? "bg-emerald-500" : "bg-destructive"}`} style={{ width: `${Math.max(Math.min(p.profitPct, 100), 0)}%` }} title={`Profit ${pctStr(p.profitPct)}`}>
                {p.profitPct >= 9 ? `${p.profitPct.toFixed(1)}%` : ""}
              </div>
            </div>
            <div className="flex justify-between mt-1.5 text-[11px]">
              <span className="text-muted-foreground">Land + Site <span className="text-foreground/80 font-medium">{pctStr(p.padReadyPct)}</span> · target ≤ 20%</span>
              <span className="text-muted-foreground">Construction <span className="text-foreground/80 font-medium">{pctStr(p.buildPct)}</span> · target ≤ 60%</span>
              <span className="text-muted-foreground">Profit <span className="text-foreground/80 font-medium">{pctStr(p.profitPct)}</span> · target ≥ 20%</span>
            </div>
          </div>

          {/* 3 — Overall result */}
          <div className={`rounded-md border px-4 py-3 ${p.feasible ? "border-emerald-500/40 bg-emerald-500/10" : "border-destructive/40 bg-destructive/10"}`} data-testid="pad-feasibility-verdict">
            <div className={`text-sm font-bold ${p.feasible ? "text-emerald-400" : "text-destructive"}`}>
              {p.feasible ? `✅ DEAL PASSES — ${pctStr(p.profitPct)} Projected Profit` : `⛔ DEAL FAILS — ${pctStr(p.profitPct)} Projected Profit`}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {money(p.profit)} projected profit ·{" "}
              {p.profit >= p.targetProfit
                ? <><span className="text-emerald-400 font-medium">{money(p.profit - p.targetProfit)}</span> above the 20% profit target</>
                : <><span className="text-destructive font-medium">{money(p.targetProfit - p.profit)}</span> below the 20% profit target</>}
            </div>
          </div>

          {/* 4 — Secondary warnings (only when a cost bucket exceeds its target) */}
          {p.padReady > p.targetLand && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3" data-testid="pad-warning-land">
              <div className="text-sm font-semibold text-amber-400">⚠️ Land + site is {(p.padReadyPct - 20).toFixed(1)}% above target</div>
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                <div>Target pad-ready land: <span className="text-foreground/80 font-medium">{money(p.targetLand)}</span></div>
                <div>Current pad-ready land: <span className="text-foreground/80 font-medium">{money(p.padReady)}</span></div>
                <div>Over target by: <span className="text-amber-400 font-medium">{money(p.padReady - p.targetLand)}</span></div>
              </div>
            </div>
          )}
          {p.construction > p.targetBuild && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3" data-testid="pad-warning-construction">
              <div className="text-sm font-semibold text-amber-400">⚠️ Construction is {(p.buildPct - 60).toFixed(1)}% above target</div>
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                <div>Target construction (60%): <span className="text-foreground/80 font-medium">{money(p.targetBuild)}</span></div>
                <div>Current construction: <span className="text-foreground/80 font-medium">{money(p.construction)}</span></div>
                <div>Over target by: <span className="text-amber-400 font-medium">{money(p.construction - p.targetBuild)}</span></div>
              </div>
            </div>
          )}
          </>
          )}

          {/* Inputs — prefilled from the record; edit to override */}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Inputs · prefilled from record (edit to override)</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pad-ready-inputs">
              <PadEditTile label="GDV (End Value)" field="gdv" value={inputs.gdv} hint="Gross Development Value — prefilled from the record; edit to override"
                onChange={(e) => setInputs((s) => ({ ...s, gdv: e.target.value }))} onBlur={(e) => saveField("pad_gdv_override", e.target.value)} />
              <PadEditTile label="Land Cost" field="land" value={inputs.land} hint="Lot purchase price"
                onChange={(e) => setInputs((s) => ({ ...s, land: e.target.value }))} onBlur={(e) => saveField("pad_land_override", e.target.value)} />
              <PadEditTile label="Site Work" field="siteWork" value={inputs.siteWork} hint="Clearing, sewer/water, demo to make the lot pad-ready"
                onChange={(e) => setInputs((s) => ({ ...s, siteWork: e.target.value }))} onBlur={(e) => saveField("pad_site_work", e.target.value)} />
              <PadEditTile label="Construction Cost" field="construction" value={inputs.construction} hint="Total vertical construction budget"
                onChange={(e) => setInputs((s) => ({ ...s, construction: e.target.value }))} onBlur={(e) => saveField("pad_construction_override", e.target.value)} />
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">Independent feasibility lens — the 90/50/15 Underwriting Model is unaffected.</div>
          </div>

          {/* Advanced metrics — collapsed by default */}
          {p.gdv > 0 && (
          <div className="border-t border-border/50 pt-4">
            <button type="button" onClick={() => setAdvOpen((v) => !v)} className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors" data-testid="pad-advanced-toggle">
              Advanced Metrics
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${advOpen ? "rotate-180" : ""}`} />
            </button>
            {advOpen && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm rounded-md border border-border/60 bg-secondary/20 px-4 py-3" data-testid="pad-advanced-body">
                <KVRow label="Max Land Purchase Price" value={<span data-testid="pad-max-land" className={p.maxLandPrice < 0 ? "text-destructive" : ""}>{money(p.maxLandPrice)}</span>} />
                <KVRow label="Max Construction Budget (60%)" value={money(p.targetBuild)} />
                <KVRow label="Profit Target (20% floor)" value={<span className="text-primary">{money(p.targetProfit)}</span>} />
                <KVRow label="Break-Even Sale Price" value={money(p.allIn)} />
                <KVRow label="Min Sale Price (20% profit)" value={<span data-testid="pad-implied-gdv" className="text-primary">{money(p.impliedGdv)}</span>} />
                <KVRow label="Headroom Above 20% Target" value={<span className={p.profit >= p.targetProfit ? "text-emerald-400" : "text-destructive"}>{money(p.profit - p.targetProfit)}</span>} />
              </div>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
}



const ACRE_SF = 43560;
const cleanNum = (v) => String(v ?? "").replace(/[^0-9.]/g, "");
const parcelSqft = (p) => Number(cleanNum(p?.lot_size_sqft)) || 0;
const sumParcelSqft = (list) => (list || []).reduce((a, p) => a + parcelSqft(p), 0);

// Multi-parcel deals: one deal made of 2+ adjacent parcels. Each parcel keeps its own
// APN + lot size; the app auto-sums lot sizes into the deal's total (lot_size_sqft/acres),
// which stays a single value feeding underwriting, buildable envelope, $/SF, etc.
function ParcelsModule({ record, resource, id, onSaved }) {
  const [local, setLocal] = useState(Array.isArray(record.parcels) ? record.parcels : []);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [dupes, setDupes] = useState({});
  useEffect(() => { setLocal(Array.isArray(record.parcels) ? record.parcels : []); }, [JSON.stringify(record.parcels)]);

  const multi = local.length > 1;
  const seedPrimary = () => ({ id: `p${Date.now()}`, apn: record.apn || "", lot_size_sqft: record.lot_size_sqft || "", lot_size_acres: record.lot_size_acres || "" });

  const persist = async (list, exit = false) => {
    setLocal(list);
    setBusy(true);
    const effective = exit ? [] : list;
    const total = sumParcelSqft(list);
    const patch = {
      parcels: effective,
      apn: (effective[0]?.apn) || list[0]?.apn || record.apn || "",
      lot_size_sqft: total || "",
      lot_size_acres: total ? Math.round((total / ACRE_SF) * 100) / 100 : "",
    };
    try { const updated = await updateItem(resource, id, patch); onSaved(updated); if (exit) setLocal([]); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const addParcel = () => persist([...(local.length ? local : [seedPrimary()]), { id: `p${Date.now()}`, apn: "", lot_size_sqft: "", lot_size_acres: "" }]);
  const removeParcel = (idx) => {
    const next = local.filter((_, j) => j !== idx);
    if (next.length <= 1) persist(next.length ? next : [seedPrimary()], true);
    else persist(next);
  };

  const setParcel = (idx, patch) => setLocal((l) => l.map((p, j) => (j === idx ? { ...p, ...patch } : p)));
  const onSqft = (idx, v) => {
    const s = cleanNum(v);
    const sf = Number(s);
    setParcel(idx, { lot_size_sqft: s, lot_size_acres: (s && !isNaN(sf) && sf > 0) ? Math.round((sf / ACRE_SF) * 100) / 100 : "" });
  };
  const onAcres = (idx, v) => {
    const s = cleanNum(v);
    const ac = Number(s);
    setParcel(idx, { lot_size_acres: s, lot_size_sqft: (s && !isNaN(ac) && ac > 0) ? Math.round(ac * ACRE_SF) : "" });
  };
  const commit = () => persist(local);

  const checkDup = async (idx, apn) => {
    const a = String(apn || "").trim();
    if (!a) { setDupes((d) => ({ ...d, [idx]: [] })); return; }
    try {
      const { data } = await api.get(`/property-search/duplicate-apn?apn=${encodeURIComponent(a)}`);
      const matches = (data.matches || []).filter((m) => m.id !== record.id && m.id !== record.source_property_search_id);
      setDupes((d) => ({ ...d, [idx]: matches }));
    } catch { /* best-effort */ }
  };

  const total = sumParcelSqft(local);
  const totalAcres = total ? Math.round((total / ACRE_SF) * 100) / 100 : 0;

  if (!multi) return null;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="parcels-module">
      <button type="button" onClick={() => setCollapsed((c) => !c)} className="w-full flex items-center justify-between gap-3 px-5 py-4 border-b border-border text-left hover:bg-accent/40 transition-colors" title={collapsed ? "Expand" : "Collapse"} data-testid="parcels-collapse-btn">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Parcels</h3>
          <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-1.5 py-0.5" data-testid="parcels-count">{local.length} parcels</span>
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${collapsed ? "" : "rotate-180"}`} />
      </button>
      {!collapsed && (
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3" data-testid="parcels-total">
            <div className="rounded-md border border-border/60 bg-secondary/20 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total Lot Size</div>
              <div className="text-sm font-semibold text-foreground">{total.toLocaleString()} SF</div>
            </div>
            <div className="rounded-md border border-border/60 bg-secondary/20 px-3 py-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Total Acres</div>
              <div className="text-sm font-semibold text-foreground">{totalAcres.toLocaleString()} ac</div>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Each parcel keeps its own APN &amp; lot size. Sizes auto-sum into the deal total above (used by underwriting, buildable envelope &amp; $/SF). GDV and economics remain a single deal.</p>
          <div className="space-y-3">
            {local.map((p, i) => (
              <div key={p.id || i} className="rounded-md border border-border/60 p-3" data-testid={`parcel-row-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-foreground">Parcel {i + 1}{i === 0 ? " · Primary" : ""}</span>
                  {local.length > 1 && (
                    <button onClick={() => removeParcel(i)} className="ml-auto p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-accent" title="Remove parcel" data-testid={`parcel-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-muted-foreground">APN</label>
                    <Input value={p.apn || ""} onChange={(e) => setParcel(i, { apn: e.target.value })} onBlur={() => { commit(); checkDup(i, p.apn); }} placeholder="APN" className="h-8 text-sm" data-testid={`parcel-apn-${i}`} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Lot Size (Sq Ft)</label>
                    <Input value={p.lot_size_sqft ?? ""} onChange={(e) => onSqft(i, e.target.value)} onBlur={commit} placeholder="0" className="h-8 text-sm" data-testid={`parcel-sqft-${i}`} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Lot Size (Acres)</label>
                    <Input value={p.lot_size_acres ?? ""} onChange={(e) => onAcres(i, e.target.value)} onBlur={commit} placeholder="0" className="h-8 text-sm" data-testid={`parcel-acres-${i}`} />
                  </div>
                </div>
                {(dupes[i] || []).length > 0 && (
                  <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-400" data-testid={`parcel-dup-${i}`}>
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>This APN already exists on {(dupes[i] || []).length} record(s): {(dupes[i] || []).map((m) => m.name).join(", ")}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={addParcel} disabled={busy} data-testid="parcels-add-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add parcel</Button>
        </div>
      )}
    </div>
  );
}


function SubdivisionModule({ record, resource, id, onSaved }) {
  const [sub, setSub] = useState(record.subdivision || { enabled: false, lots: [], site_costs: [] });
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [countInput, setCountInput] = useState("");
  const [srcSqft, setSrcSqft] = useState(0);
  useEffect(() => { setSub(record.subdivision || { enabled: false, lots: [], site_costs: [] }); }, [JSON.stringify(record.subdivision)]);
  useEffect(() => {
    const own = Number(cleanNum(record.lot_size_sqft))
      || (Number(cleanNum(record.lot_size_acres)) ? Math.round(Number(cleanNum(record.lot_size_acres)) * 43560) : 0)
      || (Array.isArray(record.parcels) ? record.parcels.reduce((a, p) => a + parcelSqft(p), 0) : 0);
    if (own > 0 || !record.source_property_search_id) { setSrcSqft(0); return; }
    let cancelled = false;
    api.get(`/property_search/${record.source_property_search_id}`).then((r) => {
      const ps = r.data || {};
      const s = Number(cleanNum(ps.lot_size_sqft))
        || (Number(cleanNum(ps.lot_size_acres)) ? Math.round(Number(cleanNum(ps.lot_size_acres)) * 43560) : 0)
        || (Array.isArray(ps.parcels) ? ps.parcels.reduce((a, p) => a + parcelSqft(p), 0) : 0);
      if (!cancelled) setSrcSqft(s || 0);
    }).catch(() => { if (!cancelled) setSrcSqft(0); });
    return () => { cancelled = true; };
  }, [record.source_property_search_id, record.lot_size_sqft, record.lot_size_acres, JSON.stringify(record.parcels)]);
  useEffect(() => {
    const lc = (record.subdivision?.lots || []).length;
    const pl = record.proposed_lots;
    setCountInput(String((pl ?? "") !== "" ? Number(pl) : (lc || "")));
  }, [record.proposed_lots, (record.subdivision?.lots || []).length]);

  const persist = async (next) => {
    setSub(next);
    setBusy(true);
    try { const updated = await updateItem(resource, id, { subdivision: next }); onSaved(updated); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const commit = () => persist(sub);
  const setLot = (i, patch) => setSub((s) => ({ ...s, lots: s.lots.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const setSite = (i, patch) => setSub((s) => ({ ...s, site_costs: s.site_costs.map((x, j) => (j === i ? { ...x, ...patch } : x)) }));
  const changeMode = (i, mode) => persist({ ...sub, lots: sub.lots.map((l, j) => (j === i ? { ...l, mode } : l)) });
  const addSite = () => persist({ ...sub, site_costs: [...(sub.site_costs || []), { label: "", amount: "" }] });
  const removeSite = (i) => persist({ ...sub, site_costs: sub.site_costs.filter((_, j) => j !== i) });

  // Number of lots is driven by the Proposed Lots field (two-way): editing it here regenerates
  // the lot rows and writes back to the record's proposed_lots. New lots inherit Lot 1's specs.
  const syncLotCount = async (raw) => {
    let n = parseInt(String(raw).replace(/[^0-9]/g, ""), 10);
    if (!n || n < 1) n = 1;
    n = Math.min(n, 500);
    const cur = sub.lots || [];
    const tmpl = cur[0] || {};
    let lots;
    if (n <= cur.length) {
      lots = cur.slice(0, n);
    } else {
      lots = [...cur, ...Array.from({ length: n - cur.length }, (_, k) => ({
        id: `${Date.now()}${k}`, name: `Lot ${cur.length + k + 1}`, mode: "build",
        lot_size: "", home_size: tmpl.home_size || "", arv: tmpl.arv || "", cost: tmpl.cost || "",
      }))];
    }
    const next = { ...sub, lots };
    setSub(next);
    setBusy(true);
    try { const updated = await updateItem(resource, id, { subdivision: next, proposed_lots: n }); onSaved(updated); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const roll = computeSubdivision(sub);
  const money = (v) => formatCurrency(Math.round(v || 0));
  const enabled = !!sub.enabled;

  // Lot size is implied by the split: property's total land ÷ number of lots (read-only).
  // Robust to comma-formatted values and falls back to the sum of parcels when set.
  const parcelsTotalSqft = Array.isArray(record.parcels) ? record.parcels.reduce((a, p) => a + parcelSqft(p), 0) : 0;
  const acresSqft = Number(cleanNum(record.lot_size_acres)) ? Math.round(Number(cleanNum(record.lot_size_acres)) * 43560) : 0;
  const totalSqft = Number(cleanNum(record.lot_size_sqft)) || acresSqft || parcelsTotalSqft || srcSqft;
  const perLotSqft = totalSqft ? Math.round(totalSqft / Math.max(1, (sub.lots || []).length)) : null;

  const SITE_PRESETS = ["Land Acquisition", "Private Road", "Mass Grading", "Offsite Grading", "Wet Utilities", "Dry Utilities", "Retention Basin", "Entitlement / Subdivision Fees"];

  if (!enabled) return null;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="subdivision-module">
      <button type="button" onClick={() => setCollapsed((c) => !c)} className="w-full flex items-center justify-between gap-3 px-5 py-4 border-b border-border text-left hover:bg-accent/40 transition-colors" title={collapsed ? "Expand" : "Collapse"} data-testid="subdivision-collapse-btn">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Subdivision / Multi-Lot</h3>
          {roll.count > 0 && <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-1.5 py-0.5">{roll.count} lots</span>}
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${collapsed ? "" : "rotate-180"}`} />
      </button>

      {!collapsed && (
      <div className="p-5 space-y-6">
          {/* Roll-up summary — the Lots card is editable and drives the lot count */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="subdivision-summary">
            {[
              { l: "Lots", edit: true },
              { l: "Avg Lot Size", v: perLotSqft ? `${perLotSqft.toLocaleString()} SF` : "—" },
              { l: "Total GDV", v: money(roll.totalArv) },
              { l: "All-In Cost", v: money(roll.allIn) },
              { l: "Net Profit", v: money(roll.profit), cls: roll.profit >= 0 ? "text-primary" : "text-destructive" },
              { l: "Margin", v: roll.margin == null ? "—" : `${roll.margin.toFixed(1)}%`, cls: (roll.margin ?? 0) >= 10 ? "text-primary" : (roll.margin ?? 0) >= 5 ? "text-amber-400" : "text-destructive" },
            ].map((m) => (
              <div key={m.l} className="rounded-md border border-border/60 bg-secondary/20 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{m.l}</div>
                {m.edit ? (
                  <Input type="number" min="1" value={countInput} disabled={busy}
                    onChange={(e) => setCountInput(e.target.value)}
                    onBlur={(e) => syncLotCount(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    className="h-7 -ml-1 px-1 text-sm font-semibold border-0 bg-transparent focus-visible:ring-1 focus-visible:ring-primary" data-testid="subdivision-lot-count" title="Edit the number of lots" />
                ) : (
                  <div className={`text-sm font-semibold ${m.cls || "text-foreground"}`}>{m.v}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Per-parcel subdivision economics: when the land is split, this lays out each proposed
// parcel's GDV/value vs its share of the land price and its individual profit margin,
// alongside the original (pre-split) lot size vs the proposed size per new parcel.
function SubdivisionParcelBreakdown({ record, resource, id, onSaved }) {
  const [busy, setBusy] = useState(false);
  const sub = record.subdivision || {};
  const roll = computeSubdivision(sub);
  const count = Math.max(1, roll.count);
  const money = (v) => formatCurrency(Math.round(v || 0));
  const flip = sub.land_flip || null;

  // Original (pre-split) total land size.
  const acresSqft = Number(cleanNum(record.lot_size_acres)) ? Math.round(Number(cleanNum(record.lot_size_acres)) * 43560) : 0;
  const parcelsTotalSqft = Array.isArray(record.parcels) ? record.parcels.reduce((a, p) => a + parcelSqft(p), 0) : 0;
  const totalSqft = Number(cleanNum(record.lot_size_sqft)) || acresSqft || parcelsTotalSqft || 0;

  // Land bought once for the whole deal.
  const landRow = (sub.site_costs || []).find((x) => /land acquisition/i.test(x.label || ""));
  const landTotal = landRow ? subNum(landRow.amount) : roll.totalSite;
  const sitePerLot = roll.totalSite / count;

  const fmtSf = (sf) => (sf ? `${Math.round(sf).toLocaleString()} SF` : "—");
  const fmtAc = (sf) => (sf ? `${(sf / 43560).toFixed(3)} ac` : "");
  const avgProposedSqft = totalSqft ? totalSqft / count : 0;

  const summary = [
    { l: "Original Parcel", v: fmtSf(totalSqft), sub: fmtAc(totalSqft) },
    { l: "Proposed Parcels", v: `${count}`, sub: "individual lots" },
    { l: "Proposed Size / Parcel", v: fmtSf(avgProposedSqft), sub: fmtAc(avgProposedSqft) },
    { l: "Land Purchase", v: money(landTotal), sub: `${money(sitePerLot)} / lot` },
  ];

  const estimate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post("/ai/estimate-subdivision-land", { resource, record_id: id });
      onSaved && onSaved((prev) => ({ ...prev, subdivision: { ...(prev.subdivision || {}), land_flip: res.data } }));
    } catch (e) {
      // Silent — Angela runs in the background; cards simply stay unpopulated on failure.
    } finally {
      setBusy(false);
    }
  };

  // The saved valuation is stale if the lot count no longer matches what Angela priced.
  const stale = !!flip && Number(flip.parcel_count) !== count;
  const fresh = flip && !stale;

  // Auto-estimate in the background whenever a multi-lot subdivision has no valuation yet
  // OR the lot count changed since the last estimate (parcels added/removed).
  useEffect(() => {
    if (count >= 2 && (!flip || stale) && !busy) estimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, count, flip ? Number(flip.parcel_count) : 0]);

  const f = fresh ? flip : null;
  const valuationCards = [
    { l: "Land Basis / Parcel", v: money(f ? f.cost_per_parcel : Math.round(sitePerLot / 1000) * 1000), sub: "evenly split basis" },
    { l: "Uplift Benchmark", v: f && f.uplift_pct != null ? `+${f.uplift_pct}%` : null, sub: "local cost-uplift · secondary" },
    { l: "Cost-Based Benchmark", v: f ? money(f.cost_based_benchmark) : null, sub: "basis × uplift · sanity check" },
    { l: "Market Value / Parcel", v: f ? money(f.market_value_per_parcel) : null, sub: f && f.value_low && f.value_high ? `comps ${money(f.value_low)}–${money(f.value_high)}` : "comp-supported", primary: true },
  ];
  const returnCards = [
    { l: "Total Entitled Market Value", v: f ? money(f.total_market_value) : null, sub: `all ${count} parcels` },
    { l: "Entitlement + Carry Costs", v: f ? money(f.entitlement_cost_total) : null, sub: "engineering · legal · carry" },
    { l: "Net Land-Flip Profit", v: f ? money(f.net_profit) : null, sub: f && f.margin_pct != null ? `${f.margin_pct}% margin on sale` : "", highlight: true, positive: !f || (f.net_profit || 0) >= 0 },
    { l: "ROI on Total Investment", v: f && f.roi_pct != null ? `${f.roi_pct}%` : null, sub: f ? `on ${money(f.total_investment)} invested` : "", highlight: true, positive: !f || (f.roi_pct || 0) >= 0 },
  ];

  const renderCard = (c) => (
    <div
      key={c.l}
      data-testid={`land-flip-card-${c.l.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
      className={`rounded-md px-3 py-2.5 border ${c.highlight ? "border-primary/40 bg-primary/[0.06]" : c.primary ? "border-primary/30 bg-primary/[0.03]" : "border-border/60 bg-secondary/20"}`}
    >
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{c.l}</div>
      {c.v == null ? (
        <div className="mt-1.5 flex items-center gap-1.5 text-muted-foreground" data-testid="land-flip-card-loading">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-[11px]">Calculating…</span>
        </div>
      ) : (
        <div className={`text-sm font-semibold ${c.highlight ? (c.positive ? "text-primary" : "text-destructive") : c.primary ? "text-primary" : "text-foreground"}`}>{c.v}</div>
      )}
      {c.sub && c.v != null ? <div className="text-[11px] text-muted-foreground mt-0.5">{c.sub}</div> : null}
    </div>
  );

  return (
    <CollapseCard
      testid="subdivision-parcel-breakdown"
      title="Per-Parcel Subdivision Economics"
      right={<span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-2 py-0.5">{count} parcels</span>}
    >
      <div className="px-6 py-4 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="subdivision-parcel-summary">
        {summary.map((m) => (
          <div key={m.l} className="rounded-md border border-border/60 bg-secondary/20 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{m.l}</div>
            <div className="text-sm font-semibold text-foreground">{m.v}</div>
            {m.sub && <div className="text-[11px] text-muted-foreground mt-0.5">{m.sub}</div>}
          </div>
        ))}
      </div>

      <div className="px-6 pb-5 space-y-4" data-testid="subdivision-land-flip">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Valuation <span className="text-muted-foreground/60 normal-case tracking-normal">· entitled lot value from market comps</span></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{valuationCards.map(renderCard)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">Returns <span className="text-muted-foreground/60 normal-case tracking-normal">· market value vs. your basis and costs</span></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{returnCards.map(renderCard)}</div>
        </div>
      </div>
    </CollapseCard>
  );
}


function LabeledNum({ label, value, onChange, onBlur, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">{label}</div>
      <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} className="h-8 text-sm" data-testid={testid} />
    </div>
  );
}

function KVRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground font-medium text-right">{value}</span>
    </div>
  );
}

function DealReturnAnalysis({ record }) {
  const [open, setOpen] = useState(false);
  const isSub = isSubdivisionActive(record);
  const arv = isSub ? computeSubdivision(record.subdivision).totalArv : (Number(record.arv_market_value) || 0);
  if (arv <= 0) return null;
  const money = (v) => formatCurrency(Math.round(v || 0));
  const tiers = [
    { label: "Protected Floor", m: 0.10, note: "Worst case — all-in at the 90% cap" },
    { label: "Base Case", m: 0.15, note: "All-in near 85% of GDV" },
    { label: "Target", m: 0.20, note: "All-in near 80% of GDV" },
    { label: "Upside", m: 0.25, note: "All-in near 75% of GDV" },
    { label: "Stretch", m: 0.30, note: "All-in near 70% of GDV" },
  ];

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="deal-return-analysis">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full px-5 py-4 border-b border-border flex items-center justify-between hover:bg-accent/40 transition-colors" data-testid="deal-return-analysis-toggle">
        <h3 className="text-sm font-semibold text-foreground">Return Potential</h3>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
      <div className="p-5 space-y-5">
        <div className="divide-y divide-border/60">
          {tiers.map((t) => {
            const profit = Math.round(arv * t.m);
            const allIn = Math.round(arv * (1 - t.m));
            return (
              <div key={t.label} className="flex items-center justify-between py-2" data-testid={`return-tier-${t.label.replace(/\s+/g, "-").toLowerCase()}`}>
                <div><div className="text-sm font-medium text-foreground">{t.label} · {Math.round(t.m * 100)}% margin</div><div className="text-[11px] text-muted-foreground">{t.note}</div></div>
                <div className="text-right"><div className="text-sm font-semibold text-primary">{money(profit)}</div><div className="text-[11px] text-muted-foreground">all-in {money(allIn)}</div></div>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}

function OfferVsList({ record }) {
  const [open, setOpen] = useState(false);
  const isSub = isSubdivisionActive(record);
  const roll = isSub ? computeSubdivision(record.subdivision) : null;
  const s = computeUnderwritingStatus(record);
  const arv = isSub ? roll.totalArv : s.arv;
  const listed = s.listed;
  if (arv <= 0 || listed <= 0) return null;
  const money = (v) => formatCurrency(Math.round(v || 0));
  const landCap = isSub ? Math.round(arv * 0.15) : s.landCap;   // 15% of (blended) GDV land budget
  const adjusted = isSub ? (listed > 0 && listed < landCap) : s.landAdjusted;
  const savings = adjusted ? (landCap - listed) : 0;            // landCap - listed (>0 when adjusted)
  const pctDiff = ((landCap - listed) / listed) * 100;
  const allIn = isSub ? roll.allIn : s.allIn;
  const profit = isSub ? roll.profit : s.projectedProfit;
  const margin = arv > 0 ? (profit / arv) * 100 : 0;
  const baselineProfit = profit - savings;   // if we'd paid the full 15% budget

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="offer-list-adjustment">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full px-5 py-4 border-b border-border flex items-center justify-between hover:bg-accent/40 transition-colors" data-testid="offer-list-toggle">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Offer vs. List</h3>
          {adjusted && <span className="text-[10px] font-semibold text-primary bg-primary/10 rounded-full px-1.5 py-0.5">Adjusted</span>}
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
      <div className="p-5">
        <div className="space-y-1.5 text-sm">
          <KVRow label="Current Listed Price" value={money(listed)} />
          <KVRow label="Land Budget (15% of GDV)" value={money(landCap)} />
          <KVRow label="Difference" value={<span className={adjusted ? "text-primary" : "text-amber-400"}>{pctDiff >= 0 ? "+" : ""}{pctDiff.toFixed(1)}% {adjusted ? "under budget" : "over budget"}</span>} />
        </div>
        {adjusted ? (
          <div className="mt-3 pt-3 border-t border-border/60">
            <div className="text-[11px] uppercase tracking-wide text-primary mb-2.5">Adjustment — land acquired at list price</div>
            <div className="space-y-1.5 text-sm">
              <KVRow label="Land Purchase (adjusted to list)" value={<span>{money(listed)} <span className="text-muted-foreground text-xs">was {money(landCap)}</span></span>} />
              <KVRow label="Land Savings" value={<span className="text-primary font-semibold">+{money(savings)}</span>} />
              <KVRow label="Adjusted All-In" value={money(allIn)} />
              <KVRow label="Adjusted Net Profit" value={<span className="text-primary">{money(profit)} <span className="text-muted-foreground text-xs">was {money(baselineProfit)}</span></span>} />
              <KVRow label="Adjusted Margin" value={<span className="text-primary font-semibold">{margin.toFixed(1)}%</span>} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-2.5">List price is below the 15%-of-GDV land budget, so Land Purchase is auto-adjusted to the list price — lowering all-in cost and lifting net profit across the model.</p>
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground mt-3 pt-3 border-t border-border/60">List price is at or above the 15%-of-GDV land budget ({money(landCap)}), so no downward land adjustment applies.</p>
        )}
      </div>
      )}
    </div>
  );
}

function ContractPickerDialog({ open, onOpenChange, recordId, resource = "acquisition_pipeline" }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(true);
  const [sel, setSel] = useState("");
  useEffect(() => {
    if (!open) return;
    setTplLoading(true);
    api.get("/contracts/library-templates")
      .then((r) => { const its = (r.data.items || []).filter((t) => t.is_docx); setTemplates(its); if (its[0]) setSel(its[0].id); })
      .catch(() => setTemplates([]))
      .finally(() => setTplLoading(false));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="contract-picker-dialog">
        <DialogHeader>
          <DialogTitle>Generate Contract</DialogTitle>
          <DialogDescription>Select which contract to generate, then fill it in on the next page.</DialogDescription>
        </DialogHeader>
        {tplLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-2 py-1" data-testid="contract-picker-list">
            <button
              onClick={() => setSel("__cover_letter__")}
              data-testid="contract-pick-cover-letter"
              className={`w-full text-left flex items-center gap-3 rounded-lg border p-3 transition-colors ${sel === "__cover_letter__" ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"}`}
            >
              <Mail className={`h-5 w-5 ${sel === "__cover_letter__" ? "text-primary" : "text-muted-foreground"}`} />
              <div>
                <div className="text-sm font-medium">Cover Letter</div>
                <div className="text-xs text-muted-foreground">Arqitek Letter of Intent — branded cover page</div>
              </div>
            </button>
            <button
              onClick={() => setSel("__loi__")}
              data-testid="contract-pick-loi"
              className={`w-full text-left flex items-center gap-3 rounded-lg border p-3 transition-colors ${sel === "__loi__" ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"}`}
            >
              <FileText className={`h-5 w-5 ${sel === "__loi__" ? "text-primary" : "text-muted-foreground"}`} />
              <div>
                <div className="text-sm font-medium">LOI – Purchase of Vacant Land</div>
                <div className="text-xs text-muted-foreground">Arqitek branded LOI — offer terms document</div>
              </div>
            </button>
            <button
              onClick={() => setSel("__option_loi__")}
              data-testid="contract-pick-option-loi"
              className={`w-full text-left flex items-center gap-3 rounded-lg border p-3 transition-colors ${sel === "__option_loi__" ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"}`}
            >
              <FileText className={`h-5 w-5 ${sel === "__option_loi__" ? "text-primary" : "text-muted-foreground"}`} />
              <div>
                <div className="text-sm font-medium">LOI – Option to Purchase &amp; Entitlement Rights</div>
                <div className="text-xs text-muted-foreground">Exclusive option + entitlement rights + permitted assignment</div>
              </div>
            </button>
            <button
              onClick={() => setSel("__jv_loi__")}
              data-testid="contract-pick-jv-loi"
              className={`w-full text-left flex items-center gap-3 rounded-lg border p-3 transition-colors ${sel === "__jv_loi__" ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"}`}
            >
              <FileText className={`h-5 w-5 ${sel === "__jv_loi__" ? "text-primary" : "text-muted-foreground"}`} />
              <div>
                <div className="text-sm font-medium">LOI – Land Contribution &amp; Development JV</div>
                <div className="text-xs text-muted-foreground">Seller contributes land; Arqitek develops &amp; shares profit</div>
              </div>
            </button>
            <button
              onClick={() => setSel("__gl_loi__")}
              data-testid="contract-pick-ground-lease-loi"
              className={`w-full text-left flex items-center gap-3 rounded-lg border p-3 transition-colors ${sel === "__gl_loi__" ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"}`}
            >
              <FileText className={`h-5 w-5 ${sel === "__gl_loi__" ? "text-primary" : "text-muted-foreground"}`} />
              <div>
                <div className="text-sm font-medium">LOI – Ground Lease &amp; Development</div>
                <div className="text-xs text-muted-foreground">Long-term site control via ground lease — no upfront land purchase</div>
              </div>
            </button>
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => setSel(t.id)}
                data-testid={`contract-pick-${t.id}`}
                className={`w-full text-left flex items-center gap-3 rounded-lg border p-3 transition-colors ${sel === t.id ? "border-primary bg-primary/10" : "border-border hover:bg-secondary/50"}`}
              >
                <FileSignature className={`h-5 w-5 ${sel === t.id ? "text-primary" : "text-muted-foreground"}`} />
                <div>
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.original_filename}</div>
                </div>
              </button>
            ))}
            {templates.length === 0 && (
              <div className="text-xs text-muted-foreground pt-1">No contract templates yet. Upload a .docx in Operations → Contracts to enable contracts.</div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="contract-picker-cancel">Cancel</Button>
          <Button
            disabled={!sel}
            onClick={() => { onOpenChange(false); navigate(sel === "__cover_letter__" ? `/record/${resource}/${recordId}/cover-letter` : sel === "__loi__" ? `/record/${resource}/${recordId}/loi` : sel === "__option_loi__" ? `/record/${resource}/${recordId}/option-loi` : sel === "__jv_loi__" ? `/record/${resource}/${recordId}/jv-loi` : sel === "__gl_loi__" ? `/record/${resource}/${recordId}/ground-lease-loi` : `/record/${resource}/${recordId}/contract?template=${sel}`); }}
            data-testid="contract-picker-open"
          >
            {sel === "__cover_letter__" ? "Open Cover Letter" : sel === "__loi__" ? "Open Letter of Intent" : sel === "__option_loi__" ? "Open Option LOI" : sel === "__jv_loi__" ? "Open JV LOI" : sel === "__gl_loi__" ? "Open Ground Lease LOI" : "Open Contract"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenerateContractCard({ record, onClose, onGenerated }) {
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const price = record.max_offer || record.asking_price || record.list_price || "";
  const ownerName = record.owner_name || `${record.owner_first || ""} ${record.owner_last || ""}`.trim();
  const propAddr = record.street_address || record.address || [record.city, record.state].filter(Boolean).join(", ") || "";
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({
    effective_date: today, seller_name: ownerName, buyer_state: record.state || "",
    property_address: propAddr,
    apn: record.apn || "", purchase_price: price ? String(price) : "",
    earnest_deposit: "", escrow_agent: "", closing_date: "",
    acreage: record.lot_size_acres || "", zoning: record.zoning || "",
    seller_email: record.owner_email || "", seller_phone: record.owner_phone || "",
  });
  useEffect(() => {
    api.get("/contracts/library-templates")
      .then((r) => { const its = r.data.items || []; setTemplates(its); if (its[0]) setTemplateId(its[0].id); })
      .catch(() => setTemplates([]));
  }, []);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const FIELDS = [
    ["property_address", "Property Address"], ["apn", "APN"], ["seller_name", "Seller Name"],
    ["purchase_price", "Purchase Price"], ["earnest_deposit", "Earnest Deposit"], ["effective_date", "Effective Date"],
    ["closing_date", "Closing Date"], ["escrow_agent", "Escrow Agent"], ["buyer_state", "Buyer LLC State"],
    ["acreage", "Acreage"], ["zoning", "Zoning"], ["seller_email", "Seller Email"], ["seller_phone", "Seller Phone"],
  ];
  const activeTpl = templates.find((t) => t.id === templateId);
  const generate = async () => {
    setBusy(true);
    try {
      const nm = activeTpl?.name || "Land Purchase Agreement";
      const { data } = await api.post("/contracts/generate", { record_id: record.id, template_id: templateId, name: nm, fields: f });
      const res = await api.get(`/documents/${data.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${nm}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Contract generated & saved to Documents");
      onGenerated && onGenerated();
      onClose && onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-primary/30 bg-secondary/30 p-4" data-testid="generate-contract-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-sm font-semibold flex items-center gap-2"><FileSignature className="h-4 w-4 text-primary" />Generate Contract</div>
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="gc-close">Close</Button>
      </div>
      <div className="space-y-3" data-testid="generate-contract-form">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Contract Template</div>
          {templates.length === 0 ? (
            <div className="text-xs text-muted-foreground">No templates. Upload a contract in Operations → Contracts first.</div>
          ) : (
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="gc-template"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_docx ? "" : " (PDF)"}</option>)}
            </select>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, label]) => (
            <div key={k}>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
              <Input value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} type={k.includes("date") ? "date" : "text"} className="h-8 text-sm" data-testid={`gc-${k}`} />
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={generate} disabled={busy || templates.length === 0} data-testid="gc-generate-submit">{busy ? "Generating…" : "Generate & Download PDF"}</Button>
        </div>
      </div>
    </div>
  );
}

function AnalysisSnapshot({ record, resource, related, onOwnerClick, refreshKey, selectedStrategyKey }) {
  const isAcq = resource === "acquisition_pipeline";
  const [counts, setCounts] = useState({});
  useEffect(() => {
    if (!record?.id) return;
    api.get(`/tasks?record_id=${record.id}&limit=300`)
      .then((r) => {
        const c = {};
        for (const t of (r.data.items || [])) {
          if (!t.source) continue;
          const s = (c[t.source] = c[t.source] || { done: 0, total: 0 });
          s.total += 1;
          if (t.status === "Completed") s.done += 1;
        }
        setCounts(c);
      })
      .catch(() => {});
  }, [record?.id, refreshKey]);
  const liveGate = (baseline, source) => {
    const b = (baseline || "").toUpperCase();
    const c = counts[source];
    if (!c || c.total === 0) return b;
    if (c.done >= c.total) return "GREEN";
    if (b === "RED") return "RED";
    return "YELLOW";
  };
  const base = computeUnderwritingStatus(record);
  const isSub = isSubdivisionActive(record);
  const roll = isSub ? computeSubdivision(record.subdivision) : null;
  // Snapshot financials follow the strategy the user is viewing in Exit Analysis (falls back
  // to the Primary strategy when nothing is selected), so the header reflects the chosen exit.
  const primaryEcon = strategyEconomicsFor(record, selectedStrategyKey) || primaryStrategyEconomics(record);
  const arv = primaryEcon ? (primaryEcon.revenue || 0) : (isSub ? roll.totalArv : base.arv);
  const allIn = primaryEcon ? (primaryEcon.allIn || 0) : (isSub ? roll.allIn : base.allIn);
  const projectedProfit = primaryEcon ? (primaryEcon.profit || 0) : (isSub ? roll.profit : base.projectedProfit);
  const pass = primaryEcon ? (primaryEcon.margin ?? 0) >= 10 : (isSub ? (roll.margin ?? 0) >= 10 : base.pass);
  const margin = arv > 0 ? (projectedProfit / arv) * 100 : null;
  const owner = related?.owner;
  const money = (v) => (arv > 0 ? formatCurrency(Math.round(v)) : "—");
  const verdictCls = (v) => v === "GO" ? "text-primary" : v === "CAUTION" ? "text-amber-400" : v === "NO-GO" ? "text-destructive" : "text-muted-foreground";
  const anaVerdict = (record.angela_analysis?.verdict || "").toUpperCase();
  const gateV = (raw) => { const u = (raw || "").toUpperCase(); return u === "GREEN" ? "GO" : u === "YELLOW" ? "CAUTION" : u === "RED" ? "NO-GO" : u; };
  const ddVerdict = gateV(liveGate(record.dd_verdict || record.dd_analysis?.verdict, "angela_due_diligence"));
  const feasVerdict = gateV(liveGate(record.feasibility_verdict || record.feasibility_analysis?.verdict, "angela_feasibility"));
  const finVerdict = gateV(liveGate(record.financial_verdict || record.financial_analysis?.verdict, "angela_financial_review"));
  const financials = [
    { key: "arv", icon: TrendingUp, label: primaryEcon ? `${primaryEcon.label} · ${primaryEcon.key === "build_sfr" ? "GDV" : "Revenue"}` : (isSub ? `Total GDV · ${roll.count} lots` : "GDV"), value: arv > 0 ? formatCurrency(arv) : "—" },
    { key: "all-in", icon: DollarSign, label: "All-In Cost", value: money(allIn) },
    { key: "net-profit", icon: Target, label: "Net Profit", value: money(projectedProfit), cls: projectedProfit >= 0 ? "text-primary" : "text-destructive" },
    { key: "margin", icon: Target, label: "Profit Margin", value: margin == null ? "—" : `${margin.toFixed(1)}%`, cls: (margin ?? 0) >= 10 ? "text-primary" : (margin ?? 0) >= 5 ? "text-amber-400" : "text-destructive" },
  ];
  // Property Analysis shows only the Analysis verdict (does the deal make sense).
  const analysisGate = { key: "analysis", icon: ScanSearch, label: "Analysis", value: anaVerdict || "Not run", cls: anaVerdict ? verdictCls(anaVerdict) : "text-muted-foreground" };
  // Offer snapshot — shows the strategy the user selected in the Land Offer module.
  const offerStratLabels = { flip: "Land Flip", "pad-ready": "Pad Ready", "build-ready": "Build Ready", "vertical-build": "Vertical Build", "over-asking-jv": "JV Max" };
  const offerStrat = offerStratLabels[record.land_offer_strategy] || null;
  const offerGate = { key: "offer", icon: FileSignature, label: "Offer", value: offerStrat || "Not set", cls: offerStrat ? "text-primary" : "text-muted-foreground" };
  // Due Diligence / Feasibility / Financial Review verdicts now live on the Acquisition snapshot.
  const acqGateItems = [
    { key: "due-diligence", icon: ClipboardCheck, label: "Due Diligence", value: ddVerdict || "Not run", cls: ddVerdict ? verdictCls(ddVerdict) : "text-muted-foreground" },
    { key: "feasibility", icon: ClipboardCheck, label: "Feasibility", value: feasVerdict || "Not run", cls: feasVerdict ? verdictCls(feasVerdict) : "text-muted-foreground" },
    { key: "financial", icon: ClipboardCheck, label: "Financial Review", value: finVerdict || "Not run", cls: finVerdict ? verdictCls(finVerdict) : "text-muted-foreground" },
  ];
  const num = (v) => { const n = Number(String(v ?? "").replace(/[^0-9.-]/g, "")); return isNaN(n) ? null : n; };
  const asking = num(record.asking_price) ?? num(record.list_price);
  const maxOffer = num(record.max_offer) ?? num(record.target_offer);
  const acqItems = [
    { key: "asking-price", icon: DollarSign, label: "Asking Price", value: asking ? formatCurrency(asking) : "—" },
    { key: "max-offer", icon: DollarSign, label: "Max Offer", value: maxOffer ? formatCurrency(maxOffer) : "—" },
  ];
  const maxLandOffer = primaryEcon && primaryEcon.maxLandBasis != null ? Math.max(0, primaryEcon.maxLandBasis) : num(record.max_land_purchase_price);
  const actualLandOffer = num(record.actual_land_offer_price);
  const landOfferItems = [
    { key: "max-land-offer", icon: DollarSign, label: "Max Land Offer Price", value: maxLandOffer ? formatCurrency(maxLandOffer) : "—" },
    { key: "actual-land-offer", icon: DollarSign, label: "Actual Land Offer Price", value: actualLandOffer ? formatCurrency(actualLandOffer) : "—" },
  ];
  const items = isAcq ? [...financials, ...acqItems, ...acqGateItems] : [...financials, ...landOfferItems, analysisGate, offerGate];
  return (
    <div className="rounded-lg border border-border bg-secondary/30 px-5 py-3.5 mb-6" data-testid="analysis-snapshot">
      <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        <Radar className="h-3.5 w-3.5" />Property Snapshot
      </div>
      <div className="flex flex-wrap justify-between gap-x-4 gap-y-3">
        {items.map(({ key, icon: Icon, label, value, onClick, cls }) => (
          <div key={key} className="min-w-0 max-w-[220px]" data-testid={`snapshot-${key}`}>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5 whitespace-nowrap">
              <Icon className="h-3.5 w-3.5 shrink-0" />{label}
            </div>
            {onClick ? (
              <button onClick={onClick} className="text-sm font-medium text-primary hover:underline truncate text-left" data-testid={`snapshot-${key}-value`}>{value}</button>
            ) : (
              <div className={`text-sm font-medium truncate ${cls || "text-foreground"}`} data-testid={`snapshot-${key}-value`}>{value}</div>
            )}
          </div>
        ))}
      </div>
      {isSub && !isAcq && (
        <p className="mt-3 text-[11px] text-muted-foreground" data-testid="snapshot-subdivision-note">
          These deal totals come from your per-lot costs in the Subdivision / Multi-Lot section. The Underwriting Model below is the cap-based view (each cost capped as a % of GDV), so its Preliminary figures can differ.
        </p>
      )}
    </div>
  );
}

function SystemInformation({ record, related, onOwnerClick }) {
  const [open, setOpen] = useState(false);
  const owner = related?.owner;
  const rows = [
    { key: "owner", label: "Owner", value: owner ? owner.name : "Unassigned", onClick: onOwnerClick },
    { key: "record-id", label: "Record ID", value: record.record_id ? formatRecordId(record.record_id) : (record.id || "—") },
    { key: "created", label: "Created", value: record.created_at ? formatDateTime(record.created_at) : "—" },
    { key: "updated", label: "Last Updated", value: record.updated_at ? formatDateTime(record.updated_at) : "—" },
  ];
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="system-information">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full px-5 py-4 border-b border-border flex items-center justify-between hover:bg-accent/40 transition-colors" data-testid="system-information-toggle">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">System Information</h3>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
          {rows.map(({ key, label, value, onClick }) => (
            <div key={key} className="min-w-0" data-testid={`sysinfo-${key}`}>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-0.5">{label}</div>
              {onClick ? (
                <button onClick={onClick} className="text-sm font-medium text-primary hover:underline truncate text-left" data-testid={`sysinfo-${key}-value`}>{value}</button>
              ) : (
                <div className="text-sm font-medium text-foreground truncate" data-testid={`sysinfo-${key}-value`}>{value}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}




function AcquisitionFinancialSummary({ record }) {
  const arv = Number(record.approved_arv) || 0;
  const approvedMaxLand = Number(record.approved_max_land_price) || 0;
  const approvedAllIn = Number(record.approved_all_in_cost) || 0;
  const approvedMinPct = Number(record.approved_min_profit_pct) || 10;
  const approvedProfit = arv - approvedAllIn;
  const approvedMargin = arv > 0 ? (approvedProfit / arv) * 100 : null;

  const purchasePrice = Number(record.actual_purchase_price) || Number(record.target_purchase_price) || 0;
  const acqCosts = Number(record.acquisition_costs) || 0;
  const totalLandBasis = purchasePrice + acqCosts;
  // Reflow: swap the approved land cap for the actual land basis.
  const currentAllIn = (approvedAllIn - approvedMaxLand) + totalLandBasis;
  const currentProfit = arv - currentAllIn;
  const currentMargin = arv > 0 ? (currentProfit / arv) * 100 : null;

  const landExceeded = approvedMaxLand > 0 && purchasePrice > approvedMaxLand;
  const profitViolated = arv > 0 && currentMargin != null && currentMargin < approvedMinPct;
  const pass = arv > 0 && !landExceeded && !profitViolated;
  const has = arv > 0;
  const money = (v) => (has && v != null && !isNaN(v) ? formatCurrency(Math.round(v)) : "—");
  const pct = (v) => (v == null ? "—" : `${v.toFixed(1)}%`);

  const warnings = [];
  if (landExceeded) warnings.push(`Max Land Price Exceeded — purchase price ${money(purchasePrice)} is above the approved maximum ${money(approvedMaxLand)}`);
  if (profitViolated) warnings.push(`Minimum Profit Violated — projected margin ${pct(currentMargin)} is below the approved floor of ${approvedMinPct}%`);

  const banner = (
    <div className={`px-6 py-4 border-b ${pass ? "bg-primary/10 border-primary/30" : "bg-destructive/10 border-destructive/40"}`} data-testid="acquisition-status-banner">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2.5">
          {pass ? <ShieldCheck className="h-5 w-5 text-primary" /> : <AlertTriangle className="h-5 w-5 text-destructive" />}
          <div>
            <div className={`text-sm font-semibold ${pass ? "text-primary" : "text-destructive"}`} data-testid="acquisition-status-label">
              {has ? (pass ? "Acquisition Within Approved Guardrails" : "Guardrail Warning") : "No approved underwriting snapshot"}
            </div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Purchase price ≤ max land · margin ≥ {approvedMinPct}%</div>
          </div>
        </div>
        <div className="flex gap-8 text-right">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total Land Basis</div>
            <div className={`text-sm font-semibold ${landExceeded ? "text-destructive" : "text-foreground"}`} data-testid="acquisition-total-land-basis">{money(totalLandBasis)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Current Profit</div>
            <div className={`text-sm font-semibold ${profitViolated ? "text-destructive" : "text-primary"}`} data-testid="acquisition-current-profit">{money(currentProfit)}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Current Margin</div>
            <div className={`text-sm font-semibold ${profitViolated ? "text-destructive" : "text-primary"}`} data-testid="acquisition-current-margin">{pct(currentMargin)}</div>
          </div>
        </div>
      </div>
      {warnings.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="acquisition-warnings">
          {warnings.map((w, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-destructive"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{w}</li>
          ))}
        </ul>
      )}
    </div>
  );

  const rows = [
    { key: "arv", label: "GDV", approved: money(arv), current: money(arv) },
    { key: "land", label: "Land / Purchase Price", approved: money(approvedMaxLand), current: money(purchasePrice), curCls: landExceeded ? "text-destructive" : "" },
    { key: "acq-costs", label: "Acquisition Costs", approved: "—", current: money(acqCosts) },
    { key: "land-basis", label: "Total Land Basis", approved: money(approvedMaxLand), current: money(totalLandBasis), curCls: landExceeded ? "text-destructive" : "" },
    { key: "all-in", label: "All-In Cost", approved: money(approvedAllIn), current: money(currentAllIn) },
    { key: "profit", label: "Net Developer Profit", approved: money(approvedProfit), current: money(currentProfit), curCls: profitViolated ? "text-destructive" : "text-primary" },
    { key: "margin", label: "Profit Margin", approved: pct(approvedMargin), current: pct(currentMargin), curCls: profitViolated ? "text-destructive" : "text-primary" },
  ];

  return (
    <CollapseCard
      testid="acquisition-financial-summary-card"
      title="Acquisition Financial Summary"
      banner={banner}
      defaultOpen
      right={<span className="text-[11px] uppercase tracking-wide text-muted-foreground">Approved snapshot vs current</span>}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="px-6 py-2.5 font-medium">Metric</th>
            <th className="px-6 py-2.5 font-medium text-right">Approved</th>
            <th className="px-6 py-2.5 font-medium text-right">Current</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-border/50" data-testid={`acq-fin-row-${r.key}`}>
              <td className="px-6 py-3 text-foreground">{r.label}</td>
              <td className="px-6 py-3 text-right font-medium text-muted-foreground" data-testid={`acq-fin-${r.key}-approved`}>{r.approved}</td>
              <td className={`px-6 py-3 text-right font-semibold ${r.curCls || "text-foreground"}`} data-testid={`acq-fin-${r.key}-current`}>{r.current}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </CollapseCard>
  );
}

function PlanningOverview({ record }) {
  const money = (v) => (v != null && v !== "" && !isNaN(v) ? formatCurrency(Math.round(Number(v))) : "—");
  const txt = (v) => (v != null && v !== "" ? String(v) : "—");
  const items = [
    { key: "address", label: "Property Address", value: txt(record.planning_property_address) },
    { key: "apn", label: "APN", value: txt(record.planning_apn) },
    { key: "lot-size", label: "Lot Size", value: record.planning_lot_size_sqft ? `${Number(record.planning_lot_size_sqft).toLocaleString()} SF` : "—" },
    { key: "acq-status", label: "Acquisition Status", value: txt(record.planning_acquisition_status) },
    { key: "contract-price", label: "Contract Purchase Price", value: money(record.planning_contract_price) },
    { key: "dd-deadline", label: "Due Diligence Deadline", value: record.due_diligence_deadline ? formatDate(record.due_diligence_deadline) : "—" },
    { key: "closing", label: "Scheduled Closing", value: record.scheduled_closing_date ? formatDate(record.scheduled_closing_date) : "—" },
    { key: "start-date", label: "Planning Start", value: record.planning_start_date ? formatDate(record.planning_start_date) : "—" },
    { key: "owner", label: "Planning Owner", value: txt(record.planning_owner) },
    { key: "status", label: "Planning Status", value: txt(record.lead_status) },
  ];
  return (
    <div className="rounded-xl border border-border bg-card" data-testid="planning-overview-card">
      <div className="px-6 py-3.5 border-b border-border flex items-center gap-2">
        <Home className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">Planning Started — Property Snapshot</h3>
        <span className="ml-auto text-[11px] uppercase tracking-wide text-muted-foreground">Brought forward from Property Analysis + Acquisition</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-4 px-6 py-5">
        {items.map((it) => (
          <div key={it.key} data-testid={`planning-overview-${it.key}`}>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{it.label}</div>
            <div className="text-sm font-semibold text-foreground mt-0.5 break-words">{it.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BuildableEnvelope({ record }) {
  const n = (k) => Number(record[k]) || 0;
  const grossLot = n("be_gross_lot_area") || n("sv_gross_lot_sqft") || n("planning_lot_size_sqft");
  const lotWidth = n("sv_lot_width");
  const lotDepth = n("sv_lot_depth");
  const front = n("be_required_front_setback") || n("lz_front_setback");
  const rear = n("be_required_rear_setback") || n("lz_rear_setback");
  const leftSide = n("lz_left_side_setback");
  const rightSide = n("lz_right_side_setback");
  const sideTotal = n("be_required_side_setbacks") || (leftSide + rightSide);
  const bWidth = lotWidth ? Math.max(0, lotWidth - (leftSide || sideTotal / 2) - (rightSide || sideTotal / 2)) : 0;
  const bDepth = lotDepth ? Math.max(0, lotDepth - front - rear) : 0;
  let envelope = bWidth && bDepth ? bWidth * bDepth : 0;
  envelope = Math.max(0, envelope - n("be_easement_area") - n("be_restricted_area") - n("be_environmental_restricted_area"));
  const coveragePct = n("lz_max_lot_coverage");
  const footprintByCoverage = coveragePct && grossLot ? (grossLot * coveragePct) / 100 : 0;
  const maxFootprint = envelope && footprintByCoverage ? Math.min(envelope, footprintByCoverage) : (envelope || footprintByCoverage);
  const stories = n("lz_max_stories") || n("be_max_stories") || 1;
  const far = n("lz_max_far");
  const maxFloorArea = n("be_max_allowed_floor_area") || (far && grossLot ? grossLot * far : maxFootprint * stories);
  const estLiving = n("be_est_max_living_sqft") || maxFloorArea;
  const sf = (v) => (v ? `${Math.round(v).toLocaleString()} SF` : "—");

  const rows = [
    { key: "gross-lot", label: "Gross Lot Area", value: sf(grossLot), calc: false },
    { key: "envelope", label: "Buildable Envelope", value: sf(envelope), calc: true },
    { key: "footprint", label: "Maximum Building Footprint", value: sf(maxFootprint), calc: true },
    { key: "floor-area", label: "Maximum Allowed Floor Area", value: sf(maxFloorArea), calc: true },
    { key: "living", label: "Estimated Maximum Living Sq Ft", value: sf(estLiving), calc: true },
    { key: "stories", label: "Maximum Stories", value: stories ? String(stories) : "—", calc: false },
  ];
  return (
    <CollapseCard
      testid="buildable-envelope-card"
      title="Buildable Envelope"
      defaultOpen
      right={<span className="text-[11px] uppercase tracking-wide text-muted-foreground">Calculated estimates until survey-confirmed</span>}
    >
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4 px-6 py-5">
        {rows.map((r) => (
          <div key={r.key} data-testid={`buildable-${r.key}`}>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              {r.label}
              {r.calc && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 font-medium">EST</span>}
            </div>
            <div className="text-sm font-semibold text-foreground mt-0.5">{r.value}</div>
          </div>
        ))}
      </div>
    </CollapseCard>
  );
}

export default function RecordDetail() {
  const { resource: rawResource, id } = useParams();
  // "analysis" is the canonical name for the Property Analysis module; it maps to the legacy
  // physical collection "leads". Normalize so /record/analysis and /record/leads behave identically.
  const resource = rawResource === "analysis" ? "leads" : rawResource;
  const navigate = useNavigate();
  const cfg = getRecordConfig(resource);
  const [record, setRecord] = useState(null);
  const [usageBusy, setUsageBusy] = useState(false);
  const generateUsage = async () => {
    setUsageBusy(true);
    const t = toast.loading("Angela is reviewing the zoning…");
    try {
      const { data } = await api.post("/ai/zoning-usage", { resource, record_id: id });
      setRecord((r) => (r ? { ...r, usage: data.usage } : r));
      toast.success("Angela added the possible uses", { id: t });
    } catch (e) {
      toast.error(apiError(e), { id: t });
    } finally {
      setUsageBusy(false);
    }
  };
  const [marketSaving, setMarketSaving] = useState(false);
  const [related, setRelated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activityRefresh, setActivityRefresh] = useState(0);
  const [docRefresh, setDocRefresh] = useState(0);
  const [contractOpen, setContractOpen] = useState(false);
  const bumpActivity = () => setActivityRefresh((x) => x + 1);
  const [statusSaving, setStatusSaving] = useState(false);
  const [screening, setScreening] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmConvertLead, setConfirmConvertLead] = useState(false);
  const [showConceptPlan, setShowConceptPlan] = useState(false);
  const [ownerDialog, setOwnerDialog] = useState(false);
  const [confirmAuto, setConfirmAuto] = useState(null);
  const requestConfirm = (cfg) => setConfirmAuto(cfg);
  const runConfirmed = () => {
    const fn = confirmAuto?.run;
    setConfirmAuto(null);
    if (fn) fn();
  };
  const confirmScreening = () => requestConfirm({
    title: record?.screening_decision ? "Re-run Screening?" : "Run Screening?",
    description: "Angela will screen this property with AI and set a Qualified / Not Qualified decision. This may take a moment.",
    cta: record?.screening_decision ? "Re-run Screening" : "Run Screening",
    run: runScreening,
  });
  const confirmAnalyze = () => requestConfirm({
    title: "Run Analysis?",
    description: "Angela will underwrite this deal and produce a GO / CAUTION / NO-GO verdict, advancing the record to Preliminary Review.",
    cta: "Run Analysis",
    run: runAngelaVerdict,
  });
  const confirmDueDiligence = () => requestConfirm({
    title: "Run Due Diligence?",
    description: "Angela will research authoritative agency contacts online and generate step-by-step due-diligence tasks with a GO / CAUTION / NO-GO verdict. This runs in the background (~1–3 min).",
    cta: "Run Due Diligence",
    run: runDueDiligence,
  });
  const confirmFeasibility = () => requestConfirm({
    title: "Run Feasibility?",
    description: "Angela will assess whether the intended home can physically & legally be built, produce a GO / CAUTION / NO-GO verdict, and create tasks. Runs in the background (~1–3 min).",
    cta: "Run Feasibility",
    run: () => runGate("feasibility"),
  });
  const confirmFinancial = () => requestConfirm({
    title: "Run Financial Review?",
    description: "Angela will review the deal economics (GDV vs all-in, margin, unbudgeted costs), produce a GO / CAUTION / NO-GO verdict, and create tasks. Runs in the background.",
    cta: "Run Financial Review",
    run: () => runGate("financial"),
  });
  const [users, setUsers] = useState([]);
  const [ownerDraft, setOwnerDraft] = useState("");
  const [ownerSaving, setOwnerSaving] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [engageTrigger, setEngageTrigger] = useState(null);
  const [oppLead, setOppLead] = useState(null);
  const [moduleLinks, setModuleLinks] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [zoningLoading, setZoningLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const runImport = async () => {
    setZoningLoading(true);
    setImportResult(null);
    try {
      const res = await api.post("/ai/import_property", { resource, record_id: id });
      setImportResult(res.data);
      if ((res.data?.retrieved || 0) > 0) load();
    } catch (e) {
      setImportResult({ error: apiError(e) });
    } finally {
      setZoningLoading(false);
    }
  };
  const [portalOpen, setPortalOpen] = useState(false);
  const [portalUrl, setPortalUrl] = useState("");
  const [portalBusy, setPortalBusy] = useState(false);
  const generateInvestorPortal = async () => {
    setPortalBusy(true);
    // Open the tab synchronously (on the click) so popup blockers don't block it;
    // point it at the portal once the token comes back.
    const tab = window.open("", "_blank");
    try {
      toast.success("Investor link ready — Angela is writing the deal memo in the background");
      const res = await api.post("/ai/investor_portal", { resource, record_id: id });
      const url = `${window.location.origin}${res.data.path}`;
      setPortalUrl(url);
      if (tab) tab.location = url; else window.open(url, "_blank");
    } catch (e) {
      if (tab) tab.close();
      toast.error(apiError(e));
    } finally {
      setPortalBusy(false);
    }
  };
  const copyPortalUrl = () => {
    navigator.clipboard?.writeText(portalUrl);
    toast.success("Investor link copied to clipboard");
  };

  const [strategyOpen, setStrategyOpen] = useState(0);
  const [exitStratKey, setExitStratKey] = useState(null);
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [typeDraft, setTypeDraft] = useState("Single-Family Residence");
  const [unitsDraft, setUnitsDraft] = useState("1");
  const [effDraft, setEffDraft] = useState(String(DEFAULT_EFFICIENCY));
  const [psfDraft, setPsfDraft] = useState(String(DEFAULT_NEW_CONSTRUCTION_PSF));
  const [typeBusy, setTypeBusy] = useState(false);
  const openTypeDialog = () => {
    const dt = record.development_type || "Single-Family Residence";
    setTypeDraft(dt);
    setUnitsDraft(String(devTypeUnits(record)));
    setEffDraft(String(Number(record.unit_efficiency_pct) || DEFAULT_EFFICIENCY));
    setPsfDraft(String(Number(cleanNum(record.new_construction_psf)) || DEFAULT_NEW_CONSTRUCTION_PSF));
    setShowTypeDialog(true);
  };
  const applyPropertyType = async () => {
    const m = devTypeMeta(typeDraft);
    const units = m.lock ? m.units : Math.max(1, Math.min(200, parseInt(unitsDraft) || m.units || 1));
    const payload = { development_type: typeDraft, unit_count: units };
    if (isAttachedType(typeDraft)) {
      payload.unit_efficiency_pct = Math.min(Math.max(parseInt(effDraft) || DEFAULT_EFFICIENCY, 50), 100);
      payload.new_construction_psf = Math.max(parseInt(psfDraft) || DEFAULT_NEW_CONSTRUCTION_PSF, 1);
    }
    setTypeBusy(true);
    try {
      const updated = await updateItem(resource, id, payload);
      setRecord(updated);
      setShowTypeDialog(false);
      toast.success(`Property type set to ${typeDraft}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setTypeBusy(false); }
  };
  const [verdictBusy, setVerdictBusy] = useState(false);
  const [verdict, setVerdict] = useState(record?.angela_analysis || null);
  const [verdictAt, setVerdictAt] = useState(record?.angela_analyzed_at || null);
  useEffect(() => {
    if (record?.angela_analysis && !verdict) {
      setVerdict(record.angela_analysis);
      setVerdictAt(record.angela_analyzed_at || null);
    }
  }, [record]);
  const [angelaMode, setAngelaMode] = useState("DEEP");
  useEffect(() => {
    api.get("/ab/default-mode").then((r) => { if (r.data?.mode) setAngelaMode(r.data.mode); }).catch(() => {});
  }, []);
  // Keep the Investor Portal's Exit-Strategy Snapshot synced to the live analysis. Recompute
  // all four strategies with the SAME engine the Exit Analysis uses and persist to
  // record.exit_strategy_snapshot whenever the numbers change. The portal only presents this
  // — one source of truth, no second underwriting engine, no drift.
  const exitSnapSig = useRef(null);
  useEffect(() => {
    if (!record || resource !== "leads" || !id) return;
    let snap;
    try { snap = buildExitSnapshot(record); } catch (e) { return; }
    const stripTs = (o) => { if (!o) return null; const { generated_at, ...rest } = o; return JSON.stringify(rest); };
    const sig = stripTs(snap);
    if (sig === exitSnapSig.current) return;
    if (sig === stripTs(record.exit_strategy_snapshot)) { exitSnapSig.current = sig; return; }
    exitSnapSig.current = sig;
    updateItem(resource, id, { exit_strategy_snapshot: snap })
      .then(() => setRecord((r) => (r ? { ...r, exit_strategy_snapshot: snap } : r)))
      .catch(() => { exitSnapSig.current = null; });
  }, [record, resource, id]);
  const [generatingOffer, setGeneratingOffer] = useState(false);
  const [landOfferSignal, setLandOfferSignal] = useState(0);
  const generateOffer = async () => {
    setGeneratingOffer(true);
    const t = toast.loading("Angela is generating the offer…");
    try {
      let rec = record;
      try {
        const { data } = await api.post("/ai/adjust-land-value", { resource, record_id: id });
        rec = { ...rec, land_adjusted_market_value: data.adjusted_market_land_value, land_value_adjustment_notes: data.rationale };
      } catch (e) { /* comps may be absent — proceed with current values */ }
      const calc = computeLandOffer(rec);
      const primaryEcon = primaryStrategyEconomics(rec);
      const appliedMax = primaryEcon && primaryEcon.maxLandBasis != null ? Math.max(0, primaryEcon.maxLandBasis) : calc.absoluteMax;
      const patch = {};
      if (appliedMax != null) patch.max_land_purchase_price = appliedMax;
      if (calc.opening != null) patch.recommended_opening_offer = calc.opening;
      const ov = computeOfferVerdict(rec);
      if (ov.verdict) { patch.offer_verdict = ov.verdict; patch.offer_verdict_note = ov.note; patch.offer_verdict_at = new Date().toISOString(); }
      const updated = Object.keys(patch).length ? await updateItem(resource, id, patch) : rec;
      setRecord({ ...updated, land_adjusted_market_value: rec.land_adjusted_market_value, land_value_adjustment_notes: rec.land_value_adjustment_notes });
      bumpActivity();
      setLandOfferSignal((s) => s + 1);
      if (appliedMax != null) {
        toast.success(`Offer generated — ${ov.verdict ? ov.verdict + " · " : ""}max ${formatCurrency(appliedMax)}${primaryEcon ? " (" + primaryEcon.label + ")" : ""}, opening ${formatCurrency(calc.opening || 0)}`, { id: t });
      } else {
        toast.message("Add land comps + development economics, then generate again.", { id: t });
      }
    } catch (e) {
      toast.error(apiError(e), { id: t });
    } finally { setGeneratingOffer(false); }
  };

  const runAngelaVerdict = async () => {
    setVerdictBusy(true);
    try {
      const _b = computeStrategy(record, "build_sfr");
      const strategies = [
        { key: "flip", label: "Land Flip", max_land_offer: computeStrategy(record, "land_flip").maxLandBasis },
        { key: "pad-ready", label: "Pad Ready", max_land_offer: computeStrategy(record, "entitle").maxLandBasis },
        { key: "build-ready", label: "Build Ready", max_land_offer: computeStrategy(record, "subdivide").maxLandBasis },
        { key: "vertical-build", label: "Vertical Build", max_land_offer: _b.maxLandBasis },
        { key: "over-asking-jv", label: "Over-Asking JV", max_land_offer: (_b.maxLandBasis != null && _b.requiredProfit != null) ? _b.maxLandBasis + _b.requiredProfit : null },
      ];
      const res = await api.post(`/ai/analyze_deal?mode=${angelaMode}`, { resource, record_id: id, strategies });
      if (res.data.status === "running") {
        toast.message("Dual-AI verification running", { description: "Two models are cross-checking — this can take a minute." });
        for (let i = 0; i < 40; i++) {
          await new Promise((r) => setTimeout(r, 4000));
          const rec = await api.get(`/${resource}/${id}`).then((r) => r.data).catch(() => null);
          if (rec && rec.angela_status === "complete") {
            setVerdict(rec.angela_analysis);
            setVerdictAt(rec.angela_analyzed_at);
            setRecord((r) => (r ? { ...r, ...rec } : rec));
            const ar = rec.angela_ai_review;
            if (ar) toast[ar.requires_human_verification ? "warning" : "success"](
              `AI consensus: ${ar.agreement_status}`,
              { description: ar.requires_human_verification ? "Models disagree — human verification required." : "Both models aligned." });
            break;
          }
          if (rec && rec.angela_status === "error") { toast.error("Angela could not complete the analysis."); break; }
        }
        return;
      }
      setVerdict(res.data.analysis);
      setVerdictAt(res.data.analyzed_at);
      setRecord((r) => (r ? { ...r, angela_analysis: res.data.analysis, angela_ai_review: res.data.ai_review, angela_analyzed_at: res.data.analyzed_at } : r));
      if (res.data.ai_review) {
        const ar = res.data.ai_review;
        toast[ar.requires_human_verification ? "warning" : "success"](`AI consensus: ${ar.agreement_status}`);
      }
      // Running Analyze on a Property Analysis moves it from "New" to "Analyze".
      if (resource === "leads" && record?.lead_status === "New") {
        try {
          const patch = { lead_status: "Analyze", status: "Analyze", analysis_start_date: record?.analysis_start_date || new Date().toISOString().slice(0, 10) };
          const updated = await updateItem("leads", id, patch);
          setRecord(updated);
          bumpActivity();
        } catch { /* status advance is best-effort */ }
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setVerdictBusy(false);
    }
  };

  const [ddBusy, setDdBusy] = useState(false);
  const [ddAnalysis, setDdAnalysis] = useState(record?.dd_analysis || null);
  const [ddAt, setDdAt] = useState(record?.dd_analyzed_at || null);
  const [taskReload, setTaskReload] = useState(0);
  const [ddRefresh, setDdRefresh] = useState(0);
  const [gateCounts, setGateCounts] = useState({});
  useEffect(() => {
    if (resource !== "leads" || !id) return;
    api.get(`/tasks?record_id=${id}&limit=300`)
      .then((r) => {
        const c = {};
        for (const t of (r.data.items || [])) {
          if (!t.source) continue;
          const s = (c[t.source] = c[t.source] || { done: 0, total: 0 });
          s.total += 1;
          if (t.status === "Completed") s.done += 1;
        }
        setGateCounts(c);
      })
      .catch(() => {});
  }, [resource, id, ddRefresh]);
  useEffect(() => {
    if (record?.dd_analysis && !ddAnalysis) {
      setDdAnalysis(record.dd_analysis);
      setDdAt(record.dd_analyzed_at || null);
    }
  }, [record]);
  const runDueDiligence = async () => {
    setDdBusy(true);
    try {
      await api.post("/ai/due_diligence", { resource, record_id: id });
      toast.info("Angela is researching agencies and building due-diligence tasks…");
      // Poll the record until the background run finishes (web search ~40-90s).
      let done = false;
      for (let i = 0; i < 60 && !done; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data } = await api.get(`/${resource}/${id}`);
          if (data?.dd_status === "ready") {
            setDdAnalysis(data.dd_analysis || null);
            setDdAt(data.dd_analyzed_at || null);
            setRecord(data);
            setTaskReload((x) => x + 1);
            bumpActivity();
            const n = data.dd_analysis?.tasks_created || 0;
            toast.success(`Due diligence complete — ${n} task${n === 1 ? "" : "s"} created`);
            done = true;
          } else if (data?.dd_status === "error") {
            toast.error("Angela could not complete due diligence. Please try again.");
            done = true;
          }
        } catch { /* keep polling */ }
      }
      if (!done) toast.error("Due diligence is taking longer than expected. Check back shortly.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDdBusy(false);
    }
  };

  // Generic Feasibility / Financial Review gate runner (mirrors due diligence).
  const [gateBusy, setGateBusy] = useState({});
  const [feas, setFeas] = useState(record?.feasibility_analysis || null);
  const [feasAt, setFeasAt] = useState(record?.feasibility_analyzed_at || null);
  const [fin, setFin] = useState(record?.financial_analysis || null);
  const [finAt, setFinAt] = useState(record?.financial_analyzed_at || null);
  useEffect(() => {
    if (record?.feasibility_analysis && !feas) { setFeas(record.feasibility_analysis); setFeasAt(record.feasibility_analyzed_at || null); }
    if (record?.financial_analysis && !fin) { setFin(record.financial_analysis); setFinAt(record.financial_analyzed_at || null); }
  }, [record]);
  // Inbound leads auto-enrich in the background — poll until the agents finish so
  // the newly populated fields appear without a manual refresh.
  useEffect(() => {
    if (resource !== "inbound_leads" || record?.enrichment_status !== "running") return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/${resource}/${id}`);
        if (data && data.enrichment_status !== "running") {
          setRecord(data);
          clearInterval(iv);
        }
      } catch { /* keep polling */ }
    }, 6000);
    return () => clearInterval(iv);
  }, [resource, id, record?.enrichment_status]);
  const [researching, setResearching] = useState(false);
  const toggleMarketChannel = async () => {
    const nextOn = !isLeadOnMarket(record);
    setMarketSaving(true);
    try {
      const updated = await updateItem(resource, id, nextOn ? { source_type: "ACTIVE" } : { source_type: "OFF-MARKET", property_source: "Off-Market" });
      setRecord(updated);
      toast.success(nextOn ? "Marked as On-Market" : "Marked as Off-Market");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setMarketSaving(false);
    }
  };
  const toggleHot = async () => {
    const next = !record.is_hot;
    try {
      const updated = await updateItem(resource, id, { is_hot: next });
      setRecord(updated);
      toast.success(next ? "Marked as Hot" : "Removed Hot flag");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const GATE_META = {
    feasibility: { endpoint: "/ai/feasibility", statusKey: "feasibility_status", analysisKey: "feasibility_analysis", atKey: "feasibility_analyzed_at", set: setFeas, setAt: setFeasAt, label: "Feasibility" },
    financial: { endpoint: "/ai/financial_review", statusKey: "financial_status", analysisKey: "financial_analysis", atKey: "financial_analyzed_at", set: setFin, setAt: setFinAt, label: "Financial Review" },
  };
  const runGate = async (gate) => {
    const m = GATE_META[gate];
    setGateBusy((b) => ({ ...b, [gate]: true }));
    try {
      await api.post(m.endpoint, { resource, record_id: id });
      toast.info(`Angela is running ${m.label}…`);
      let done = false;
      for (let i = 0; i < 60 && !done; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data } = await api.get(`/${resource}/${id}`);
          if (data?.[m.statusKey] === "ready") {
            m.set(data[m.analysisKey] || null);
            m.setAt(data[m.atKey] || null);
            setRecord(data);
            setTaskReload((x) => x + 1);
            setDdRefresh((x) => x + 1);
            bumpActivity();
            const n = data[m.analysisKey]?.tasks_created || 0;
            toast.success(`${m.label} complete — ${n} task${n === 1 ? "" : "s"} created`);
            done = true;
          } else if (data?.[m.statusKey] === "error") {
            toast.error(`Angela could not complete ${m.label}. Please try again.`);
            done = true;
          }
        } catch { /* keep polling */ }
      }
      if (!done) toast.error(`${m.label} is taking longer than expected. Check back shortly.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGateBusy((b) => ({ ...b, [gate]: false }));
    }
  };

  const normGate = (v) => { const u = (v || "").toUpperCase(); return u === "GREEN" ? "GO" : u === "YELLOW" ? "CAUTION" : u === "RED" ? "NO-GO" : u; };
  const liveGateRaw = (baseline, source) => {
    const b = (baseline || "").toUpperCase();
    const c = gateCounts[source];
    if (!c || c.total === 0) return b;
    if (c.done >= c.total) return "GREEN";
    if (b === "RED") return "RED";
    return "YELLOW";
  };
  const anaVerdict = (record?.angela_analysis?.verdict || "").toUpperCase();
  const ddVerdictNorm = normGate(liveGateRaw(record?.dd_verdict || record?.dd_analysis?.verdict, "angela_due_diligence"));
  const feasVerdictNorm = normGate(liveGateRaw(record?.feasibility_verdict || record?.feasibility_analysis?.verdict, "angela_feasibility"));
  const finVerdictNorm = normGate(liveGateRaw(record?.financial_verdict || record?.financial_analysis?.verdict, "angela_financial_review"));
  let overallStatus = "Under Review";
  if (record?.lead_status === "Not Approved") {
    overallStatus = "Not Approved";
  } else if ([anaVerdict, ddVerdictNorm, feasVerdictNorm, finVerdictNorm].every((g) => g === "GO")) {
    overallStatus = "Approved for Acquisition";
  }
  // Auto-advance the pipeline stage as gates pass (forward-only; never regresses,
  // never touches manual terminal outcomes). All four GO -> Approved.
  // Property Analysis only answers "does this deal make sense + max land offer".
  // Once Angela has produced a verdict, advance New -> Analyze. The offer / negotiation
  // stages after that are driven manually by the acquisitions team.
  useEffect(() => {
    if (resource !== "leads" || !record?.id) return;
    if (["Not Accepted", "Converted"].includes(record.lead_status)) return;
    if (record.angela_analysis && record.lead_status === "New") {
      updateItem("leads", record.id, { lead_status: "Analyze", status: "Analyze" })
        .then((u) => { setRecord(u); bumpActivity(); })
        .catch(() => {});
      return;
    }
    // A GO offer verdict (with an offer actually set) advances the deal to "Offer Approved".
    // Forward-only: only from New/Analyze, never regress a later/terminal stage.
    if (record.actual_land_offer_price != null && ["New", "Analyze"].includes(record.lead_status)) {
      if (computeOfferVerdict(record).verdict === "GO") {
        updateItem("leads", record.id, { lead_status: "Offer Approved", status: "Offer Approved" })
          .then((u) => { setRecord(u); bumpActivity(); toast.success("Offer is a GO — advanced to Offer Approved"); })
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, record?.id, record?.lead_status, record?.angela_analysis, record?.actual_land_offer_price]);
  const overallCls = overallStatus === "Approved for Acquisition"
    ? "text-emerald-500 bg-emerald-500/10 border-emerald-500/30"
    : overallStatus === "Not Approved"
    ? "text-red-500 bg-red-500/10 border-red-500/30"
    : "text-amber-400 bg-amber-500/10 border-amber-500/30";

  const [agentPickBusy, setAgentPickBusy] = useState(false);
  const populateAgentFromDirectory = async (contact) => {
    const name = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || contact.company_name || "";
    const patch = {
      listing_agent_contact: name,
      listing_agent_phone: formatPhoneInput(contact.mobile_phone || contact.business_phone || ""),
      listing_agent_email: contact.email || "",
    };
    setAgentPickBusy(true);
    try {
      const updated = await updateItem(resource, id, patch);
      setRecord(updated);
      toast.success(`Pulled ${name || "agent"} from Directory`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setAgentPickBusy(false); }
  };

  const launchCadence = async () => {
    setLaunching(true);
    try {
      await api.post(`/leads/${id}/cadence/launch`);
      toast.success("Cadence launched");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setLaunching(false); }
  };
  // On an opportunity, Call/Conversation act on its linked lead.
  const engageLead = resource === "opportunities" ? oppLead : record;
  const openDialer = () => setEngageTrigger({ type: "call", value: engageLead?.mobile_phone || engageLead?.business_phone || "", seq: Date.now() });
  const quick = (type, value) => {
    if (resource !== "leads") return;
    if (type === "sms") navigate(`/leads/${id}/conversation?channel=sms`);
    else if (type === "email") navigate(`/leads/${id}/conversation?channel=email`);
    else if (type === "call") setEngageTrigger({ type: "call", value: value || "", seq: Date.now() });
  };

  useEffect(() => {
    if (resource === "leads" || resource === "opportunities") {
      api.get("/campaigns?limit=200").then((r) => setCampaigns(r.data.items || [])).catch(() => {});
    }
  }, [resource]);

  const openOwnerDialog = () => {
    setOwnerDraft(record?.owner_id || "");
    setOwnerDialog(true);
    if (users.length === 0) {
      api.get("/auth/users").then((r) => setUsers(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => setUsers([]));
    }
  };

  const saveOwner = async () => {
    setOwnerSaving(true);
    try {
      const updated = await updateItem(resource, id, { owner_id: ownerDraft || null });
      setRecord(updated);
      bumpActivity();
      setOwnerDialog(false);
      api.get(`/related/${resource}/${id}`).then((r) => setRelated(r.data)).catch(() => {});
      toast.success("Owner updated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setOwnerSaving(false);
    }
  };

  const runScreening = async () => {
    setScreening(true);
    const tid = toast.loading("Angela is screening this property…");
    try {
      const { data } = await api.post("/ai/screen_property", { resource, record_id: id });
      const d = data.decision || {};
      setRecord((prev) => ({
        ...prev,
        screening_decision: d,
        rating_score: d.overall_score,
        rating_color: d.overall_color,
        lead_status: data.lead_status,
        status: data.lead_status,
        screened_at: data.screened_at,
      }));
      bumpActivity();
      toast.success(`Angela screened this property → ${data.lead_status} · ${d.overall_score}/10`, { id: tid });
    } catch (e) {
      toast.error(apiError(e), { id: tid });
    } finally {
      setScreening(false);
    }
  };

  const changeStatus = async (s) => {
    // Clicking the "Screening" stage on a Property Search runs Angela's screening,
    // which itself advances the status to Qualified / Not Qualified.
    if (resource === "property_search" && s === "Screening") {
      confirmScreening();
      return;
    }
    // Clicking the final "Converted" stage on an acquisition deal spins up the
    // full linked project chain (Analysis → Project → Acquisition → …).
    if (resource === "acquisition_pipeline" && s === "Converted") {
      convertDealToProject();
      return;
    }
    // A Property Analysis reaching "Converted" (only valid once it's Under Contract)
    // hands the deal off to the Acquisition workflow, carrying all data forward.
    if (resource === "leads" && s === "Converted" && record.lead_status === "Under Contract") {
      convertAnalysisToAcquisition();
      return;
    }
    setStatusSaving(true);
    try {
      if (resource === "opportunities") {
        const res = await api.post(`/opportunities/${id}/transition`, { to_stage: s });
        setRecord(res.data);
        bumpActivity();
        toast.success(`Stage → ${s}`);
      } else {
        const patch = { lead_status: s, status: s };
        // Moving a Property Analysis into "Analyze" stamps the analysis start date.
        if (resource === "leads" && s === "Analyze") {
          patch.analysis_start_date = new Date().toISOString().slice(0, 10);
        }
        const updated = await updateItem(resource, id, patch);
        setRecord(updated);
        bumpActivity();
        toast.success(`Status → ${s}`);
        // A Project entering the "Acquisition" stage spins up (once) a linked
        // Acquisition record, snapshotting the approved underwriting at this moment.
        if (resource === "projects" && s === "Acquisition") {
          await ensureAcquisitionForProject(updated);
        }
        // Selecting "Analyze" on a Property Analysis auto-starts Angela's analysis.
        if (resource === "leads" && s === "Analyze") {
          runAngelaVerdict();
        }
        // An Acquisition reaching "Acquired" spins up (once) a linked Planning
        // record, bringing forward Property Analysis + Acquisition data.
        if (resource === "acquisition" && s === "Acquired") {
          await ensurePlanningForAcquisition(updated);
        }
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setStatusSaving(false);
    }
  };

  // Idempotently create the Acquisition record linked to a Project when it enters
  // the "Acquisition" stage. Snapshots the approved underwriting so the "Approved"
  // column stays frozen even if the Project is edited later.
  const ensureAcquisitionForProject = async (proj) => {
    try {
      const existing = await api
        .get(`/acquisition?project_id=${proj.id}`)
        .then((r) => (r.data.items || []).filter((a) => !a.archived_at))
        .catch(() => []);
      if (existing.length) {
        toast.info("Acquisition record already exists");
        navigate(`/record/acquisition/${existing[0].id}`);
        return;
      }
      const acq = await createItem("acquisition", buildAcquisitionSnapshot(proj));
      toast.success("Acquisition record created");
      navigate(`/record/acquisition/${acq.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Idempotently create the Planning record when an Acquisition reaches "Acquired".
  // Brings forward Property Analysis (project) + Acquisition data — no duplicate entry.
  const ensurePlanningForAcquisition = async (acq) => {
    try {
      const existing = await api
        .get(`/planning?project_id=${acq.project_id}`)
        .then((r) => (r.data.items || []).filter((p) => !p.archived_at))
        .catch(() => []);
      if (existing.length) {
        toast.info("Planning record already exists");
        navigate(`/record/planning/${existing[0].id}`);
        return;
      }
      let proj = {};
      if (acq.project_id) {
        proj = await api.get(`/projects/${acq.project_id}`).then((r) => r.data).catch(() => ({}));
      }
      const plan = await createItem("planning", buildPlanningSnapshot(acq, proj));
      toast.success("Planning record created");
      navigate(`/record/planning/${plan.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  // value for fields that appear on BOTH the Analysis and Project pages.
  const convertAnalysisToProject = async () => {
    setStatusSaving(true);
    try {
      const leadKeys = new Set(SECTIONS.leads.flatMap((s) => s.keys));
      const shared = SECTIONS.projects.flatMap((s) => s.keys).filter((k) => leadKeys.has(k));
      const payload = {};
      shared.forEach((k) => {
        const v = record[k];
        if (v !== undefined && v !== null && v !== "") payload[k] = v;
      });
      // Both pages share the LEAD_FIELDS schema — carry over all data fields
      // (GDV, construction budget, allocation, etc.), not just the ones the
      // Project layout happens to render, so downstream rollups stay intact.
      const EXCLUDE = new Set(["lead_status", "status"]);
      LEAD_FIELDS.forEach((f) => {
        if (EXCLUDE.has(f.key)) return;
        const v = record[f.key];
        if (v !== undefined && v !== null && v !== "") payload[f.key] = v;
      });
      const name = record.property_analysis_name || record.company_name || `${record.first_name || ""} ${record.last_name || ""}`.trim();
      if (name) { payload.first_name = name; payload.project_name = name; payload.property_analysis = name; }
      payload.property_analysis_id = record.id;
      if (record.owner_id) payload.owner_id = record.owner_id;
      if (record.lead_intake) payload.lead_intake = record.lead_intake;
      const intakeCarry = record.lead_intake ? { lead_intake: record.lead_intake } : {};
      const proj = await createItem("projects", payload);
      // Create the full linked record chain so the Project carries its
      // Analysis, Acquisition, Planning, and Design records from day one.
      const acq = await createItem("acquisition", buildAcquisitionSnapshot(proj));
      const plan = await createItem("planning", buildPlanningSnapshot(acq, proj));
      const design = await createItem("opportunities", {
        opportunity_name: proj.project_name || name || "Design",
        project_id: proj.id,
        stage: "Programming",
        status: "Programming",
        ...(proj.owner_id ? { owner_id: proj.owner_id } : {}),
        ...intakeCarry,
      });
      const mkChild = (res, st) => createItem(res, {
        first_name: proj.project_name || name || res,
        project_name: proj.project_name || name || res,
        project_id: proj.id,
        lead_status: st,
        status: st,
        ...(proj.owner_id ? { owner_id: proj.owner_id } : {}),
        ...intakeCarry,
      });
      const [precon, constr, closeout] = await Promise.all([
        mkChild("preconstruction", "Estimating"),
        mkChild("construction", "Site Work"),
        mkChild("closeout", "Final Inspections"),
      ]);
      await updateItem("projects", proj.id, {
        source_analysis_id: record.id,
        acquisition_id: acq.id,
        planning_id: plan.id,
        design_id: design.id,
        preconstruction_id: precon.id,
        construction_id: constr.id,
        closeout_id: closeout.id,
      });
      await updateItem(resource, record.id, { lead_status: "Converted", status: "Converted" });
      await archiveItem(resource, record.id);
      toast.success("Converted to Project — Acquisition, Planning & Design created");
      navigate(`/record/projects/${proj.id}`);
    } catch (e) {
      toast.error(apiError(e));
      setStatusSaving(false);
    }
  };

  // Property Analysis → Acquisition pipeline deal. Carries over ALL analysis
  // fields/components so the Acquisition record mirrors the Analysis record, then
  // marks the source analysis Converted. The deal starts at the "New" stage.
  const convertAnalysisToAcquisition = async () => {
    setStatusSaving(true);
    try {
      const skip = new Set(["id", "_id", "created_at", "updated_at", "created_by",
        "lead_status", "status", "resource", "record_no", "activity"]);
      const payload = {};
      Object.keys(record || {}).forEach((k) => {
        if (skip.has(k)) return;
        const v = record[k];
        if (v === undefined || v === null || v === "") return;
        payload[k] = v;
      });
      const name = record.property_name || record.property_analysis_name
        || record.street_address || record.address || record.apn
        || `${record.first_name || ""} ${record.last_name || ""}`.trim() || "Acquisition";
      payload.property_label = name;
      const ownerFull = record.owner_name || `${record.first_name || ""} ${record.last_name || ""}`.trim();
      if (ownerFull) {
        const parts = ownerFull.split(" ");
        payload.owner_first = parts[0] || "";
        payload.owner_last = parts.slice(1).join(" ") || "";
      }
      if (record.owner_phone) payload.owner_phone = record.owner_phone;
      if (record.owner_email) payload.owner_email = record.owner_email;
      if (record.street_address || record.address) payload.address = record.street_address || record.address;
      if (record.postal_code) payload.zip = record.postal_code;
      payload.status = "Under Contract";
      payload.lead_status = "Under Contract";
      payload.property_analysis_id = record.id;
      if (record.owner_id) payload.owner_id = record.owner_id;
      const acq = await createItem("acquisition_pipeline", { ...payload, allow_duplicate: true });
      await updateItem(resource, record.id, { lead_status: "Converted", status: "Converted" });
      await archiveItem(resource, record.id);
      toast.success("Converted to Acquisition — starts at Under Contract");
      navigate(`/record/acquisition_pipeline/${acq.id}`);
    } catch (e) {
      toast.error(apiError(e));
      setStatusSaving(false);
    }
  };

  // Lead → Property Screening. Creates a Property Screening (property_search) record
  // from the lead's data, marks the lead Converted, and opens the new screening record.
  const convertLeadToScreening = async () => {
    setStatusSaving(true);
    try {
      const name = record.property_name || `${record.first_name || ""} ${record.last_name || ""}`.trim() || record.address || record.apn || "Property";
      const skip = new Set(["id", "_id", "created_at", "updated_at", "owner_id", "created_by",
        "lead_status", "status", "converted_screening_id", "resource", "record_no", "is_hot",
        "angela_verdict", "angela_analysis", "angela_ai_review", "angela_status", "angela_mode", "angela_analyzed_at"]);
      const payload = {};
      Object.keys(record || {}).forEach((k) => {
        if (skip.has(k)) return;
        const v = record[k];
        if (v === undefined || v === null || v === "") return;
        payload[k] = v;
      });
      payload.property_name = name;
      if (record.address) { payload.street_address = record.address; payload.street_name = record.address; }
      if (record.asking_price != null && record.asking_price !== "") payload.list_price = Number(record.asking_price) || record.asking_price;
      if (Array.isArray(record.parcels) && record.parcels.length) payload.parcels = record.parcels;
      payload.source_inbound_lead_id = record.id;
      const screening = await createItem("property_search", { ...payload, allow_duplicate: true });
      await updateItem(resource, record.id, { lead_status: "Converted", status: "Converted", converted_screening_id: screening.id });
      await archiveItem(resource, record.id);
      toast.success("Converted to Property Screening");
      navigate(`/record/property_search/${screening.id}`);
    } catch (e) {
      toast.error(apiError(e));
      setStatusSaving(false);
    }
  };

  const openLandReconForLead = () => {
    const name = record.property_name || record.address || record.apn || "Property";
    navigate("/property-intelligence", { state: {
      fromLead: true, leadId: record.id, updateLeadId: record.id,
      apn: record.apn || "", address: record.address || "", propertyName: name,
    } });
  };

  const convertToOpportunity = async () => {
    setStatusSaving(true);
    try {
      const plan = record.product_plan || "Pro";
      const opp = await createItem("opportunities", {
        opportunity_name: record.company_name || `${record.first_name} ${record.last_name}`.trim(),
        company_name: record.company_name,
        lead_id: record.id,
        account_id: record.account_id,
        primary_contact_id: record.primary_contact_id,
        product_plan: plan,
        product_name: record.product_name || "Arq+",
        campaign_name: record.campaign_name,
        campaign_id: record.campaign_id,
        stage: "Qualified",
        status: "Qualified",
      });
      await updateItem(resource, record.id, { lead_status: "Converted", status: "Converted" });
      toast.success("Lead converted to opportunity");
      navigate(`/record/opportunities/${opp.id}`);
    } catch (e) {
      toast.error(apiError(e));
      setStatusSaving(false);
    }
  };

  // Acquisition Deal → full record chain, built atomically on the server so a
  // mid-chain failure never leaves a half-built project (see /acquisition_pipeline/{id}/convert).
  const convertDealToProject = async () => {
    setStatusSaving(true);
    try {
      const { data } = await api.post(`/acquisition_pipeline/${record.id}/convert`);
      toast.success("Deal converted — Analysis, Project & linked records created");
      navigate(`/record/projects/${data.project_id}`);
    } catch (e) {
      toast.error(apiError(e));
      setStatusSaving(false);
    }
  };

  // Property Search → Property Analysis: create a new leads record and carry over
  // every field the two schemas share (plus a few obvious equivalents & the GDV).
  const activateParcels = async () => {
    const now = Date.now();
    const primary = { id: `p${now}`, apn: record.apn || "", lot_size_sqft: record.lot_size_sqft || "", lot_size_acres: record.lot_size_acres || "" };
    const second = { id: `p${now + 1}`, apn: "", lot_size_sqft: "", lot_size_acres: "" };
    try {
      const updated = await updateItem(resource, id, { parcels: [primary, second], apn: primary.apn });
      setRecord(updated);
      toast.success("Multi-parcel deal enabled — add each parcel below");
    } catch (e) { toast.error(apiError(e)); }
  };

  const convertPropertyToAnalysis = async () => {
    setStatusSaving(true);
    try {
      const psKeys = new Set(PROPERTY_SEARCH_FIELDS.map((f) => f.key));
      const payload = {};
      LEAD_FIELDS.forEach((f) => {
        if (psKeys.has(f.key) && !["lead_status", "status"].includes(f.key)) {
          const v = record[f.key];
          if (v !== undefined && v !== null && v !== "") payload[f.key] = v;
        }
      });
      // Sensible equivalents (different key names, same meaning).
      if (record.property_name) { payload.property_analysis_name = record.property_name; }
      if (record.street_address) payload.street_name = record.street_address;
      if (record.asking_price != null && record.asking_price !== "") payload.list_price = record.asking_price;
      // Carry Property Search utilities (Yes/No) into the Analysis Lot Utility Assessment (boolean).
      const _yn = (v) => { if (v == null || v === "") return undefined; const s = String(v).trim().toLowerCase(); return s === "yes" ? true : s === "no" ? false : undefined; };
      [["lot_utility_water", "water_available"], ["lot_utility_sewer", "sewer_available"],
       ["lot_utility_septic", "septic_available"],
       ["lot_utility_electric", "electric_available"], ["lot_utility_natural_gas", "gas_available"]].forEach(([src, dst]) => {
        const b = _yn(record[src]);
        if (b !== undefined) payload[dst] = b;
      });
      // Do NOT carry over the source record's owner — let the backend assign the
      // converting user as owner so the new analysis reliably shows under their "Mine" filter.
      if (record.date_added) payload.date_added = record.date_added;
      payload.source_property_search_id = record.id;
      if (record.lead_intake) payload.lead_intake = record.lead_intake;
      if (Array.isArray(record.parcels) && record.parcels.length) payload.parcels = record.parcels;
      const arv = record.estimated_new_construction_arv;
      if (arv != null && arv !== "" && !isNaN(Number(arv))) {
        payload.arv_market_value = Number(arv);
        Object.assign(payload, computeArvUnderwriting(arv));
      }
      payload.analysis_status = "Analysis — In Progress";
      const lead = await createItem("leads", { ...payload, allow_duplicate: true });
      // Carry over Notes & Tasks from the Property Search record to the new Analysis record.
      try {
        const [tRes, nRes] = await Promise.all([
          api.get(`/tasks?record_id=${record.id}&limit=200`),
          api.get(`/notes?record_id=${record.id}&limit=200`),
        ]);
        const srcTasks = (tRes.data.items || []).filter((t) => !t.archived_at);
        const srcNotes = (nRes.data.items || []).filter((n) => !n.archived_at);
        await Promise.all([
          ...srcTasks.map((t) => api.post("/tasks", {
            title: t.title, description: t.description, task_type: t.task_type,
            priority: t.priority, due_date: t.due_date, status: t.status,
            record_id: lead.id, resource: "leads", lead_id: lead.id,
          })),
          ...srcNotes.map((n) => api.post("/notes", {
            body: n.body, author_name: n.author_name,
            record_id: lead.id, resource: "leads", lead_id: lead.id,
          })),
        ]);
      } catch { /* note/task carry-over is best-effort */ }
      await updateItem(resource, record.id, { lead_status: "Converted", status: "Converted" });
      await archiveItem(resource, record.id);
      toast.success("Converted to Property Analysis");
      navigate(`/record/analysis/${lead.id}`);
    } catch (e) {
      toast.error(apiError(e));
      setStatusSaving(false);
    }
  };


  const load = () => {
    setLoading(true);
    api.get(`/${resource}/${id}`).then((r) => setRecord(r.data)).catch(() => setRecord(null)).finally(() => setLoading(false));
  };
  useEffect(load, [resource, id]);

  // One-time self-heal: older subdivisions booked land off the SINGLE-lot GDV
  // (~1/N of the true land), badly overstating the header margin. Correct the
  // Land Acquisition line to the real deal-level land the Underwriting Model uses.
  useEffect(() => {
    const sub = record?.subdivision;
    if (!record?.id || !id || !sub?.enabled || !(sub.lots || []).length) return;
    const correct = dealLandAcquisition(record, sub.lots.length);
    if (!(correct > 0)) return;
    const site = sub.site_costs || [];
    const idx = site.findIndex((x) => /land acquisition/i.test(x.label || ""));
    const current = idx >= 0 ? Math.round(Number(site[idx].amount) || 0) : 0;
    if (Math.abs(current - correct) <= 1) return;
    const nextSite = idx >= 0
      ? site.map((x, i) => (i === idx ? { ...x, amount: correct } : x))
      : [{ label: "Land Acquisition", amount: correct }, ...site];
    updateItem(resource, id, { subdivision: { ...sub, site_costs: nextSite } })
      .then((u) => setRecord(u))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  // For chain records (Project / Acquisition / Planning / Design): resolve the
  // linked Analysis / Acquisition / Planning / Design records for the Quick Links bar.
  useEffect(() => {
    const isChain = ["projects", "acquisition", "planning", "preconstruction", "construction", "closeout"].includes(resource) || (resource === "opportunities" && record?.project_id);
    if (!isChain || !record) { setModuleLinks(null); return; }
    const byId = (res, rid) => (rid ? api.get(`/${res}/${rid}`).then((r) => r.data).catch(() => null) : Promise.resolve(null));
    const byProject = (res, pid) => api.get(`/${res}?project_id=${pid}`).then((r) => (r.data.items || []).filter((x) => !x.archived_at)[0] || null).catch(() => null);
    const projectId = resource === "projects" ? record.id : record.project_id;
    if (!projectId) {
      setModuleLinks(null);
      return;
    }
    byId("projects", projectId).then((project) => {
      const p = project || {};
      Promise.all([
        byId("leads", p.source_analysis_id || p.property_analysis_id),
        p.acquisition_id ? byId("acquisition", p.acquisition_id) : byProject("acquisition", projectId),
        p.planning_id ? byId("planning", p.planning_id) : byProject("planning", projectId),
        p.design_id ? byId("opportunities", p.design_id) : byProject("opportunities", projectId),
        p.preconstruction_id ? byId("preconstruction", p.preconstruction_id) : byProject("preconstruction", projectId),
        p.construction_id ? byId("construction", p.construction_id) : byProject("construction", projectId),
        p.closeout_id ? byId("closeout", p.closeout_id) : byProject("closeout", projectId),
      ]).then(([analysis, acquisition, planning, design, preconstruction, construction, closeout]) =>
        setModuleLinks({ project, analysis, acquisition, planning, design, preconstruction, construction, closeout })
      );
    });
  }, [resource, record?.id, record?.project_id, record?.acquisition_id, record?.planning_id, record?.design_id]);

  useEffect(() => {
    if (resource === "opportunities" && record?.lead_id) {
      api.get(`/leads/${record.lead_id}`).then((r) => setOppLead(r.data)).catch(() => setOppLead(null));
    } else {
      setOppLead(null);
    }
  }, [resource, record?.lead_id]);

  const reloadRelated = () => {
    if (LEAD_LIKE.includes(resource) || resource === "opportunities" || resource === "accounts")
      api.get(`/related/${resource}/${id}`).then((r) => setRelated(r.data)).catch(() => {});
  };
  const onCardChange = () => { bumpActivity(); reloadRelated(); setDdRefresh((x) => x + 1); };
  const [taskListSignal, setTaskListSignal] = useState(0);
  const [noteListSignal, setNoteListSignal] = useState(0);

  useEffect(() => {
    if (LEAD_LIKE.includes(resource) || resource === "opportunities" || resource === "accounts") {
      api.get(`/related/${resource}/${id}`).then((r) => setRelated(r.data)).catch(() => setRelated(null));
    } else {
      setRelated(null);
    }
  }, [resource, id, record]);

  const saveField = async (key, value) => {
    try {
      const payload = { [key]: value };
      // Days on Market ⇄ Date Added: entering days sets date_added = today − N days
      // (the reverse of the date_added → days_on_market computed display).
      if (key === "days_on_market") {
        const n = Math.max(parseInt(String(value).replace(/[^0-9]/g, ""), 10) || 0, 0);
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() - n);
        payload.date_added = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        payload.days_on_market = n;
      }
      // Editing the pipeline status via a field keeps `status` in sync with the pathway.
      if (key === "lead_status") payload.status = value;
      // Land Comparable lot size: Acre field (land_comp_N_sqft) ⇄ Sq Ft field (land_comp_N_lot).
      const _landAcre = key.match(/^land_comp_([123])_sqft$/);
      if (_landAcre) {
        const acres = parseFloat(String(value).replace(/[^0-9.]/g, ""));
        if (!isNaN(acres)) payload[`land_comp_${_landAcre[1]}_lot`] = Math.round(acres * 43560);
      }
      const _landSqft = key.match(/^land_comp_([123])_lot$/);
      if (_landSqft) {
        const sqft = parseFloat(String(value).replace(/[^0-9.]/g, ""));
        if (!isNaN(sqft)) payload[`land_comp_${_landSqft[1]}_sqft`] = Math.round((sqft / 43560) * 10000) / 10000;
      }
      // Home Comparable lot size: Acre field (comp_N_lot_acre) ⇄ Sq Ft field (comp_N_lot).
      const _homeAcre = key.match(/^comp_([123])_lot_acre$/);
      if (_homeAcre) {
        const acres = parseFloat(String(value).replace(/[^0-9.]/g, ""));
        if (!isNaN(acres)) payload[`comp_${_homeAcre[1]}_lot`] = Math.round(acres * 43560);
      }
      const _homeSqft = key.match(/^comp_([123])_lot$/);
      if (_homeSqft) {
        const sqft = parseFloat(String(value).replace(/[^0-9.]/g, ""));
        if (!isNaN(sqft)) payload[`comp_${_homeSqft[1]}_lot_acre`] = Math.round((sqft / 43560) * 10000) / 10000;
      }
      // Land Comparables Summary auto-fills from the 3 land comps (pre-filled, still editable).
      // Size-adjusted: median $/SF across comps × the SUBJECT parcel's lot size (a raw price
      // average ignores that comps rarely match the subject size). Falls back to a price average.
      if (/^land_comp_[123]($|_)/.test(key)) {
        const merged = { ...record, ...payload };
        const prices = [], pairs = [];
        [1, 2, 3].forEach((i) => {
          const price = parseFloat(String(merged[`land_comp_${i}`] ?? "").replace(/[^0-9.]/g, ""));
          const lot = parseFloat(String(merged[`land_comp_${i}_lot`] ?? "").replace(/[^0-9.]/g, ""));
          if (!isNaN(price) && price > 0) prices.push(price);
          if (!isNaN(price) && price > 0 && !isNaN(lot) && lot > 0) pairs.push({ price, sqft: lot });
        });
        const subj = parseFloat(String(merged.lot_size_sqft ?? "").replace(/[^0-9.]/g, ""));
        const summary = landValueFromComps(pairs, prices, isNaN(subj) ? null : subj);
        if (summary) {
          if (summary.ppsf != null) payload.land_price_per_sqft = summary.ppsf.toFixed(2);
          if (summary.est != null) payload.land_estimated_land_value = String(summary.est);
          if (summary.note) payload.land_valuation_note = summary.note;
        }
      }
      // GDV drives the hard-cap underwriting allocation + normalized hard-construction line items.
      if (key === "arv_market_value") Object.assign(payload, computeArvUnderwriting(value, Number(record.builder_gc_fee) || defaultBuilderFee(value)));
      // Editing the Builder / GC Fee re-allocates the hard-construction line items from the
      // remaining (available) hard construction budget so the 50% cap is never exceeded.
      // The Builder / GC Fee is tied to (included in) the hard cost. Changing it keeps the
      // Hard Construction estimate CONSTANT and shifts the total construction_budget by the
      // fee delta — so the Available Hard Cap (and the Hard Construction variance) moves to
      // reflect any increase/decrease in the fee, without altering the hard estimate itself.
      if (key === "builder_gc_fee") {
        const newFee = Math.max(Number(value) || 0, 0);
        const arvNow = Number(record.arv_market_value) || 0;
        const oldFee = Number(record.builder_gc_fee) || DEFAULT_BUILDER_GC_FEE;
        const oldTotal = Number(record.construction_budget) || (arvNow ? Math.round(arvNow * 0.5) : 0);
        const hard = Math.max(oldTotal - oldFee, 0);
        payload.builder_gc_fee = newFee;
        payload.construction_budget = hard + newFee;
      }
      // Editing the Hard Construction preliminary sets the TOTAL construction budget
      // (hard construction + Builder/GC fee); the Builder/GC fee itself stays fixed.
      if (key === "hard_construction") {
        const feeNow = Number(record.builder_gc_fee) || DEFAULT_BUILDER_GC_FEE;
        payload.construction_budget = Math.max(Number(value) || 0, 0) + feeNow;
        delete payload.hard_construction;
      }
      // Home Comparables: price avg → GDV; Sq Ft avg → Proposed Home Size; beds/baths avg → Proposed Bedrooms/Baths.
      if (["comp_1", "comp_2", "comp_3", "comp_1_sqft", "comp_2_sqft", "comp_3_sqft", "comp_1_beds", "comp_2_beds", "comp_3_beds", "comp_1_baths", "comp_2_baths", "comp_3_baths"].includes(key)) {
        const avg = (keys, round = (n) => Math.round(n)) => {
          const nums = keys
            .map((k) => (k === key ? value : record[k]))
            .map(Number)
            .filter((v) => !isNaN(v) && v > 0);
          return nums.length ? round(nums.reduce((a, b) => a + b, 0) / nums.length) : null;
        };
        payload.estimated_new_construction_arv = avg(["comp_1", "comp_2", "comp_3"]);
        const sqftAvg = avg(["comp_1_sqft", "comp_2_sqft", "comp_3_sqft"]);
        if (sqftAvg != null) payload.living_sqft = sqftAvg;
        const bedsAvg = avg(["comp_1_beds", "comp_2_beds", "comp_3_beds"]);
        if (bedsAvg != null) payload.proposed_beds = bedsAvg;
        const bathsAvg = avg(["comp_1_baths", "comp_2_baths", "comp_3_baths"], (n) => Math.round(n * 2) / 2);
        if (bathsAvg != null) payload.proposed_baths = bathsAvg;
      }
      // Lot size: keep Sq Ft and Acres in sync (1 acre = 43,560 sq ft).
      if (key === "lot_size_sqft") {
        const sf = Number(String(value).replace(/,/g, ""));
        payload.lot_size_acres = (!isNaN(sf) && sf > 0) ? Math.round((sf / 43560) * 100) / 100 : "";
      }
      if (key === "lot_size_acres") {
        const ac = Number(String(value).replace(/,/g, ""));
        payload.lot_size_sqft = (!isNaN(ac) && ac > 0) ? Math.round(ac * 43560) : "";
      }
      let zipFilled = false;
      if (key === "postal_code" && ["leads", "property_search", "inbound_leads"].includes(resource) && value) {
        const geo = await lookupZip(String(value).trim());
        if (geo) {
          payload.city = geo.city;
          payload.state = geo.state;
          if (geo.county) payload.county = geo.county;
          zipFilled = true;
        }
      }
      // Mailing ZIP → auto-fill the contact's mailing city/state (Lead page).
      if (key === "mailing_zip" && resource === "inbound_leads" && value) {
        const geo = await lookupZip(String(value).trim());
        if (geo) {
          payload.mailing_city = geo.city;
          payload.mailing_state = geo.state;
          zipFilled = true;
        }
      }
      // Comparable ZIP → auto-fill that comparable's city/state.
      const compZip = key.match(/^comp_([123])_zip$/);
      if (compZip && ["property_search", "leads", "inbound_leads"].includes(resource) && value) {
        const geo = await lookupZip(String(value).trim());
        if (geo) {
          const n = compZip[1];
          payload[`comp_${n}_city`] = geo.city;
          payload[`comp_${n}_state`] = geo.state;
          zipFilled = true;
        }
      }
      // Land Comparable ZIP → auto-fill that land comparable's city/state.
      const landCompZip = key.match(/^land_comp_([123])_zip$/);
      if (landCompZip && ["property_search", "leads", "inbound_leads"].includes(resource) && value) {
        const geo = await lookupZip(String(value).trim());
        if (geo) {
          const n = landCompZip[1];
          payload[`land_comp_${n}_city`] = geo.city;
          payload[`land_comp_${n}_state`] = geo.state;
          zipFilled = true;
        }
      }
      if (key === "campaign_name") {
        const c = campaigns.find((x) => (x.name || x.campaign_name) === value);
        payload.campaign_id = c ? c.id : null;
      }
      // Persist the auto land-price adjustment (list < 15%-of-GDV budget) so
      // exports + the Investor Portal read the applied land value, not just the
      // on-screen computation. Recomputed whenever GDV / list / land inputs change.
      const LAND_ADJ_KEYS = ["arv_market_value", "list_price", "asking_price", "max_land_purchase_price"];
      if (resource === "leads" && LAND_ADJ_KEYS.includes(key)) {
        const s = computeUnderwritingStatus({ ...record, ...payload });
        payload.land_adjusted = s.landAdjusted;
        payload.land_applied = s.cost.max_land_purchase_price;
        payload.land_savings = s.landSavings;
      }
      const updated = await updateItem(resource, id, payload);
      setRecord(updated);
      bumpActivity();
      toast.success(zipFilled ? "City, state & county set from ZIP" : "Field updated");
      return true;
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("This record no longer exists — it may have been deleted. Reloading…");
        setTimeout(() => navigate(`/${resource}`), 1200);
      } else {
        toast.error(apiError(e));
      }
      return false;
    }
  };

  const doArchive = async () => {
    setConfirmArchive(false);
    try { await archiveItem(resource, id); toast.success(`${cfg.label} archived`); navigate(-1); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    try { await deleteItem(resource, id); toast.success(`${cfg.label} deleted`); navigate(-1); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading)
    return <div className="p-6 lg:p-8 max-w-[1100px] mx-auto space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64" /></div>;
  if (!record)
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1100px] mx-auto">
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="detail-back-btn"><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>
        <div className="mt-8 text-center text-muted-foreground">Record not found.</div>
      </div>
    );

  const { sections } = getSections(resource, record, HIDE);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5" data-testid="detail-back-btn">
        <ArrowLeft className="h-4 w-4" />Back
      </button>

      {false && resource === "leads" && (
        <div className="flex justify-end mb-4" data-testid="overall-status-wrap">
          <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 ${overallCls}`} data-testid="overall-status-badge">
            <span className="text-[10px] uppercase tracking-[0.14em] opacity-80">Overall Status</span>
            <span className="text-sm font-bold" data-testid="overall-status-value">{overallStatus}</span>
          </div>
        </div>
      )}

      {resource === "inbound_leads" && record.location_verified === false && (
        <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-3" data-testid="location-unverified-banner">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-amber-500">Location not verified</div>
            <div className="text-muted-foreground mt-0.5">
              {record.location_verification_note || "The agents couldn't confirm this address to a specific parcel."} Aerial imagery and comparable sales were withheld to avoid showing data for the wrong property. Verify the address or APN, then re-run enrichment.
            </div>
          </div>
        </div>
      )}


      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-border">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-primary mb-1.5">
            {cfg.label}
            {record.record_no != null && (
              <span className="ml-2 text-muted-foreground normal-case tracking-normal font-mono" data-testid="detail-record-no">{formatRecordId(resource, record.record_no)}</span>
            )}
          </div>
          {resource === "leads" ? (
            <>
              <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="detail-title">
                {record.property_name || record.property_analysis_name || record.company_name || `${record.first_name || ""} ${record.last_name || ""}`.trim() || "—"}
              </h1>
              {record.company_name && !record.property_name && !record.property_analysis_name && (
                <div className="text-base text-muted-foreground mt-0.5" data-testid="detail-subtitle">
                  {`${record.first_name || ""} ${record.last_name || ""}`.trim()}
                </div>
              )}
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="detail-title">{cfg.title(record)}</h1>
              {resource === "opportunities" && related?.primary_contact?.name && (
                <div className="text-base text-muted-foreground mt-0.5" data-testid="detail-subtitle">
                  {related.primary_contact.name}
                </div>
              )}
            </>
          )}
          <div className="mt-2 flex items-center gap-2 flex-wrap"><StatusBadge value={record[cfg.statusKey]} label={stageLabel(record[cfg.statusKey])} />
            {["leads", "inbound_leads"].includes(resource) && record.mail_address_status && (
              record.mail_address_deliverable ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 border border-emerald-500/30" data-testid="mail-address-badge" title={`Mailing address verified ${record.mail_address_verified_at ? "on " + formatDate(record.mail_address_verified_at) : ""}`}>
                  <MapPin className="h-3 w-3" />Address verified · mailable
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-destructive/10 text-destructive border border-destructive/30" data-testid="mail-address-badge">
                  <AlertTriangle className="h-3 w-3" />Address undeliverable
                </span>
              )
            )}
          </div>
        </div>
        <div className="flex items-center flex-wrap justify-end gap-2">
          {ZONING_RESOURCES.includes(resource) && (
            <Button variant="outline" onClick={runImport} disabled={zoningLoading} data-testid="import-property-btn">
              {zoningLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}Import
            </Button>
          )}
          {resource === "property_search" && (
            <Button variant="outline" onClick={confirmScreening} disabled={screening} data-testid="run-screening-btn">
              {screening ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ScanSearch className="h-4 w-4 mr-1.5" />}{record.screening_decision ? "Re-run Screening" : "Run Screening"}
            </Button>
          )}
          {resource === "leads" && (
            <Button variant="outline" onClick={confirmAnalyze} disabled={verdictBusy} data-testid="angela-verdict-btn">
              {verdictBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}Analyze
            </Button>
          )}
          {resource === "acquisition_pipeline" && (
            <Button variant="outline" onClick={confirmDueDiligence} disabled={ddBusy} data-testid="due-diligence-btn">
              {ddBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-1.5" />}Due Diligence
            </Button>
          )}
          {resource === "acquisition_pipeline" && (
            <Button variant="outline" onClick={confirmFeasibility} disabled={!!gateBusy.feasibility} data-testid="feasibility-btn">
              {gateBusy.feasibility ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-1.5" />}Feasibility
            </Button>
          )}
          {resource === "acquisition_pipeline" && (
            <Button variant="outline" onClick={confirmFinancial} disabled={!!gateBusy.financial} data-testid="financial-btn">
              {gateBusy.financial ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-1.5" />}Financial Review
            </Button>
          )}
          {resource === "opportunities" && record.lead_id && (
            <Button variant="outline" onClick={() => navigate(`/leads/${record.lead_id}/conversation?opp=${record.id}`)} data-testid="opp-conversation-btn">
              <MessagesSquare className="h-4 w-4 mr-1.5" />Conversation
            </Button>
          )}
          {resource === "acquisition" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive" data-testid="acquisition-terminate-btn">
                  <AlertTriangle className="h-4 w-4 mr-1.5" />Terminate<ChevronDown className="h-4 w-4 ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                {ACQUISITION_TERMINAL.map((t) => (
                  <DropdownMenuItem
                    key={t}
                    onClick={() => changeStatus(t)}
                    disabled={statusSaving}
                    className={record.lead_status === t ? "text-destructive font-semibold focus:text-destructive" : "text-destructive focus:text-destructive"}
                    data-testid={`acquisition-terminal-${t.toLowerCase()}`}
                  >
                    {t}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {ANALYSIS_LIKE.includes(resource) && (
            <Button variant="outline" onClick={generateOffer} disabled={generatingOffer} data-testid="generate-offer-btn">
              {generatingOffer ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <DollarSign className="h-4 w-4 mr-1.5" />}Offer
            </Button>
          )}
          {resource === "inbound_leads" && (
            <Button
              variant="outline"
              onClick={toggleMarketChannel}
              disabled={marketSaving}
              data-testid="market-channel-btn"
              title="Click to switch between On-Market and Off-Market"
              className={isLeadOnMarket(record) ? "border-emerald-500/40 text-emerald-500 hover:text-emerald-400" : "border-sky-500/40 text-sky-400 hover:text-sky-300"}
            >
              {marketSaving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : (isLeadOnMarket(record) ? <TrendingUp className="h-4 w-4 mr-1.5" /> : <Radar className="h-4 w-4 mr-1.5" />)}
              {isLeadOnMarket(record) ? "On-Market" : "Off-Market"}
            </Button>
          )}
          {resource === "inbound_leads" && (
            <Button variant="outline" onClick={openLandReconForLead} data-testid="record-land-recon-btn">
              <ScanSearch className="h-4 w-4 mr-1.5" />Land Recon
            </Button>
          )}
          {ANALYSIS_LIKE.includes(resource) && (
            <Button variant="outline" onClick={() => setStrategyOpen((n) => n + 1)} data-testid="exit-analysis-cta">
              <Gauge className="h-4 w-4 mr-1.5" />Exit Analysis
            </Button>
          )}
          {resource === "inbound_leads" && (
            <Button variant="outline" onClick={() => navigate(`/record/${resource}/${id}/mailer`)} data-testid="record-mailer-btn">
              <Mail className="h-4 w-4 mr-1.5" />Mailer
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="record-actions-btn">
                Actions<MoreHorizontal className="h-4 w-4 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {resource === "inbound_leads" && (
                <DropdownMenuItem onClick={toggleHot} data-testid="action-hot">
                  <Flame className={`h-4 w-4 mr-2 ${record.is_hot ? "text-red-500" : ""}`} />{record.is_hot ? "Remove Hot" : "Hot"}
                </DropdownMenuItem>
              )}
              {resource === "leads" && (
                <DropdownMenuItem onClick={() => setContractOpen(true)} data-testid="action-contracts">
                  <FileSignature className="h-4 w-4 mr-2" />Contracts
                </DropdownMenuItem>
              )}
              {resource === "leads" && (
                <DropdownMenuItem onClick={generateInvestorPortal} disabled={portalBusy} data-testid="action-portal">
                  {portalBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Presentation className="h-4 w-4 mr-2" />}Investor Portal
                </DropdownMenuItem>
              )}
              {resource === "leads" && (
                <DropdownMenuItem onClick={() => navigate(`/record/${resource}/${id}/2060`)} data-testid="action-2060">
                  <Percent className="h-4 w-4 mr-2" />20/60/20
                </DropdownMenuItem>
              )}
              {LEAD_LIKE.includes(resource) && resource !== "acquisition" && resource !== "planning" && !(resource === "leads" && record.lead_status !== "Under Contract") && (
                <DropdownMenuItem onClick={resource === "property_search" ? convertPropertyToAnalysis : resource === "leads" ? convertAnalysisToAcquisition : resource === "acquisition_pipeline" ? convertDealToProject : resource === "inbound_leads" ? () => setConfirmConvertLead(true) : convertToOpportunity} disabled={statusSaving} data-testid={resource === "property_search" ? "convert-property-analysis-btn" : resource === "leads" ? "convert-acquisition-btn" : resource === "acquisition_pipeline" ? "convert-deal-project-btn" : resource === "inbound_leads" ? "convert-lead-screening-btn" : "convert-opportunity-btn"}>
                  <ArrowRightLeft className="h-4 w-4 mr-2" />{resource === "leads" ? "Convert to Acquisition" : "Convert"}
                </DropdownMenuItem>
              )}
              {["property_search","leads"].includes(resource) && !(Array.isArray(record.parcels) && record.parcels.length > 1) && (
                <DropdownMenuItem onClick={activateParcels} data-testid="action-add-parcel">
                  <MapPin className="h-4 w-4 mr-2" />Add Parcel
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={openOwnerDialog} data-testid="action-change-owner">
                <User className="h-4 w-4 mr-2" />Change Owner
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setConfirmArchive(true)} data-testid="action-archive">
                <Archive className="h-4 w-4 mr-2" />Archive
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setConfirmDelete(true)} className="text-destructive focus:text-destructive" data-testid="action-delete">
                <Trash2 className="h-4 w-4 mr-2" />Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>


      <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
        <DialogContent data-testid="property-type-dialog">
          <DialogHeader>
            <DialogTitle>Development Type</DialogTitle>
            <DialogDescription>Choose the development type. Duplex, Triplex and Fourplex set the unit count automatically; the others let you enter the number of units. The development's living area, GDV and all-in cost are split evenly across the units.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <div className="text-xs text-muted-foreground mb-1">Development Type</div>
              <Select value={typeDraft} onValueChange={(v) => { setTypeDraft(v); setUnitsDraft(String(devTypeMeta(v).units)); }}>
                <SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEVELOPMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.value}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Number of Units</div>
              <Input type="number" min={1} max={200} value={unitsDraft} disabled={devTypeMeta(typeDraft).lock}
                onChange={(e) => setUnitsDraft(e.target.value)} className="w-28" data-testid="property-type-units-input" />
              {devTypeMeta(typeDraft).lock && <div className="text-[11px] text-muted-foreground mt-1">{typeDraft} is fixed at {devTypeMeta(typeDraft).units} unit{devTypeMeta(typeDraft).units > 1 ? "s" : ""}.</div>}
            </div>
            {isAttachedType(typeDraft) && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Efficiency % (net sellable)</div>
                  <Input type="number" min={50} max={100} value={effDraft}
                    onChange={(e) => setEffDraft(e.target.value)} className="w-full" data-testid="property-type-eff-input" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">New-Construction $/SF (no comps)</div>
                  <Input type="number" min={1} value={psfDraft}
                    onChange={(e) => setPsfDraft(e.target.value)} className="w-full" data-testid="property-type-psf-input" />
                </div>
              </div>
            )}
            {(() => {
              const m = devTypeMeta(typeDraft);
              const units = m.lock ? m.units : Math.max(1, Math.min(200, parseInt(unitsDraft) || m.units || 1));
              const money = (v) => formatCurrency(Math.round(v || 0));
              if (isAttachedType(typeDraft)) {
                const draftRec = { ...record, development_type: typeDraft, unit_count: units,
                  unit_efficiency_pct: parseInt(effDraft) || DEFAULT_EFFICIENCY,
                  new_construction_psf: parseInt(psfDraft) || DEFAULT_NEW_CONSTRUCTION_PSF };
                const a = computeAttachedType(draftRec);
                return (
                  <div className="rounded-md border border-border bg-secondary/20 p-3 text-sm space-y-1.5" data-testid="property-type-preview">
                    <KVRow label="Units" value={units} />
                    <KVRow label="Building (Gross)" value={a.gross ? `${a.gross.toLocaleString()} SF` : "—"} />
                    <KVRow label={`Net Sellable (${a.effPct}%)`} value={a.net ? `${a.net.toLocaleString()} SF` : "—"} />
                    <KVRow label="Unit Size (Net)" value={a.perUnitNet ? `${a.perUnitNet.toLocaleString()} SF` : "—"} />
                    <KVRow label="Total GDV" value={money(a.effArv)} />
                    <KVRow label="All-In Cost" value={money(a.allIn)} />
                    <KVRow label="Net Profit" value={money(a.profit)} />
                    <KVRow label="Margin" value={a.margin == null ? "—" : `${a.margin.toFixed(1)}%`} />
                    <div className="text-[11px] text-muted-foreground pt-1 border-t border-border/50">
                      Building: {a.buildingSource || "not enough data"} · GDV: {a.arvSource || "needs GDV or $/SF"}
                    </div>
                  </div>
                );
              }
              const p = computePropertyType(record, units);
              return (
                <div className="rounded-md border border-border bg-secondary/20 p-3 text-sm space-y-1.5" data-testid="property-type-preview">
                  <KVRow label="Units" value={units} />
                  <KVRow label="Unit Size" value={p.perUnitSqft ? `${p.perUnitSqft.toLocaleString()} SF` : "—"} />
                  <KVRow label="Total GDV" value={money(p.arv)} />
                  <KVRow label="All-In Cost" value={money(p.allIn)} />
                  <KVRow label="Net Profit" value={money(p.profit)} />
                  <KVRow label="Margin" value={p.margin == null ? "—" : `${p.margin.toFixed(1)}%`} />
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTypeDialog(false)} data-testid="property-type-cancel">Cancel</Button>
            <Button onClick={applyPropertyType} disabled={typeBusy} data-testid="property-type-apply">
              {typeBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Home className="h-4 w-4 mr-1.5" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ContractPickerDialog open={contractOpen} onOpenChange={setContractOpen} recordId={id} resource={resource} />
      <AlertDialog open={confirmConvertLead} onOpenChange={setConfirmConvertLead}>
        <AlertDialogContent data-testid="convert-lead-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="convert-lead-confirm-title">Convert lead to Property Screening?</AlertDialogTitle>
            <AlertDialogDescription>
              This creates a new Property Screening record from this lead's data and marks the lead as Converted. You'll be taken to the new screening record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="convert-lead-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmConvertLead(false); convertLeadToScreening(); }} data-testid="convert-lead-confirm-go">Convert</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmAuto} onOpenChange={(o) => { if (!o) setConfirmAuto(null); }}>
        <AlertDialogContent data-testid="automation-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="automation-confirm-title">{confirmAuto?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmAuto?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="automation-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirmed} data-testid="automation-confirm-run">{confirmAuto?.cta || "Run"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={zoningLoading || !!importResult} onOpenChange={(o) => { if (!o) setImportResult(null); }}>
        <DialogContent data-testid="import-results-dialog" className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import from Public Records</DialogTitle>
            <DialogDescription>Angela searches county assessor, zoning and listing sources for this parcel.</DialogDescription>
          </DialogHeader>
          {zoningLoading ? (
            <div className="flex flex-col items-center gap-3 py-10 text-sm text-muted-foreground" data-testid="import-loading">
              <Loader2 className="h-6 w-6 animate-spin" />Searching public records… this can take up to a minute.
            </div>
          ) : importResult?.error ? (
            <div className="flex items-center gap-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-600 text-sm" data-testid="import-error">
              <AlertTriangle className="h-5 w-5 shrink-0" />{importResult.error}
            </div>
          ) : importResult ? (
            <div className="space-y-4" data-testid="import-summary">
              {(() => {
                const pct = importResult.percentage || 0;
                const ok = (importResult.retrieved || 0) > 0;
                const tone = pct >= 60 ? "text-emerald-600" : pct > 0 ? "text-amber-600" : "text-red-600";
                return (
                  <div className="flex items-center gap-4 rounded-lg border border-border bg-secondary/40 px-4 py-4">
                    <div className={`text-4xl font-bold ${tone}`} data-testid="import-percentage">{pct}%</div>
                    <div>
                      <div className="text-sm font-semibold flex items-center gap-1.5">
                        {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <AlertTriangle className="h-4 w-4 text-red-500" />}
                        {ok ? "Data retrieved" : "No data retrieved"}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {importResult.retrieved} of {importResult.attempted} searched fields filled
                      </div>
                    </div>
                  </div>
                );
              })()}
              {importResult.retrieved_fields?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-emerald-600 mb-1.5">Retrieved ({importResult.retrieved_fields.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {importResult.retrieved_fields.map((f, i) => <span key={i} className="text-xs rounded-full bg-emerald-500/10 text-emerald-600 px-2 py-0.5">{f}</span>)}
                  </div>
                </div>
              )}
              {importResult.missing_fields?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Not Found ({importResult.missing_fields.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {importResult.missing_fields.map((f, i) => <span key={i} className="text-xs rounded-full bg-secondary text-muted-foreground px-2 py-0.5">{f}</span>)}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            {!zoningLoading && <Button onClick={() => setImportResult(null)} data-testid="import-results-close-btn">Done</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={portalOpen} onOpenChange={setPortalOpen}>
        <DialogContent data-testid="investor-portal-dialog">
          <DialogHeader>
            <DialogTitle>Investor Portal Link</DialogTitle>
            <DialogDescription>
              Share this link with investors. It shows a live, ARQITEK-branded summary of this deal — anyone with the link can view it, no login required. Angela's full deal memo generates in the background and appears on the page automatically within a minute.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input readOnly value={portalUrl} data-testid="investor-portal-url" className="text-xs font-mono" />
            <Button size="sm" variant="outline" onClick={copyPortalUrl} data-testid="investor-portal-copy-btn">
              <Copy className="h-4 w-4 mr-1.5" />Copy
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPortalOpen(false)} data-testid="investor-portal-close-btn">Close</Button>
            <Button onClick={() => window.open(portalUrl, "_blank")} data-testid="investor-portal-open-btn">
              <ExternalLink className="h-4 w-4 mr-1.5" />Open Portal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {record.website_url && (
        <a href={record.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline mb-6" data-testid="detail-website-link">
          <ExternalLink className="h-4 w-4" />{record.website_url}
        </a>
      )}

      {LEAD_LIKE.includes(resource) && record && (
        <EngagementActions lead={record} hideCard trigger={engageTrigger} onLogged={() => { bumpActivity(); }} />
      )}
      {resource === "opportunities" && oppLead && (
        <EngagementActions lead={oppLead} hideCard trigger={engageTrigger} opportunityId={record.id} onLogged={() => { bumpActivity(); }} />
      )}

      {resource === "property_search" ? (
        <PropertySnapshot record={record} />
      ) : ANALYSIS_LIKE.includes(resource) ? (
        <AnalysisSnapshot record={record} resource={resource} related={related} onOwnerClick={openOwnerDialog} refreshKey={ddRefresh} selectedStrategyKey={exitStratKey} />
      ) : resource === "inbound_leads" ? (
        <InboundLeadSnapshot record={record} />
      ) : resource === "projects" ? (
        <ProjectQuickLinks links={moduleLinks} navigate={navigate} owner={related?.owner} onOwnerClick={openOwnerDialog} resource={resource} />
      ) : moduleLinks && (resource === "acquisition" || resource === "planning" || resource === "preconstruction" || resource === "construction" || resource === "closeout" || (resource === "opportunities" && record.project_id)) ? (
        <ProjectQuickLinks links={moduleLinks} navigate={navigate} owner={related?.owner} onOwnerClick={openOwnerDialog} resource={resource} />
      ) : (
        related && <QuickLinks related={related} navigate={navigate} resource={resource} onOwnerClick={openOwnerDialog} onTasksClick={() => setTaskListSignal((s) => s + 1)} onNotesClick={() => setNoteListSignal((s) => s + 1)} />
      )}

      {LEAD_LIKE.includes(resource) && (
        <StatusPath statuses={STATUS_PATHS[resource] || LEAD_STATUSES} current={record.lead_status || (STATUS_PATHS[resource] || LEAD_STATUSES)[0]} onChange={changeStatus} saving={statusSaving} />
      )}
      {resource === "opportunities" && (
        <StatusPath statuses={OPP_STAGES} current={record.stage} onChange={changeStatus} saving={statusSaving} />
      )}

      {LEAD_LIKE.includes(resource) || resource === "opportunities" ? (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            {resource === "property_search" && <ArqitekDecisionCard record={record} />}
            {resource === "acquisition" && <AcquisitionFinancialSummary record={record} />}
            {resource === "planning" && <PlanningOverview record={record} />}
            {resource === "planning" && <BuildableEnvelope record={record} />}
            {resource === "leads" && (verdict || verdictBusy) && (
              <div className="space-y-5" key="ai-analysis-top">
                <AnalysisResults analysis={verdict} analyzedAt={verdictAt} loading={verdictBusy} onReanalyze={confirmAnalyze} busy={verdictBusy} />
              </div>
            )}
            {resource === "acquisition_pipeline" && (ddAnalysis || ddBusy || feas || gateBusy.feasibility || fin || gateBusy.financial) && (
              <div className="space-y-5" key="ai-gates-top">
                {(ddAnalysis || ddBusy) && (
                  <DueDiligenceResults analysis={ddAnalysis} analyzedAt={ddAt} loading={ddBusy} onRun={confirmDueDiligence} busy={ddBusy} recordId={id} refreshKey={ddRefresh} />
                )}
                {(feas || gateBusy.feasibility) && (
                  <GateResults title="Feasibility" tid="feasibility" source="angela_feasibility" analysis={feas} analyzedAt={feasAt} loading={!!gateBusy.feasibility} onRun={confirmFeasibility} busy={!!gateBusy.feasibility} recordId={id} refreshKey={ddRefresh} runningText="Angela is assessing buildability…" />
                )}
                {(fin || gateBusy.financial) && (
                  <GateResults title="Financial Review" tid="financial" source="angela_financial_review" analysis={fin} analyzedAt={finAt} loading={!!gateBusy.financial} onRun={confirmFinancial} busy={!!gateBusy.financial} recordId={id} refreshKey={ddRefresh} runningText="Angela is reviewing the economics…" />
                )}
              </div>
            )}
            {["property_search","leads","acquisition_pipeline"].includes(resource) && <ParcelsModule record={record} resource={resource} id={id} onSaved={setRecord} />}
            {ANALYSIS_LIKE.includes(resource) && <StrategyModule record={record} resource={resource} id={id} onSaved={setRecord} openSignal={strategyOpen} onSaveField={saveField} onStrategyChange={setExitStratKey} />}
            {ANALYSIS_LIKE.includes(resource) && <LandOfferModule record={record} resource={resource} id={id} onSaved={setRecord} openSignal={landOfferSignal} />}
            {ANALYSIS_LIKE.includes(resource) && record.concept_site_plan && <ConceptSitePlanCard record={record} onOpen={() => navigate(`/record/${resource}/${id}/concept`)} />}
            {resource === "acquisition_pipeline" && (() => {
              const pba = sections.find((s) => s.title === "Property Budget Analysis");
              if (!pba) return null;
              const sub = isSubdivisionActive(record);
              const n = sub ? Math.max(1, computeSubdivision(record.subdivision).count) : 1;
              return (
                <SectionCard key="pba-top" sec={pba} collapsible defaultOpen={false}>
                  {pba.fields.filter(({ def }) => def && (!def.showIf || def.showIf(record))).map(({ key, def }) => (
                    <FieldRow key={key} fieldKey={key} value={record[key]} def={def} onSave={saveField} quick={quick} campaigns={campaigns} computed={ARV_RESOURCES.includes(resource) && /^budget_/.test(key)} constructionTotal={availableHardConstruction(record) * n} scale={n} cleanEdit={["property_search","leads","acquisition_pipeline"].includes(resource)} />
                  ))}
                </SectionCard>
              );
            })()}
            {QUOTE_RESOURCES.includes(resource) && resource !== "leads" && !isSubdivisionActive(record) && <ActualConstructionCost record={record} />}
            {ARV_RESOURCES.includes(resource) && resource !== "leads" && !isSubdivisionActive(record) && <CostPerSqFtAnalysis record={record} />}
            {sections.map((sec) => {
              if (ANALYSIS_LIKE.includes(resource) && sec.title === "Property Budget Analysis") return null;
              if (resource === "leads" && (sec.title === "Single Property Analysis Summary" || sec.title === "Lot Utility Assessment")) return null;
              if (sec.title === "Owner Information" && isLeadOnMarket(record)) return null;
              const isContactInfo = sec.title === "Contact Information";
              const collapsible = LEAD_LIKE.includes(resource) && !isContactInfo;
              return (
                <Fragment key={sec.title}>
                <SectionCard sec={sec} collapsible={collapsible} defaultOpen={!collapsible}>
                  {((resource === "property_search" && sec.title === "Referred By") || sec.title === "Listing Agent") && (
                    <AgentDirectoryPicker onSelect={populateAgentFromDirectory} busy={agentPickBusy} />
                  )}
                  {sec.fields.filter(({ def }) => def && (!def.showIf || def.showIf(record))).map(({ key, def }) => {
                    const isDom = key === "days_on_market" && record.date_added;
                    const val = isDom ? computeDaysOnMarket(record.date_added) : record[key];
                    const compUrl = /^(comp|land_comp)_\d+_address$/.test(key) ? record[key.replace(/_address$/, "_source_url")] : null;
                    return (
                      <FieldRow key={key} fieldKey={key} value={val} def={def} onSave={saveField} quick={quick} campaigns={campaigns} computed={(ARV_RESOURCES.includes(resource) && /^budget_/.test(key)) || (Array.isArray(record.parcels) && record.parcels.length > 1 && ["apn", "lot_size_sqft", "lot_size_acres"].includes(key))} constructionTotal={availableHardConstruction(record)} cleanEdit={["property_search","leads","acquisition_pipeline"].includes(resource)} linkHref={compUrl} />
                    );
                  })}
                  {sec.title === "Zoning" && (
                    <div className="pt-2">
                      <button type="button" onClick={generateUsage} disabled={usageBusy} data-testid="zoning-usage-angela-btn" className="text-[11px] text-primary hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                        {usageBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                        {record.usage ? "Regenerate Usage with Angela" : "Generate Usage with Angela"}
                      </button>
                      <p className="text-[10px] text-muted-foreground mt-1">Angela describes the possible uses of the land based on its zoning.</p>
                    </div>
                  )}
                </SectionCard>
                </Fragment>
              );
            })}
            {(resource === "property_search" || resource === "inbound_leads" || ANALYSIS_LIKE.includes(resource)) && <RecordDocuments key={docRefresh} recordId={record.id} />}
            <ActivityLog recordId={record.id} refreshToken={activityRefresh} />
            <SystemInformation record={record} related={related} onOwnerClick={openOwnerDialog} />
          </div>
          <aside className="space-y-6 self-start">
            {record.lead_intake && <IntakeCard intake={record.lead_intake} />}
            {resource === "leads" && (record.email_bounced || record.sms_bounced) && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4" data-testid="deliverability-flag">
                <div className="flex items-center gap-2 text-destructive font-semibold text-sm mb-1">
                  <AlertTriangle className="h-4 w-4" />Deliverability issue
                </div>
                <ul className="text-xs text-muted-foreground space-y-1">
                  {record.email_bounced && <li data-testid="email-bounced-note">Email {record.email_spam_complaint ? "marked as spam" : "bounced"} — auto-set to Do Not Email. {record.email_bounce_reason || ""}</li>}
                  {record.sms_bounced && <li data-testid="sms-bounced-note">SMS undeliverable — auto-set to Do Not Text. {record.sms_bounce_reason || ""}</li>}
                </ul>
              </div>
            )}
            {COMM_RESOURCES.includes(resource) && (
              <CommunicationCard resource={resource} recordId={record.id} recordName={record.project_name || `${record.first_name || ""} ${record.last_name || ""}`.trim() || record.company_name || record.property_name} />
            )}
            <TasksCard recordId={record.id} resource={resource} onChange={onCardChange} listSignal={taskListSignal} reloadToken={taskReload} />
            <NotesCard recordId={record.id} resource={resource} onChange={onCardChange} listSignal={noteListSignal} />
            {record.investor_token && <PortalViewsCard recordId={record.id} />}
          </aside>
        </div>
        </>
      ) : (
        <>
          <div className="space-y-5">
            {sections.filter((sec) => !(sec.title === "Owner Information" && isLeadOnMarket(record))).map((sec) => (
              <div key={sec.title} className="rounded-lg border border-border bg-card overflow-hidden">
                <div className="px-6 py-3.5 border-b border-border bg-secondary/30">
                  <div className="text-sm font-semibold">{sec.title}</div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 px-6 py-2">
                  {sec.fields.map(({ key, def }) => (
                    <FieldRow key={key} fieldKey={key} value={record[key]} def={def} onSave={saveField} quick={null} linkHref={/^(comp|land_comp)_\d+_address$/.test(key) ? record[key.replace(/_address$/, "_source_url")] : null} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          {resource === "accounts" && <ArqplusCard account={record} />}
          {resource === "accounts" && <AccountRelatedLists accountId={record.id} navigate={navigate} />}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
            <TasksCard recordId={record.id} resource={resource} onChange={onCardChange} listSignal={taskListSignal} />
            <NotesCard recordId={record.id} resource={resource} onChange={onCardChange} listSignal={noteListSignal} />
          </div>
          <ActivityLog recordId={record.id} refreshToken={activityRefresh} />
          <SystemInformation record={record} related={related} onOwnerClick={openOwnerDialog} />
        </>
      )}

      <Dialog open={ownerDialog} onOpenChange={setOwnerDialog}>
        <DialogContent data-testid="change-owner-dialog">
          <DialogHeader>
            <DialogTitle>Change owner</DialogTitle>
            <DialogDescription>Assign this {cfg.label.toLowerCase()} to a different team member.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <div className="text-xs text-muted-foreground mb-1.5">Owner</div>
            <Select value={ownerDraft || "unassigned"} onValueChange={(v) => setOwnerDraft(v === "unassigned" ? "" : v)}>
              <SelectTrigger data-testid="owner-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {(resource === "leads"
                  ? users.filter((u) => ["Sales Manager", "Sales Representative"].includes(u.role) && (u.status ? u.status === "Active" : true))
                  : users
                ).map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name || u.email}{u.role ? ` — ${u.role}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOwnerDialog(false)} data-testid="change-owner-cancel">Cancel</Button>
            <Button onClick={saveOwner} disabled={ownerSaving} data-testid="change-owner-confirm">
              {ownerSaving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {cfg.label.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this {cfg.label.toLowerCase()}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent data-testid="confirm-archive-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {cfg.label.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>
              This {cfg.label.toLowerCase()} will be moved to the archive. You can restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-archive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doArchive} data-testid="confirm-archive-confirm">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SectionCard({ sec, children, collapsible, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const testid = `section-${sec.title.replace(/\s+/g, "-").toLowerCase()}`;
  if (!collapsible) {
    return (
      <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid={testid}>
        <div className="px-6 py-3.5 border-b border-border bg-secondary/30">
          <div className="text-sm font-semibold">{sec.title}</div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 px-6 py-2">{children}</div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-6 py-3.5 border-b border-border bg-secondary/30 hover:bg-secondary/50 transition-colors"
        data-testid={`${testid}-toggle`}>
        <div className="text-sm font-semibold">{sec.title}</div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 px-6 py-2" data-testid={`${testid}-body`}>{children}</div>}
    </div>
  );
}

